import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CAP } from '../lib/planAccess';
import { AJUSTE_YA_EN_MARCHA, abrirPagoDelAjuste, seLeOfreceLaRevision } from '../lib/revision';
import { verComo } from '../lib/modoRevision';
import { MEDIDAS, VIDEO_MEDIDAS, revisarMedidas } from '../lib/medidas';
import CerrarSesion from '../components/CerrarSesion';
import { cierreDelAltaPendiente, tramoDelCierre, dondeRetomarElCierre } from '../lib/cierreDelAlta';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Loader2, Check, ClipboardList, ImagePlus } from 'lucide-react';
import Logo12EN12 from '../components/Logo12EN12';
import BrandArrow from '../components/BrandArrow';
import FondoFotos from '../components/FondoFotos';
import PreferencesSetup from '../components/nutrition/PreferencesSetup';
import { ExclusionesDelAlta, PreferidosDelAlta } from '../components/alta/PreferenciasDelAlta';
import { preferidasDePartida } from '../lib/preferenciasDeComida';
import TresFotos from '../components/reports/TresFotos';

// Cuestionario inicial en DOS NIVELES (spec 18-07-2026):
//  - Nivel 0 (todo el mundo): las 8 preguntas que mueven los macros -> CALCULAR.
//  - Nivel 1 (solo planes con coach, calculadora == 'personalizado'): perfil largo
//    (biotipo, salud, historial...). NO toca los macros.
// Estilo paso a paso (una pregunta por pantalla).

const BIOTIPOS = [
    { value: 'ectomorfo', label: 'Ectomorfo (el delgado)', img: '/biotipos/ectomorfo.webp', desc: 'Complexión delgada (hombros estrechos, huesos largos y finos, articulaciones pequeñas), pero aspecto un poco "blando" (no gordo), sin tono muscular. Metabolismo muy rápido, quema calorías con facilidad y le cuesta ganar peso. No suele tener apetito, le cuesta comer. Acumula poca grasa, sobre todo en abdomen y parte baja de la espalda.' },
    { value: 'ecto-meso', label: 'Ecto-meso (el "fibrado")', img: '/biotipos/ecto-meso.webp', desc: 'Delgado pero "fibroso" (como el anterior, pero con tono). Suele ser nervioso y le gusta el deporte, normalmente cardio, que se le da mejor. Si entrena fuerza hace descansos cortos, no puede estar parado. Puede acumular algo de grasa en el abdomen, pero no suele ser problema por ser más activo.' },
    { value: 'ecto-endo', label: 'Ecto-endo (el "gordi-flaco")', img: '/biotipos/ecto-endo.webp', desc: 'Delgado pero con "tripita", no se cuida mucho la dieta (el típico "fofisano"). Se ve claramente que es una persona delgada pero con más grasa. No la acumula concentrada en un solo sitio, sino dispersa por varias áreas (abdomen, caderas, espalda baja) en cantidades pequeñas.' },
    { value: 'mesomorfo', label: 'Mesomorfo (el fuerte)', img: '/biotipos/mesomorfo.webp', desc: 'El típico que está fuerte de serie, con buena genética para desarrollar músculo en cuanto entrena. Estructura ósea ancha, ideal para la fuerza, con clavículas amplias y caderas estrechas. Come bastante y no coge grasa con facilidad. Si acumula, en abdomen y algo en piernas.' },
    { value: 'meso-endo', label: 'Meso-endo (el "gordi-fuerte")', img: '/biotipos/meso-endo.webp', desc: 'Gana músculo con facilidad pero también grasa. Le gusta bastante comer; para no taparse tiene que cuidarse todo el año, incluso en volumen. Como no necesita comer mucho para ponerse fuerte y le gusta comer, lo normal es verle "tapado". Grasa en abdomen, caderas y espalda baja.' },
    { value: 'endo-meso', label: 'Endo-meso (el grande)', img: '/biotipos/endo-meso.webp', desc: 'Como el meso-endo pero con más tendencia a ganar grasa. Se le ve "grande", tiene músculo pero niveles muy altos de grasa. Le gusta comer y para definir tiene que comer poco, cosa que le cuesta mucho. Grasa sobre todo en abdomen, caderas, espalda baja y muslos.' },
    { value: 'endomorfo', label: 'Endomorfo (el gordo)', img: '/biotipos/endomorfo.webp', desc: 'Tendencia clara a engordar y niveles altos de grasa casi toda la vida. Suele llevar vida muy sedentaria y malos hábitos. El abdomen es la zona más problemática (barriga prominente, grasa visceral). También acumula en muslos, caderas, brazos y espalda.' },
];

import BodyFatSlider, { BF_PERCENTAGES, BF_DEFAULT } from '../components/SelectorGrasa';
import { mensajeDeError } from '../lib/mensajeDeError';
import { hoyLocal, pintarDia, ahoraDeLaApp } from '../lib/horaEspana';
import { menusDelDia } from '../lib/menusDelDia';
import { fechaDeNacimientoUsable, porQueNoValeLaFecha } from '../lib/fechaDeNacimiento';
import { esUnTelefono, esUnCorreo, TELEFONO_QUE_NO_VALE, CORREO_QUE_NO_VALE } from '../lib/contacto';
import { pareceUnaImagen, NO_ES_UNA_FOTO } from '../lib/pareceUnaImagen';
import { porQueNoValeElAno } from '../lib/anoDelHito';
import { RANGOS_PERFIL, datoDelPerfilUsable, porQueNoValeElDato } from '../lib/rangosDelPerfil';
import { num1 } from '../lib/numeros';
import { esperaElBorrador, pantallaAnterior, pantallaParaGuardar, pasoDelBorrador } from '../lib/pantallaDelBorrador';
import { falta } from '../lib/huecosDelBasico';
import { quiereSinPeri } from '../lib/pasosDelPeri';
import { objetivosDelCuestionario, objetivoValePara, esMujer } from '../lib/objetivos';
import { COLOR_MACRO } from '../components/inicio/MacrosHoy';

// ─────────────────────────────────────────────────────────────────────────────
// EL ALTA - cuatro preguntas y ya tiene macros (doc 29-07, paso 1).
//
// El texto en el género de quien contesta (31-10). Los valores no cambian, solo lo que se lee.
const porSexo = (a, hombre, mujer) => (esMujer(a?.sex) ? mujer : hombre);

// Solo lo que hace falta para leer la tabla: sexo, objetivo, % de grasa y peso. De aqui sale
// con MACROS PROVISIONALES y con la app usable el mismo dia. Todo lo demas espera al
// cuestionario de ajuste, detras de un boton, porque cada pantalla que se pone antes de
// entregar algo cuesta gente.
//
// El nombre y el telefono se piden en el registro, no aqui.
//
// type: statement | text | email | tel | date | number | choice | bf | dieta | final0 | result
// Los datos de la tabla: sexo, objetivo, peso y grasa. Ya NO terminan en un cálculo: desde
// el 07-08 el alta es un único recorrido (punto 15 del documento) y estas preguntas son solo
// su primer tramo. El cálculo va una sola vez, al final, cuando ya están también las
// respuestas que mueven los hidratos; así lo que se le entrega son sus macros de verdad y no
// unos provisionales que había que ajustar después en otro cuestionario.
// Los textos son los del documento «LOS TEXTOS DE LA APP» de Jesús (6 de agosto, versión
// definitiva), literales, con su aclaración debajo de cada pregunta. El orden también es el
// suyo: objetivo, confirmación, experiencia, datos, actividad, deporte, apetito, engordo,
// definir, biotipo, grasa y dieta.
const PREGUNTAS_ALTA = [
    { type: 'statement', title: 'Empecemos' },
    {
        type: 'choice', key: 'goal', title: '¿Cuál es tu objetivo?',
        // EL SUBTÍTULO VUELVE (24-10): se quitó el 18-10, manda lo más reciente. Solo en
        // hombre: en mujer son tres opciones (31-10) y «una de dos» ya no es verdad.
        desc: (a) => porSexo(a, 'Una de dos: o ganar masa muscular o perder grasa. Las dos cosas a la vez, NO', ''),
        // CON EL «QUIERO» DELANTE (Francisco, 15-09, sobre el documento del 7-09). Sin él
        // son dos etiquetas; con él es una decisión suya, que es lo que esta pregunta pide.
        // En minúscula desde el 24-10: «masa muscular» y «grasa». La lista es la de
        // `lib/objetivos`, la misma que ofrece el mensual.
        options: (a) => objetivosDelCuestionario(a.sex).map((o) => ({ value: o.valor, label: o.texto })),
    },
    {
        // DOS PANTALLAS, CADA UNA CON SU TEXTO (Jesús, 11-09, punto 10).
        //
        // Había una sola confirmación y llevaba el texto del que elige VOLUMEN -- «no
        // quiero que me digas que tú en realidad lo que querías era definir» --, así que al
        // que acababa de elegir perder grasa se le hablaba del caso contrario.
        //
        // Son la misma pantalla con el texto que toca, que es lo que él pide: cada objetivo
        // con el suyo, y los dos diciendo a qué renuncia.
        type: 'choice', key: '_confirm', title: '¿Estás seguro?',
        // SOLO EN HOMBRE (Francisco, 01-11): en mujer no sale con ninguno de sus objetivos.
        cond: (a) => !esMujer(a.sex),
        // LOS DOS TEXTOS, LOS SUYOS (documento del 7-09, preguntas 03 y 04). Los que había
        // eran una reformulación nuestra, la misma frase para los dos casos cambiando el
        // macro. Al que elige VOLUMEN se le avisa de lo que va a reclamar después; al que
        // elige DEFINICIÓN se le dice a qué renuncia.
        desc: (a) => (a.goal === 'definicion'
            ? 'Si confirmas que sí, estás asumiendo que a partir de este momento lo de ganar '
              + 'masa muscular pasa a un segundo plano y la prioridad ahora será perder '
              + 'grasa, tratando de mantener el músculo que ya hay.'
            : 'Mira bien, que luego no quiero que me digas que tú en realidad lo que querías '
              + 'era definir y perder grasa.'),
        // opciones dinámicas según goal (se generan en render)
        confirm: true,
    },
    {
        // Pantalla 3 del documento. Estaba en el cuestionario largo, que solo ven los planes
        // con entrenador, y con cinco opciones por años. Jesús la quiere en el test de
        // entrada y con estas cuatro: lo que le interesa no es el desarrollo muscular, es si
        // sabe entrenar.
        type: 'choice', key: 'training_experience', title: '¿Qué experiencia tienes entrenando fuerza en el gimnasio?',
        desc: 'Me da igual el grado de desarrollo muscular que tengas en este momento, me interesa saber si sabes entrenar y cuánta experiencia tienes.',
        options: [
            // Los cuatro, literales del documento del 18-08.
            // El nivel, en mayúsculas desde el 24-10.
            { value: 'cero', label: 'Ninguna, empiezo ahora o hace mucho que no entreno. Parto de cero' },
            { value: 'principiante', label: 'Llevo menos de 1 año entrenando con regularidad (PRINCIPIANTE)' },
            { value: 'intermedio', label: 'Llevo más de un año entrenando, aunque no siempre me lo he tomado en serio (INTERMEDIO)' },
            { value: 'avanzado', label: 'Llevo años entrenando de forma seria (AVANZADO)' },
        ],
    },
    {
        type: 'choice', key: 'sex', title: 'Hombre o mujer.',
        options: [
            { value: 'hombre', label: 'Hombre' },
            { value: 'mujer', label: 'Mujer' },
        ],
    },
    // El enunciado del documento, que es una pregunta y no una etiqueta.
    {
        type: 'number', key: 'weight', title: '¿Cuánto pesas?',
        desc: 'Pésate siempre en las mismas condiciones: en ayunas, sin ropa y después de ir al baño.',
        unit: 'kg', required: true,
    },
    {
        type: 'bf', key: 'body_fat', title: '¿Cuál dirías que es tu % de grasa actual?',
        // El texto del prompt del rediseño (20-10, punto 1), en lugar del del 18-08.
        desc: 'Sube tu foto y desliza el carrusel hasta situarla entre los porcentajes, en el '
            + 'rango aproximado que consideres que debería estar.',
    },
];

// ─────────────────────────────────────────────────────────────────────────────
// AJUSTAR MACROS - el cuestionario del paso 2, detras de un boton.
//
// Todo lo que ajusta el numero: lo que hace fuera del gimnasio, como responde su cuerpo y la
// dieta que trae. Al terminar se le entregan los MACROS DEFINITIVOS.
// Trae una dieta de la que partir: la mide (`true`) o se cuida sin medirla (`parecido`).
// El "sin control pero no como mal" (`false`) y el "como mal y desorganizado"
// (`desorganizado`) no traen nada que copiar, asi que a esos dos no se les pregunta por su
// dieta. Se define aqui, en un solo sitio, porque de esto colgaban seis condiciones sueltas
// y al anadir la cuarta respuesta del documento de textos habia que acertar en las seis.
const traeDieta = (a) => a.sigue_dieta === true || a.sigue_dieta === 'parecido';

const STEPS_AJUSTE = [
    { type: 'statement', title: 'Ajusta tus macros', cta: 'Vamos' },
    {
        // CUATRO opciones (pantalla 5 del documento de textos), donde antes había tres. Ojo
        // con los macros: el +10 % de hidratos lo cobra SOLO "muy activo", que es lo que dice
        // el documento del 07-08. Las otras tres no suben nada, ni siquiera la de en medio.
        // Los valores viejos (`sedentario` / `normal`) se conservan para los clientes que ya
        // contestaron: `sedentario` es ahora "muy sedentario" y `normal` "ligeramente activo".
        type: 'choice', key: 'actividad_diaria',
        title: '¿Cuánto te mueves en tu día a día?',
        // El texto y las cuatro opciones, los del 24-10 (con los pasos de cada tramo).
        desc: 'Aquí no cuenta el entrenamiento, si es que entrenas. Piensa en tu trabajo, tus '
            + 'desplazamientos y tu tiempo libre. Si tu móvil o tu reloj cuenta pasos, usa tu media '
            + 'de la última semana. Y si dudas entre dos opciones, elige la más baja: casi todos nos '
            + 'movemos menos de lo que pensamos.',
        // LOS CUATRO TEXTOS, LOS SUYOS (Francisco, 15-09, sobre el documento del 7-09). Los
        // que había estaban acortados y reescritos por nosotros; estos son los del documento,
        // literales. Son largos a propósito: lo que distingue a un «ligeramente activo» de un
        // «moderadamente activo» está justo en el detalle que se había recortado.
        // LOS VALORES NO SE TOCAN, solo los textos: `sedentario`, `normal`, `moderado` y
        // `muy_activo` están escritos en las fichas de todos los que ya contestaron, y
        // cambiarlos les dejaría la respuesta huérfana y el macro sin su modificador.
        options: (a) => [
            { value: 'sedentario', label: `Muy poco: paso casi todo el día ${porSexo(a, 'sentado', 'sentada')}: trabajo, coche y sofá. Menos de 5.000 pasos al día` },
            { value: 'normal', label: `Poco: trabajo ${porSexo(a, 'sentado', 'sentada')}, pero me muevo algo cada día: camino, voy andando a sitios, subo escaleras. Entre 5.000 y 8.000 pasos` },
            { value: 'moderado', label: 'Bastante: paso buena parte del día de pie o caminando. Trabajos como dependiente, camarero, profesor o sanitario. Entre 8.000 y 12.000 pasos' },
            { value: 'muy_activo', label: 'Mucho: hago esfuerzo físico durante casi toda la jornada. Trabajos como construcción, almacén, reparto o campo. Más de 12.000 pasos' },
        ],
    },
    {
        type: 'choice', key: 'deporte_extra', title: '¿Practicas otro deporte con intensidad?',
        // El subtítulo del doc del 23-08 (punto 11): lo que importa no es la lista de
        // deportes, es si compite o entrena en serio y piensa seguir.
        desc: 'Me refiero a si compites en algo, o entrenas con intensidad algún deporte y tienes intención de continuar con ello.',
        options: [
            { value: true, label: 'Sí' },
            { value: false, label: 'No' },
        ],
    },
    {
        // Las dos preguntas de seguimiento de la pantalla 6. Solo para el que ha dicho que sí.
        // No mueven macros: sirven para que el entrenador sepa qué hace y cuándo.
        type: 'text', key: 'deporte_cual', title: '¿Cuál, cuántos días y a qué intensidad?',
        cond: a => a.deporte_extra === true,
        desc: 'Indica tanto lo que haces ahora, como lo que te gustaría mantener una vez que '
            + 'empecemos con la rutina de fuerza.',
        placeholder: 'Por ejemplo: pádel, dos días entre semana, a buen ritmo',
        textarea: true, required: true,
    },
    // AQUÍ IBA «¿Habría posibilidad de que lo hicieras en días en que no vayas al gimnasio?»
    // y SE CAE (Francisco, 15-09). No está en el documento del 7-09: el bloque del deporte
    // son dos pantallas, la del sí o no y la de «¿cuál, cuántos días y a qué intensidad?».
    // El campo se queda declarado en el modelo y se sigue leyendo en la ficha de quien ya lo
    // contestó; sencillamente deja de preguntarse.
    {
        // Pantalla 7, nueva. No mueve macros: alimenta el perfil.
        // «Casi nada» es del doc del 23-08 (punto 6): cuarta opción, tampoco mueve macros.
        type: 'choice', key: 'apetito', title: '¿Eres de buen comer?',
        options: [
            // Textos del 24-10; los valores, los de siempre.
            { value: 'mucho', label: 'Sí, mucho' },
            { value: 'normal', label: 'Lo normal' },
            { value: 'poco', label: 'Más bien poco' },
            { value: 'casi_nada', label: 'Al revés, me cuesta comer y me lleno enseguida' },
        ],
    },
    {
        // LAS CUATRO DEL DOC DEL 23-08 (punto 7). Los valores viejos se conservan
        // (enseguida/normal/casi_no); el cuarto es `nada` y en el motor cobra el mismo
        // +20 % de hidratos que `casi_no` (macro_engine.RESPUESTAS_QUE_SUBEN): quien dice
        // que le cuesta mucho coger peso no puede subir menos que quien dice «casi no».
        type: 'choice', key: 'facilidad_engordar', title: 'Cuando te pasas comiendo, ¿tiendes a engordar?',
        desc: 'Piensa en vacaciones, Navidades o épocas en las que sueles comer de más.',
        options: (a) => [
            // Textos del 24-10; los valores, los de siempre (el motor los lee).
            // La de mujer, «engordo» (04-11); la de hombre, la de siempre.
            { value: 'enseguida', label: porSexo(a, 'Sí, me pongo gordo rápido, a nada que me paso',
                'Sí, engordo rápido, a nada que me paso') },
            { value: 'normal', label: 'Lo normal' },
            { value: 'casi_no', label: 'No, salvo que haga grandes locuras' },
            // La de mujer, «nunca he tenido sobrepeso» (04-11); la de hombre, la de siempre.
            { value: 'nada', label: porSexo(a, 'No, nada. Coger peso me cuesta horrores, no estuve gordo en mi vida',
                'No, nada. Coger peso me cuesta horrores, nunca he tenido sobrepeso') },
        ],
    },
    {
        // No mueve macros: alimenta el biotipo declarado.
        // «¿Te cuesta perder peso?» (doc 23-08, punto 8): la palabra «definir» es de
        // gimnasio y no todo el mundo la tiene. La clave y los valores se conservan.
        type: 'choice', key: 'cuesta_definir', title: '¿Te cuesta perder peso?',
        options: [
            { value: 'mucho', label: 'Sí, mucho' },
            { value: 'normal', label: 'Lo normal' },
            { value: 'poco', label: 'Qué va, todo lo contrario' },
        ],
    },
    {
        // ── El biotipo y la altura, para LOS TRES PLANES ──────────────────────────
        // Estaban en el perfil largo, que solo ven los planes con coach, así que el de
        // 297 € nunca decía qué biotipo cree que es ni cuánto mide. Y sin altura no hay
        // índice de muscularidad, que es justo el regalo del acceso gratis.
        //
        // No mueven macros: el biotipo es una hipótesis de partida que el coach corrige
        // viendo cómo responde. Pero se guardan, y sin ellos no hay ni ficha ni modelo.
        type: 'biotype_intro',
        title: 'Ahora tienes que elegir tu biotipo, es decir, tu tipo de cuerpo.',
        desc: 'Es la tendencia natural de tu cuerpo según tu genética (independientemente de tu estado físico actual o los hábitos que tengas en este momento). Antes te explico los 7 que hay (después eliges):',
        // EN MUJERES NO SE ENSEÑA. Las siete fotos de los biotipos son de hombre y no hay
        // versión de mujer: enseñárselas para que se identifique es pedirle que elija mal.
        // Petición explícita del documento del 06-08-2026 ("que el test de mujer no
        // muestre la pantalla del biotipo"). Cuando existan las fotos, se quitan las dos
        // condiciones de aquí y de la pantalla siguiente.
        cond: a => a.sex !== 'mujer',
    },
    {
        type: 'biotype', key: 'biotype',
        title: 'Indica cuál de los 7 biotipos corporales es el tuyo',
        desc: 'Puedes volver atrás y leer las descripciones. Si no te identificas claramente con ninguno, elige el que más se acerque a ti.',
        cond: a => a.sex !== 'mujer',
    },
    {
        type: 'number', key: 'height', title: '¿Cuánto mides?', desc: 'Tu altura, en cm.',
        unit: 'cm', required: true,
    },
    {
        // CUATRO respuestas, las del documento de textos de Jesús (06-08, pantalla 12).
        // Antes eran tres, y antes de eso dos. La diferencia que importa: las dos primeras
        // traen una dieta de la que partir y las dos últimas no, pero "sin control pero no
        // como mal" y "como mal y desorganizado" no son lo mismo y hasta ahora caían las dos
        // en el mismo saco.
        //
        // Los valores se conservan (`true` / `parecido` / `false`) para no romper lo que ya
        // está guardado de los clientes que contestaron antes; el cuarto es `desorganizado`,
        // que es nuevo y se comporta como el "sin control" a efectos de macros.
        type: 'choice', key: 'sigue_dieta', title: '¿Sigues algún tipo de dieta en este momento?',
        // Los textos del 25-10; los valores, los de siempre (los lee el motor de macros).
        options: [
            { value: true, label: 'Sí, llevo una dieta estricta y mido todo lo que como' },
            { value: 'parecido', label: 'No hago dieta estricta ni peso la comida, pero me cuido bastante' },
            { value: false, label: 'No suelo comer mal, pero tampoco lo miro mucho' },
            { value: 'desorganizado', label: 'Como fatal y de forma totalmente desorganizada' },
        ],
    },
    {
        // P7: se guarda. Un mes comiendo asi no dice lo mismo que seis.
        type: 'choice', key: 'tiempo_dieta', title: '¿Cuánto tiempo llevas con esa dieta, o con una parecida?',
        cond: traeDieta,
        options: [
            { value: 'menos_1m', label: 'Menos de un mes' },
            { value: '1_3m', label: 'Entre 1 y 3 meses' },
            { value: '3_6m', label: 'Entre 3 y 6 meses' },
            { value: 'mas_6m', label: 'Más de 6 meses' },
        ],
    },
    {
        // P8: la que decide que se hace con su dieta (paso 4 del metodo). Las opciones cambian
        // segun el objetivo, porque "ir bien" no es lo mismo definiendo que en volumen.
        // A LOS TRES, también al que come lo que surge. Es la mejor pregunta del
        // cuestionario: sitúa su comida respecto a su mantenimiento sin pedirle un solo
        // número. Si dice que mantiene, lo que come ES su mantenimiento, y eso pesa más
        // que "como bastante", que es una opinión. Antes solo se le preguntaba al que
        // seguía una dieta medida, que es justo el que menos falta le hace.
        type: 'choice', key: 'como_va',
        title: a => (!traeDieta(a)
            ? 'Con lo que comes ahora, ¿mantienes el peso, ganas o pierdes?'
            : '¿Cómo te está funcionando?'),
        options: a => (a.goal === 'volumen' ? [
            { value: 'bien', label: 'Bien: estoy subiendo peso.' },
            { value: 'lento', label: 'Regular: subo, pero muy lento.' },
            { value: 'mucha_grasa', label: 'Regular: subo, pero cojo más grasa de la cuenta.' },
            { value: 'mantengo', label: 'Me mantengo igual, siento que necesito comer más.' },
            { value: 'bajando', label: 'Mal: en lugar de subir, estoy bajando.' },
        ] : [
            { value: 'bien', label: 'Bien: estoy bajando a buen ritmo.' },
            { value: 'lento', label: 'Estoy bajando, pero muy lento.' },
            { value: 'mantengo', label: 'Me mantengo.' },
            { value: 'cogiendo_peso', label: 'Mal: estoy cogiendo peso.' },
        ]),
    },
    {
        // P9: no cambia el macro de arranque; marca el ritmo de los ajustes de cada mes.
        type: 'choice', key: 'hambre_saturacion',
        title: a => (a.goal === 'volumen' ? `¿Estás ${porSexo(a, 'saturado', 'saturada')} de comer?` : '¿Pasas hambre o ansiedad comiendo así?'),
        cond: traeDieta,
        options: a => (a.goal === 'volumen' ? [
            { value: 'no_puedo_mas', label: `No estoy ${porSexo(a, 'saturado', 'saturada')}, pero tampoco me veo capaz de comer más.` },
            { value: 'puedo_mas', label: 'Puedo comer más sin problema.' },
        ] : [
            { value: 'mucho', label: 'Mucho.' },
            { value: 'normal', label: 'Lo normal cuando estás a dieta.' },
            { value: 'aguanto_mas', label: 'Nada: aguanto mucho más que esto.' },
        ]),
    },
    {
        // Al que no lo tiene medido se le pide UN DÍA, no "lo que comes en general".
        // Nadie sabe lo que come en general; todo el mundo se acuerda de lo que comió
        // ayer. Con un día tipo el sistema ya puede leerlo, y la pregunta imposible se
        // convierte en una fácil.
        // EL DÍA TIPO SE LO PIDE A TODOS (pantalla 19 del doc del 18-08). Antes solo salía
        // a quien decía seguir una dieta, y justo el que come «sin control» o «mal y
        // desorganizado» es del que menos se sabe. Su día no entra en el cálculo si no
        // trae dieta -- eso lo decide el motor, no esta pantalla -- pero se guarda, que es
        // de lo que va la pregunta: «para que sepamos de dónde partes más o menos».
        type: 'dieta', title: 'Indica un día tipo',
        desc: a => (a.sigue_dieta === 'parecido'
            ? 'Aunque no comas siempre lo mismo, ponme un día tipo. El de ayer, por ejemplo.'
            : 'Para que sepamos de dónde partes más o menos.'),
    },
    // «CONFIRMA TUS RESPUESTAS» (doc del 23-08, punto 16): antes decía «Y ya estaría» sin
    // enseñar ni una sola respuesta. Ahora es el repaso con la rejilla; tocar una tarjeta
    // salta a su pregunta y contestar devuelve aquí.
    { type: 'final0', title: 'Confirma tus respuestas', desc: 'Toca encima para cambiar algo. Si todo está OK, dale a confirmar y seguimos.' },
    { type: 'result', title: 'Tus macros' },
];

// Lo que viene despues de entregar los macros. Aqui ya no se le PIDE nada: se le ENSEÑA.
//
// Salieron de aqui, por el doc del 29-07:
//   - cuantas comidas, cuantos dias entrena y cuando entrena -> al perfil, con los valores por
//     defecto del metodo. Ninguna cambia los totales, solo el reparto, y se cambian cuando quiera
//     desde Nutricion. Puestas aqui eran tres pantallas que no movian ningun numero.
//   - los alimentos que le gustan y las alergias -> a la primera dieta. No sirven para calcular
//     macros, sirven para generar comida, y tienen sentido justo cuando va a ver su primer menu.
const STEPS_ONBOARD = [
    // Paso 3 del doc: se le piden fotos y medidas justo despues de darle los macros, que es
    // cuando mas motivado esta. Y a cambio se le entrega su ficha.
    { type: 'partida', title: 'Apunta tus medidas' },
    { type: 'magia', title: 'Comidas que puedes comer hoy' },
];

// NIVEL 1 - solo planes con coach. Alimenta el perfil y el caso gemelo; NO toca macros.
//
// El biotipo y la altura SE FUERON DE AQUÍ a las preguntas que ven los tres planes
// (06-08-2026): estando aquí, el de 297 € no los daba nunca, y sin altura no hay índice
// de muscularidad. Preguntarlos otra vez aquí sería preguntar dos veces lo mismo.
// Las preguntas que viven en el cuestionario de ajuste y que el completo también usa. Se
// referencian, no se copian: una pregunta, un sitio donde se define. Si alguien le cambia el
// texto a una, cambia en los dos cuestionarios, que es lo que se quiere.
const delAjuste = (clave) => {
    const paso = STEPS_AJUSTE.find(s => s.key === clave);
    if (!paso) throw new Error(`El completo pide una pregunta del ajuste que no existe: ${clave}`);
    return paso;
};

// LOS HITOS DE PESO, cada uno con su año. Se definen aquí arriba porque los usan los dos
// cuestionarios: el básico se los pregunta a todo el mundo, y el completo solo al que
// entró antes de que el básico existiera.
//
// «EL PESO MÁS BAJO» SE QUITÓ ENTERO (doc del 23-08, punto 4): eran tres hitos y quedan
// dos. El campo `peso_minimo` sigue existiendo en el modelo y en las fichas que lo
// tienen contestado; solo deja de preguntarse.
const PESO_HITOS = [
    {
        // Obligatoria (regla 2 del doc del 23-08): el peso y su año. La nota sigue siendo
        // opcional, que para eso lo dice su etiqueta.
        type: 'peso_hito', key: 'peso_maximo', required: true,
        title: '¿Cuál fue tu máximo peso alcanzado y cuándo?',
        desc: 'Me refiero a cuando estuviste en tu peor forma física.',
        nota: 'Cuéntame lo que quieras de esa época',
        // «ESTOY EN MI PEOR MOMENTO DE FORMA» (24-10): un botón que lo contesta con su peso
        // de hoy y el año en curso, y pasa a la siguiente.
        peorAhora: true,
    },
    {
        // SIN «SI NO, PÁSALA» (24-10): el que nunca ha estado en forma no contesta nada y
        // le da a Continuar. Dejarlo en blanco se apunta igual que antes lo apuntaba el
        // «pásala» (`mejor_forma_pasada`), para que no se le vuelva a pedir.
        type: 'peso_hito', key: 'peso_mejor_momento', conFoto: true,
        title: '¿Has estado muy en forma alguna vez?',
        desc: 'Si es que sí, dime cuándo y tu peso en aquel momento (si nunca estuviste en '
            + 'forma, simplemente no contestes nada).',
        nota: 'Cuéntame lo que quieras',
    },
];

const STEPS_NIVEL1 = [
    // Sin el «estas ya no cambian tus macros» del final (punto 4.18, decisión de Jesús): el
    // cliente no tiene por qué saber qué pregunta mueve qué número, y decírselo solo invita
    // a contestar a la ligera lo que él cree que no cuenta.
    // «Para el equipo» suena a departamento; al cliente se le habla de nosotros. Y el número
    // de preguntas se dice: sin él, «tu perfil completo» deja la sensación de que puede ser
    // eterno, y eso cuesta altas (Jesús, 11-08). El número se cuenta abajo sobre la lista de
    // verdad, para que no se quede viejo en cuanto alguien añada o quite una.
    // «Ahora, tu perfil completo» (la portada con cuántas preguntas quedan) se quita el 18-10.
    { type: 'date', key: 'birthdate', title: 'Fecha de nacimiento', required: true },
    // La experiencia entrenando SE FUE DE AQUÍ al test de entrada (pantalla 3 del documento
    // de textos de Jesús), y con sus cuatro opciones, no con los cinco tramos por años que
    // había. Aquí solo la veían los planes con entrenador; ahora la contestan los tres, que
    // es lo que él quiere: le interesa saber si sabe entrenar, no cuántos años lleva.
    //
    // ── Pantalla 01 del completo · lo médico, EN UNA ────────────────────────────
    //
    // ERAN CUATRO PANTALLAS Y SON DOS (Francisco, 15-09, sobre el documento del 7-09). Se
    // preguntaba la patología, su detalle, la medicación y su detalle, por separado. Su
    // documento lo pide en una: «¿Tomas alguna medicación o tienes alguna patología que te
    // impida entrenar o seguir un plan nutricional?» y un solo detalle detrás. Es lo mismo
    // que quiere saber -- si hay algo que desaconseje lo que se le va a mandar -- y son dos
    // pantallas menos en el sitio donde más cuesta que siga contestando.
    //
    // SE QUEDA EN LA CLAVE `patologia`, que es la que lee el panel. `medicacion` y
    // `medicacion_detalle` no se borran: siguen en el modelo y en la ficha de quien los
    // contestó, solo dejan de preguntarse.
    {
        type: 'choice', key: 'patologia',
        title: '¿Tomas alguna medicación o tienes alguna patología que te impida entrenar o seguir un plan nutricional?',
        options: [
            { value: 'no', label: 'No' },
            { value: 'si', label: 'Sí' },
        ],
    },
    {
        type: 'text', key: 'patologia_detalle', textarea: true,
        title: 'Dime qué tienes exactamente, en qué grado te afecta y si tu médico está al corriente de que quieres empezar con este programa.',
        cond: a => a.patologia === 'si',
    },
    // ── Pantalla 02 del completo · la farmacología, TODA EN UNA ─────────────────
    //
    // ERAN DOS PREGUNTAS Y ES UNA MULTISELECCIÓN DE SIETE (Francisco, 15-09, sobre el
    // documento del 7-09). Iban el TRT por un lado y las ayudas farmacológicas por otro, y
    // eso obliga a elegir: «se puede estar con TRT y con quemagrasas a la vez», que es
    // literalmente lo que dice su documento. Ahora se marcan las que sean.
    //
    // Los cinco valores de antes se conservan (`no`, `pasado`, `frecuente`, `quemagrasas`,
    // `ciclo`) y se añaden los dos que faltaban: el TRT y el que pide información. La clave
    // `trt` se queda en el modelo y en las fichas que la tienen: deja de preguntarse aparte.
    //
    // OJO CON LO GUARDADO: antes era un texto y ahora es una lista. Lo viejo se lee tal cual
    // -- una respuesta suelta -- y se envuelve al entrar, para que a quien contestó hace un
    // mes le salga su opción marcada y no la pantalla en blanco.
    {
        type: 'multiselect', key: 'farmacologia_uso',
        title: '¿Estás utilizando actualmente algún fármaco o tratamiento hormonal para aumentar tu rendimiento, ganar masa muscular o perder grasa?',
        desc: 'Se pueden marcar varias.',
        options: [
            { value: 'no', label: 'No, nada' },
            { value: 'pasado', label: 'Lo hice en el pasado, pero no planeo repetirlo' },
            { value: 'frecuente', label: 'Ahora mismo no, pero suelo usarlos con frecuencia' },
            { value: 'quemagrasas', label: 'Sí, fármacos para quemar grasa (Ozempic, efedrina...)' },
            { value: 'ciclo', label: 'Sí, un ciclo de esteroides anabolizantes' },
            { value: 'trt', label: 'Sí, tratamiento hormonal tipo TRT' },
            { value: 'info', label: 'No, pero me interesa recibir más información al respecto' },
        ],
    },
    {
        type: 'text', key: 'farmacologia_detalle', textarea: true,
        title: '¿Cuál, en qué dosis, desde cuándo y tienes analítica reciente? Y si son ciclos: cuántos has hecho, cuándo terminaste el último y si recuperaste bien.',
        cond: a => {
            const m = Array.isArray(a.farmacologia_uso) ? a.farmacologia_uso
                : (a.farmacologia_uso ? [a.farmacologia_uso] : []);
            return m.some(v => ['pasado', 'frecuente', 'quemagrasas', 'ciclo', 'trt',
                                'uso', 'use', 'intencion'].includes(v));
        },
    },
    {
        // EL AVISO DEL DOPAJE (documento del 7-09, detrás de su pregunta 02). No estaba en
        // ninguna parte. Va con sus dos respuestas, que es lo que lo convierte en una puerta
        // al soporte médico en vez de en una nota legal que nadie lee.
        type: 'choice', key: 'aviso_dopaje',
        title: 'Una cosa importante antes de seguir',
        desc: 'El uso de sustancias dopantes y ejercicio físico debe hacerse siempre bajo la '
            + 'dirección y supervisión de un médico autorizado. Si estás usando fármacos o '
            + 'tienes intención de hacerlo y necesitas orientación médica, puedes contratar '
            + 'nuestro soporte especializado.',
        cond: a => {
            const m = Array.isArray(a.farmacologia_uso) ? a.farmacologia_uso
                : (a.farmacologia_uso ? [a.farmacologia_uso] : []);
            return m.length > 0 && !(m.length === 1 && m[0] === 'no');
        },
        options: [
            { value: 'si', label: 'Sí, me interesa' },
            { value: 'no', label: 'No, de momento no' },
        ],
    },
    {
        // AL QUE DICE QUE SÍ LE INTERESA, QUÉ PASA AHORA (25-10). Antes pasaba a la siguiente
        // pregunta sin decirle nada. Su respuesta llega al equipo con el envío del largo.
        type: 'aviso_salud', ceja: 'Tu salud',
        title: 'En breve nos pondremos en contacto contigo para darte más detalles acerca de este servicio.',
        cond: a => a.aviso_dopaje === 'si',
    },
    // ── Pantallas 10 y 11 · el descanso ─────────────────────────────────────────
    // Estaban dentro de una pantalla de «Salud y descanso» con cinco campos sueltos, con
    // tres respuestas de andar por casa («Bien (7-8h)») en vez de los tramos de Jesús. Y el
    // nivel de estrés, que compartía pantalla con ellas, se va: decisión suya, bloque 5.
    {
        type: 'choice', key: 'horas_sueno', title: '¿Cuántas horas duermes al día de media?',
        options: [
            { value: 'min_7_30', label: 'Mínimo 7 y media' },
            { value: '6_7_30', label: 'Entre 6 y 7 horas y media' },
            { value: '5_6', label: 'Entre 5 y 6, no más' },
            { value: 'menos_5', label: 'Menos de 5' },
        ],
    },
    {
        // «¿Y DESCANSAS BIEN?» (documento «El cuestionario», 7-09). Faltaba entera: se
        // preguntaban las horas y directamente lo que toma para dormir, y eso deja fuera lo
        // que él quiere saber, que es SU frase: «no es lo mismo dormir poco que dormir mal.
        // Esto dice si el problema es conciliar, mantener o que el sueño no repara».
        // LA PREGUNTA Y SUS TRES OPCIONES, LAS ÚNICAS (20-10, prompt del rediseño, punto 20).
        // CLAVE NUEVA (`sueno_suficiente`): la de antes, `descansas_bien`, preguntaba otra cosa
        // (descansado / cansado / conciliar / despertares) y se queda como está en las fichas
        // que la tienen. Con la misma clave, un borrador viejo daba la pregunta por contestada
        // con una respuesta que ya no es de esta pregunta (revisor, 20-10).
        type: 'choice', key: 'sueno_suficiente',
        title: '¿Consideras que es suficiente para ti en este momento, o sientes que necesitas más para rendir bien?',
        options: [
            { value: 'mas_que_suficiente', label: 'Más que suficiente' },
            { value: 'poco_pero_vale', label: 'Es poco, pero me vale' },
            { value: 'necesito_mas', label: 'Necesito más, sin duda alguna' },
        ],
    },
    // «¿TOMAS ALGO PARA DORMIR MEJOR?» Y SU DETALLE SE QUITAN (20-10): lo mismo lo pide ya
    // la de la suplementación para el sueño (`para_dormir`, más abajo). Las respuestas
    // de quien ya las contestó (`ayuda_dormir`, `ayuda_dormir_detalle`) siguen en su ficha.
    // ── Pantallas 12, 13 y 14 · su suplementación ───────────────────────────────
    // No existía. El equipo le pauta suplementos sin saber qué está tomando ya, que es
    // la forma más rápida de repetirle algo o de chocar con lo que lleva.
    // EN EL ORDEN DEL DOCUMENTO (Francisco, 15-09): primero qué toma ya (su 05) y después
    // si le importa tomar (su 06). Iban al revés, y preguntarle si le molesta la
    // suplementación antes de preguntarle qué está tomando suena a que no se le escucha.
    {
        // OTRA PREGUNTA, OTRO CAMPO (20-10, prompt del rediseño, punto 21): la suplementación
        // o medicación para el sueño va a `para_dormir`. `suplementos_ahora` era «¿Qué
        // suplementos tomas ahora?»: reutilizarlo hacía leer en el panel «Para el sueño:
        // creatina y proteína» a quien contestó la vieja (revisor, 20-10).
        type: 'text', key: 'para_dormir',
        title: 'Si tomas suplementación específica para el sueño o medicación tipo benzodiacepinas, '
            + 'indica cuál, en qué dosis y con qué frecuencia. Si se trata de un fármaco, indica si '
            + 'lo haces por prescripción médica y desde cuándo.',
        textarea: true,
    },
    {
        // Las cuatro del documento. La pregunta no es si la quiere, es hasta dónde le
        // dejas llegar: las cuatro empiezan por «no» y lo que cambia es cuánto acepta.
        // Los valores `lo_justo` y `no` son los de antes, así que lo contestado se conserva.
        type: 'choice', key: 'quiere_pauta_suplementos',
        title: '¿Tienes algún inconveniente en utilizar suplementación deportiva?',
        options: [
            { value: 'libertad', label: 'No. De hecho, suelo utilizarlos habitualmente. Tienes libertad absoluta para mandarme todo lo que consideres que me vaya a ayudar a avanzar más deprisa.' },
            { value: 'abierto', label: 'No. Normalmente no utilizo, pero no tendría problema en empezar a usarlos si lo consideras oportuno.' },
            { value: 'lo_justo', label: 'No, pero ponme 1 o 2 como mucho, no me puedo permitir más o sencillamente no me apetece.' },
            { value: 'no', label: 'No me pongas nada, el tema de la suplementación lo descarto por completo.' },
        ],
    },
    {
        // Sustituye a «¿Y cuáles has tomado antes?», que se va (bloque 5: nadie se acuerda y
        // no cambia lo que se le pauta). Esta sí: lo que no puede o no quiere tomar es lo
        // único de las dos que cambia la pauta que se le manda.
        type: 'text', key: 'suplementos_veto',
        title: '¿Existe algún suplemento en concreto que tengas contraindicado o que no quieras tomar?',
        textarea: true,
        // Al que acaba de decir «No me pongas nada» no se le pregunta cuál no quiere (25-10).
        cond: a => a.quiere_pauta_suplementos !== 'no',
    },
    // ── LA DIETA ACTUAL: SE QUEDAN DOS DE LAS CUATRO (Francisco, 15-09) ─────────
    //
    // Estaban las cuatro aquí desde el punto 26 del doc del 19-08. De las cinco que su
    // documento del 7-09 dejaba «sin decidir», él decidió que se quedan estas dos -- las
    // mismas dos que nombra su punto 05: si sigue una dieta y cuánto lleva con ella.
    //
    // Las otras dos, «¿cómo te está funcionando?» y «¿pasas hambre?», salen: no están en su
    // documento. Se le siguen preguntando en el cuestionario de ajuste, que es de donde se
    // referenciaban, así que el motor no se queda sin ellas.
    delAjuste('sigue_dieta'),
    {
        // SU DÍA TIPO, EN TEXTO LIBRE (25-10), justo detrás de si sigue una dieta. Es para que
        // el equipo vea de dónde parte; no entra en el cálculo de macros, por eso va en su
        // propia clave (`dia_tipo`) y no en `dieta_texto`, que es la del lector del ajuste.
        type: 'text', key: 'dia_tipo', textarea: true, ceja: 'Dieta actual',
        title: 'Tu día tipo',
        desc: 'Pon un ejemplo de lo que comes en un día normal, lo que sería un día “tipo”.\n'
            + 'Tanto los alimentos, como las cantidades (si no pesas nada, indica más o menos el '
            + 'tamaño de la ración). En caso de que no sigas un patrón fijo, pon lo que comiste '
            + 'ayer o un día reciente que recuerdes y que pueda servir como ejemplo.',
    },
    delAjuste('tiempo_dieta'),
    // ── Las que ya contestó en el básico ────────────────────────────────────────
    // El deporte se queda en el básico (lo necesita el motor); aquí solo sale para el que
    // entró antes de que el básico existiera.
    delAjuste('deporte_extra'), delAjuste('deporte_cual'),
    // Y sus tres hitos de peso, por lo mismo: subieron al básico y el que no pasó por él no
    // los ha dado nunca.
    ...PESO_HITOS,
    {
        // P23
        type: 'choice', key: 'tiempo_intentandolo', title: '¿Cuánto tiempo llevas intentando conseguir este objetivo?',
        // CINCO, las del documento del 18-08. Faltaba «Empiezo ahora», que es justo la del
        // que se acaba de dar de alta sin haberlo intentado nunca: sin esa opción se veía
        // obligado a decir que lleva menos de seis meses, que no es verdad.
        options: [
            { value: 'ahora', label: 'Empiezo ahora' },
            { value: 'menos_6m', label: 'Menos de 6 meses' },
            { value: '6m_2a', label: 'Entre 6 meses y 2 años' },
            { value: 'mas_2a', label: 'Más de 2 años' },
            // «Llevo toda la vida con esto» (`siempre`) se quita el 18-10. Quien ya la tenga
            // contestada la conserva en su ficha: solo deja de ofrecerse.
        ],
    },
    // ── LAS DOS DEL «POR QUÉ» (30 y 31), PARTIDAS (Francisco, 8-09) ──
    //
    // Eran una sola: «Dime el motivo principal de querer trabajar conmigo, qué esperas y por
    // qué te decides a empezar ahora y no antes». La información se recogía, pero en un solo
    // texto: no se podía leer por separado ni contar, y son dos cosas distintas -- por qué
    // ÉL y no otro, y por qué AHORA y no antes.
    //
    // EN TEXTO LIBRE LAS DOS, que eso no cambia (doc del 18-08, decisión de Francisco: «la
    // versión de siempre dice lo mismo y mejor»). Las opciones cerradas le hacen elegir el
    // motivo que más se parece al suyo.
    //
    // Y SOLO SI NO CONTESTÓ LA FUNDIDA. Al que ya la respondió no se le vuelve a preguntar:
    // su texto sigue en `motivo_apuntarse` y en la ficha del entrenador, con su etiqueta de
    // siempre. Lo partido es para quien entra a partir de ahora.
    {
        type: 'text', textarea: true, key: 'por_que_a_mi', required: true,
        title: '¿Por qué motivo me has contratado a mí y no a otro?',
        cond: a => !a.motivo_apuntarse,
    },
    {
        type: 'text', textarea: true, key: 'por_que_ahora', required: true,
        title: '¿Por qué has decidido contratarme ahora y no antes?',
        // A quien nos acaba de conocer no se le pregunta por qué no antes (24-10).
        cond: a => !a.motivo_apuntarse && a.tiempo_siguiendome !== 'menos_1_semana',
    },
    // La fundida se queda declarada para las fichas que la tienen contestada: ya no se
    // pregunta en ningún sitio, pero se sigue guardando y leyendo.
    {
        type: 'text', textarea: true, key: 'motivo_apuntarse', required: true,
        title: 'Dime el motivo principal de querer trabajar conmigo, qué esperas y por qué te decides a empezar ahora y no antes.',
        cond: () => false,
    },
    // Desde la fusión del 23-08 (punto 13), al cliente nuevo esto se lo pregunta el básico
    // dentro de «¿Has tenido entrenador o has hecho dietas antes?»: aquí solo le sale a
    // quien no contestó ninguna de las dos (los de Calma). La condición del básico
    // (YA_ESTAN_EN_EL_BASICO) le añade encima el «solo si dietas_previas está vacía».
    { type: 'text', key: 'dietas_previas', title: '¿Has hecho dietas antes? ¿Qué tal te fue?', desc: 'Cuáles, cuánto duraste, qué pasó con tu peso...', textarea: true, required: true,
      cond: a => a.entrenador_anterior === undefined || a.entrenador_anterior === null || a.entrenador_anterior === '' },
    // ── Pantallas 19, 20 y 21 · cómo entrena hoy ────────────────────────────────
    // Comidas al día, días de entreno y cuándo entrena YA no se preguntan: van por defecto
    // y se cambian en Preferencias (bloque 5 del doc del 18-08).
    {
        // Bloque 3: si entrena AHORA no es lo mismo que cuántos años lleva. Uno puede
        // llevar diez años entrenando y estar parado desde marzo.
        //
        // Las CUATRO del documento. Las tres de antes no distinguían al que está parado por
        // una temporada del que no entrena, y a uno le montas la rutina de vuelta y al otro
        // una de empezar de cero.
        type: 'choice', key: 'entrena_ahora', title: '¿Entrenas ahora mismo de forma regular?',
        // SU MATIZ, QUE FALTABA (Francisco, 15-09): sin él, el que va a spinning cinco días
        // contesta que sí y le llega una rutina de intermedio.
        desc: 'Me refiero a entrenamiento de fuerza, no a ir al gimnasio a hacer cardio o clases colectivas.',
        // LAS CINCO DEL DOCUMENTO (Francisco, 15-09). Eran cuatro y con otras palabras:
        // faltaba «Voy, pero hago otro tipo de entrenamiento», que es la que separa al que
        // hace crossfit o clases del que hace fuerza, y a esos dos no se les manda la misma
        // rutina. Los cuatro valores de antes se conservan para no perder lo contestado.
        options: [
            { value: 'si', label: 'Sí' },
            { value: 'irregular', label: 'Voy, pero sin plan' },
            { value: 'otro_entrenamiento', label: 'Voy, pero hago otro tipo de entrenamiento' },
            { value: 'parada_puntual', label: 'Ahora no, fue un parón puntual' },
            { value: 'no', label: 'No' },
        ],
    },
    {
        // SU SEGUIMIENTO (documento del 7-09, completo 08): «Si va: ¿cuántos días vas en
        // este momento?». No estaba. Solo al que va, con la forma que tenga de ir.
        type: 'choice', key: 'entrena_ahora_dias',
        title: '¿Y cuántos días vas en este momento?',
        cond: a => ['si', 'irregular', 'otro_entrenamiento'].includes(a.entrena_ahora),
        options: [
            { value: 1, label: '1 día' },
            { value: 2, label: '2 días' },
            { value: 3, label: '3 días' },
            { value: 4, label: '4 días' },
            { value: 5, label: '5 días' },
            { value: 6, label: '6 días o más' },
        ],
    },
    {
        // SOLO AL QUE NO PRACTICA OTRO DEPORTE (pantalla 21 del doc): «Si ya ha dicho que
        // practica un deporte con intensidad, esta no se le enseña. No tiene sentido.»
        // Y las cuatro respuestas de Jesús, que no preguntan cuánto cardio hace sino qué
        // relación tiene con él, que es lo que decide cuánto se le puede mandar.
        type: 'choice', key: 'cardio', title: '¿Haces cardio?',
        cond: a => a.deporte_extra !== true,
        options: [
            { value: 'si_me_gusta', label: 'Sí, además me gusta, pero sé que ahora lo principal es el entrenamiento de fuerza. Por eso, en cuanto al cardio, ponme lo que consideres que me irá mejor.' },
            { value: 'si_lo_odio', label: 'Sí, pero lo odio. Lo hago porque quiero perder grasa, pero cuanto menos cardio me marques mejor.' },
            { value: 'no_pero_abierto', label: 'No, pero no tendría inconveniente en empezar a hacerlo si lo consideras oportuno.' },
            { value: 'no_jamas', label: 'No, no me gusta y no lo haré en ningún caso. Ahórratelo (aunque soy consciente de que esto implica apretar más la dieta).' },
        ],
    },
    // ── Pantalla 22 · las lesiones ──────────────────────────────────────────────
    // Antes era una sola pregunta suelta («¿hay algún ejercicio que no puedas hacer?») y
    // un campo perdido dentro de la pantalla de salud. Ahora son la pregunta y sus tres
    // detalles, que es lo que el entrenador necesita para no mandarle lo que le duele.
    {
        type: 'choice', key: 'lesion',
        title: '¿Arrastras alguna lesión o molestia que te condicione a la hora de entrenar fuerza?',
        options: [
            { value: 'si', label: 'Sí' },
            { value: 'no', label: 'No' },
        ],
    },
    {
        // UNA SOLA PANTALLA PARA LA LESIÓN (25-10): qué es, cuánto condiciona y qué ejercicios
        // no puede hacer, todo aquí. Es lo que llega a su ficha (`injuries`) y de ahí al
        // reporte mensual, donde se repasa cada mes.
        type: 'text', key: 'lesion_cual', title: 'Háblame de tu lesión', textarea: true,
        desc: 'Indica qué lesión es, en qué grado condiciona tu entrenamiento y qué ejercicios '
            + 'concretos no puedes hacer.\nTodos los meses actualizaremos tanto el estado de tu '
            + 'lesión, como los ejercicios que no puedes hacer y esto queda guardado para '
            + 'actualizarse cada mes.',
        cond: a => a.lesion === 'si',
    },
    // «ENUMERA EJERCICIOS CONCRETOS QUE NO PUEDES HACER...» SE QUITA (25-10): va dentro de la
    // de arriba. `ejercicios_imposibles` sigue en las fichas que la contestaron.
    // ── Pantalla 23 · lo que NO hay en su gimnasio ─────────────────────────────
    //
    // EL MULTISELECCIÓN DEL MATERIAL SE CAE (Francisco, 15-09). Preguntaba «¿con qué material
    // cuentas para entrenar?» con siete casillas, y no está en su documento del 7-09: su
    // punto 10 es solo el texto de abajo, la máquina que no tiene. Y es el que sirve: quien
    // entrena en un gimnasio normal lo tiene casi todo, así que la lista de lo que SÍ hay no
    // dice nada y la de lo que falta sí.
    {
        // Lo que NO tiene importa tanto como lo que tiene: una rutina con jaula de
        // sentadilla no vale de nada si en su gimnasio no hay. Con el enunciado de Jesús,
        // que además pide lo contrario: la máquina rara que sí tiene.
        type: 'text', key: 'maquinas_que_faltan',
        // Debajo de la caja (25-10). Lo que escribe aquí sale en su reporte mensual.
        nota: 'El listado de máquinas también lo organizaremos contigo todos los meses',
        title: 'En lo referente a maquinaria, si se te viene a la cabeza alguna máquina de esas que te encuentras en prácticamente cualquier gimnasio y sepas que en el tuyo no la hay, dímelo para evitar incluirla en tu rutina. Y si tienes alguna máquina más «especial», lo mismo, pónmela aquí.',
        textarea: true,
    },
    // LAS ALERGIAS E INTOLERANCIAS SE QUITAN DEL CUESTIONARIO LARGO (04-11), como en el
    // básico: «¿Alguna otra alergia o intolerancia?» y el detalle de la lactosa y el gluten.
    // Lo que no quiere o no tolera se quita en la pantalla de los excluidos. Lo que ya
    // tengan contestado los clientes de antes se queda en su ficha.
    // ── LAS CUATRO FUENTES DE ALIMENTOS SE CAEN (Francisco, 15-09) ──────────────
    //
    // Eran «¿qué proteínas comes?», «¿y qué hidratos?», «¿y de grasa?» y el desayuno. Su
    // documento del 7-09 las dejó «sin decidir» y hoy las decidió: no entran.
    //
    // LO QUE SE PIERDE, Y HAY QUE SABERLO: de `proteinas_habituales` salían las preferencias
    // de comida (`PROTEINAS_A_PREFERENCIAS`, en routes/users.py). Esa clave se quitó del
    // básico en agosto y volvió aquí justamente porque desde entonces viajaba vacía. Al
    // quitarla otra vez, ese camino se queda muerto de nuevo: las preferencias de «lo que sí
    // me gusta» solo se llenan si el cliente entra a la pantalla de Preferencias.
    // ── Pantallas 25 y 26 · sus fotos y sus medidas ─────────────────────────────
    //
    // SE PIDEN AQUÍ, DENTRO DEL CUESTIONARIO (documento «El cuestionario», 7-09; decisión de
    // Francisco del 8-09 al aplicar «manda lo más reciente»). Antes había una sola pantalla
    // que no pedía nada: explicaba por qué hacían falta y mandaba a Reportes a hacerlo allí.
    // Funcionaba, pero sacaba al cliente del cuestionario a mitad y rompía la promesa de una
    // pregunta por pantalla.
    //
    // SON LAS MISMAS DOS QUE YA USA EL QUE NO LLEVA ENTRENADOR (el encadenado del 19-08), no
    // dos nuevas: las tres fotos con su instrucción y las diez medidas, todas a la vista. Y
    // se guardan al pasar de pantalla -- las fotos al elegirlas, las medidas al continuar --,
    // así que salirse por el camino no pierde nada.
    //
    // Al que ya las tiene no se le piden: las dos se saltan solas (ver `visible`).
    // EL PUNTO DE PARTIDA, AL FINAL DEL LARGO Y EN ESTE ORDEN (25-10): primero las medidas
    // («Apunta tus medidas», la del vídeo, que iba justo detrás de los macros) y luego las
    // fotos. `medidas_alta` se quita de aquí: eran las mismas diez medidas.
    { type: 'partida', title: 'Tus primeras medidas', ceja: 'Tu punto de partida',
      desc: 'Esta primera medida será nuestra referencia siempre.\nEste proceso volveremos a '
          + 'repetirlo dentro de un mes, con cada reporte.\nHazlo siempre en las mismas '
          + 'condiciones y que te ayude alguien, que medirse uno mismo es muy difícil (y a poder '
          + 'ser, siempre la misma persona).' },
    { type: 'fotos_alta', title: 'Ahora tus fotos', ceja: 'Tu punto de partida',
      desc: (a) => `Frente, perfil (elige uno y que sea siempre el mismo) y espaldas. ${porSexo(a, 'Relajado', 'Relajada')} y siempre `
          + 'de cuerpo entero.\nTe tocará repetirlas igual cada mes: hazlas con buena luz, mismo '
          + 'sitio a ser posible y mismo momento del día (y si te las hace la misma persona, '
          + 'mejor aún).' },
    { type: 'final1', title: 'Perfil completo.' },
];

// ═══════════════════════════════════════════════════════════════════════════════
// EL BÁSICO · las pantallas del bloque 2 del doc del 18-08, en su orden
// ═══════════════════════════════════════════════════════════════════════════════
//
// «24 pantallas en hombre, 22 en mujer (la mujer no lleva biotipo). Una pregunta por
// pantalla, sin títulos de sección: la barra de progreso se encarga.»
//
// Lo hace TODO EL MUNDO y es la única vez: «a los que no llevan plan personalizado no se
// les vuelve a preguntar nunca más». Por eso el básico es largo, y por eso lo que se
// conteste aquí va al perfil y no a un cuestionario de segunda fila.
//
// LAS PREGUNTAS NO SE DUPLICAN: las que ya existían se referencian por su clave, así que
// cada una sigue teniendo un solo sitio donde se define, con sus opciones y sus textos.
//
// EL DEPORTE SE QUEDA Y LAS CUATRO DE LA DIETA SE VAN (punto 26 del doc del 19-08). El
// 18-08 se quedaron aquí las cinco para no calcularle al de autogestión con menos
// información; Jesús contesta: «"¿Practicas otro deporte con intensidad?" se queda en el
// básico, con sus dos preguntas de detalle. Esa la necesita el motor. Las cuatro de la
// dieta se van al cuestionario largo. Sé lo que implica: al de autogestión se le
// calcularán los macros desde la tabla, sin ajustar por lo que ya come. Está decidido
// así.» El día tipo no es una de las cuatro y se queda: se guarda para el perfil, entre
// o no en el cálculo.
const _TODAS = [...PREGUNTAS_ALTA, ...STEPS_AJUSTE, ...STEPS_NIVEL1];
const q = (clave) => {
    const paso = _TODAS.find(s => s.key === clave);
    if (!paso) throw new Error(`El básico pide una pregunta que no existe: ${clave}`);
    return paso;
};
const porTipo = (tipo) => {
    const paso = _TODAS.find(s => s.type === tipo);
    if (!paso) throw new Error(`El básico pide una pantalla que no existe: ${tipo}`);
    return paso;
};

// ENTRENADOR Y DIETAS, FUSIONADAS EN UNA (doc del 23-08, punto 13). Antes eran la pareja
// del punto 42 («¿Has tenido entrenador o has seguido un plan de nutrición antes?» + su
// «¿qué tal te fue?») y, once pantallas después, OTRA VEZ «¿Has hecho dietas antes? ¿Qué
// tal te fue?». El mismo «qué tal te fue» dos veces. Ahora es una sola pregunta y un solo
// relato; las claves se conservan (`entrenador_anterior` + `entrenador_anterior_que_tal`)
// y `dietas_previas` deja de preguntarse en el básico (en el completo solo les sale a los
// que no contestaron ni esta ni aquella: los migrados de Calma).
//
// El «qué tal te fue» solo sale si dice que sí: al que nunca ha tenido no hay nada que
// contarle.
const PREGUNTA_DEL_ENTRENADOR_ANTERIOR = [
    {
        type: 'choice', key: 'entrenador_anterior',
        title: '¿Has tenido entrenador o has contratado un servicio de estas características antes?',
        // SÍ O NO, Y EL RELATO DEBAJO (Francisco, 16-09, en su hoja de decisiones: «Sí / No,
        // y si dice que sí, "¿qué tal te fue?" en texto libre»).
        //
        // El 15-09 estuvieron aquí las cinco de su documento del 7-09 -- «No, nunca · Sí,
        // entrenador · Sí, dietas por mi cuenta · Las dos cosas · Estoy con uno ahora mismo»
        // --, y queda dicho lo que se pierde al volver a dos: ya no se distingue al que viene
        // de un entrenador del que se ha hecho dietas por su cuenta, ni sale marcado el que
        // ESTÁ CON OTRO AHORA MISMO. Eso hay que leerlo en su «¿qué tal te fue?», que para
        // eso es texto libre y lo lee una persona. Manda lo más reciente.
        //
        // Los valores no se tocan: `si` y `no` son los de siempre, y en producción es lo que
        // hay contestado (4 síes, 2 noes y un «siempre» de la migración).
        options: [
            { value: 'si', label: 'Sí' },
            { value: 'no', label: 'No' },
        ],
    },
    {
        type: 'text', key: 'entrenador_anterior_que_tal', title: '¿Cómo fue la experiencia?',
        desc: 'Cuéntame cuándo fue, cuánto duraste, si lo contrataste más veces y qué consideras que falló',
        // SALE CON CUALQUIERA MENOS «NO, NUNCA» (15-09). Antes miraba `=== 'si'`, y con las
        // cinco opciones eso dejaba fuera al de las dietas por su cuenta, al de las dos cosas
        // y al que está con otro ahora: justo los tres que más tienen que contar.
        cond: a => !!a.entrenador_anterior && a.entrenador_anterior !== 'no',
        textarea: true, required: true,
    },
];

const EL_BASICO = [
    // 1 · cinco campos en una pantalla. El nombre y el sexo solo se piden si no vienen del
    // pago; la fecha de nacimiento, el teléfono y el email se piden siempre.
    // Sin subtítulo desde el rediseño del 18-10 (plantilla B): «Cinco datos y arrancamos»
    // se quita.
    { type: 'contacto', title: 'Antes de empezar' },
    // 2 · LA PORTADA «EMPECEMOS» SE QUITA (18-10): detrás de «Antes de empezar» va el objetivo.
    // ── QUIÉN ERES (documento del 7-09, sus doce) ──
    //
    // SIN PORTADA PROPIA (Francisco, 15-09). Este bloque ya tiene la suya: «Empecemos», la
    // pantalla de justo arriba. Poniéndole encima otra que decía «Quién eres» salían DOS
    // pantallas seguidas sin pregunta, y el cliente le daba a Continuar dos veces para
    // llegar a la primera. En su documento, detrás de «Empecemos» va el objetivo.
    // Los otros cuatro bloques sí llevan la suya, porque llegan en mitad del recorrido.
    // 3 y 4 · el objetivo y su confirmación
    q('goal'), q('_confirm'),
    // 5 · cuánto tiempo lleva intentándolo (estaba en el cuestionario largo)
    q('tiempo_intentandolo'),
    // PORTADAS DE BLOQUE (doc del 23-08, regla 4: «una pantalla de título al empezar cada
    // bloque»). Son pantallas sin pregunta: sitúan y se pasan con un botón. En el
    // ?completar=1 no salen (falta() no las reconoce y las deja fuera, que es lo que toca:
    // ahí el recorrido es la lista de huecos, no el alta entero).
    // 6, 7 y 8 · peso, altura y porcentaje de grasa
    q('weight'), q('height'), q('body_fat'),
    // 9, 10 y 11 · su recorrido de peso, cada hito con su año. Antes era UNA pantalla con
    // cuatro casillas sueltas, sin años y solo para quien llevaba entrenador.
    ...PESO_HITOS,
    // 12 · a qué se dedica y cuánto se mueve, EN DOS PANTALLAS (20-10, prompt del
    // rediseño, punto 0). Estuvieron juntas desde el 23-08; ahora la profesión va sola, con
    // su «Continuar», y el sedentarismo en la siguiente, que avanza al elegir.
    { type: 'ocupacion', title: '¿A qué te dedicas?' },
    { type: 'actividad', key: 'actividad_diaria', title: '¿Cuánto te mueves en tu día a día?', desc: q('actividad_diaria').desc },
    // ── CÓMO RESPONDES A LA COMIDA (documento del 7-09, sus siete) ──
    //
    // El rótulo es el suyo. Antes había aquí dos bloques, «Tu cuerpo» y «Tu alimentación»,
    // y su documento tiene uno solo con las siete: la experiencia entrenando, cómo come, si
    // engorda, si le cuesta perder, los dos biotipos y el día tipo.
    // (Su portada de bloque se quitó el 18-10: el bloque lo dice la ceja de cada pregunta.)
    // 13 · LA EXPERIENCIA ENTRENANDO SE VA A «TU ENTRENAMIENTO» (20-10, prompt del
    // rediseño, punto 4): abre ese bloque, delante del otro deporte.
    // 14, 15 y 16 · cómo come, si engorda y si le cuesta definir
    q('apetito'), q('facilidad_engordar'), q('cuesta_definir'),
    // 17 y 18 · los siete biotipos y el suyo (en mujer no salen)
    porTipo('biotype_intro'), q('biotype'),
    // 19 · el día tipo, con el lector. Las cuatro de la dieta ya no van con él: se
    // preguntan en el cuestionario largo (punto 26 del doc del 19-08).
    // EL DÍA TIPO SE QUITA DEL ALTA (18-10). Es lo mismo que pulsar «No lo sé, sáltalo»,
    // que pasaba de pantalla sin apuntar nada: el motor sigue sin su día, como entonces.
    // «¿Has hecho dietas antes?» YA NO VA AQUÍ (doc 23-08, punto 13): se fusionó con la
    // del entrenador de más arriba y su relato cae en `entrenador_anterior_que_tal`.
    // ── TU ENTRENAMIENTO (documento del 7-09, sus tres) ──
    //
    // Sus tres: el otro deporte, qué días entrena y si ya ha tenido entrenador. Estaban
    // repartidas entre «Tu día a día» y «Tu alimentación», que son dos bloques que su
    // documento ya no tiene.
    // (Su portada de bloque se quitó el 18-10: el bloque lo dice la ceja de cada pregunta.)
    // 13 · la experiencia entrenando, que abre el bloque (ver arriba).
    q('training_experience'),
    // 20 · el otro deporte, con sus dos de detrás.
    q('deporte_extra'), q('deporte_cual'),
    // QUÉ DÍAS DE LA SEMANA ENTRENA (tarea 7.1 del 21-08, apartados 12 y 19 del doc de
    // Jesús). CUÁNTOS días ya no se pregunta (son cuatro, bloque 5 del 18-08); CUÁLES sí,
    // porque dependen de su vida y no de la rutina: con ellos y el reparto del PDF la app
    // sabe qué grupo toca cada día, y los planes de autogestión también los necesitan
    // para marcar el entreno o el descanso en Mi semana. Nombres sin tilde a propósito:
    // son los mismos que usa el backend (routes/workout_logs.DIAS_SEMANA).
    {
        type: 'multiselect', key: 'training_weekdays',
        // Los textos del doc del 23-08 (punto 12): «tienes previsto» y la semana «tipo».
        title: '¿Qué días de la semana tienes previsto entrenar?',
        // Cuatro como mucho (24-10): son cuatro sesiones, siempre.
        desc: 'Serán siempre 4 sesiones de gimnasio de aproximadamente 1 hora. Bloquea tu calendario desde ya.',
        max: 4,
        // Y CUATRO JUSTOS (04-11): ni más ni menos, o Continuar no se enciende.
        exactos: 4,
        options: [
            { value: 'lunes', label: 'Lunes' },
            { value: 'martes', label: 'Martes' },
            { value: 'miercoles', label: 'Miércoles' },
            { value: 'jueves', label: 'Jueves' },
            { value: 'viernes', label: 'Viernes' },
            { value: 'sabado', label: 'Sábado' },
            { value: 'domingo', label: 'Domingo' },
        ],
    },
    // 22 · Y SI YA LE HA PAGADO A ALGUIEN (corrección del punto 42, doc del 19-08). Cierra
    // el bloque del entrenamiento, que es donde la pone su documento del 7-09. Va lejos de
    // «¿qué experiencia tienes entrenando?» a propósito, que es con la que se confunde y no
    // son lo mismo: «una dice cuántos años lleva levantando peso, la otra si ya le ha pagado
    // a alguien y qué tal le fue».
    ...PREGUNTA_DEL_ENTRENADOR_ANTERIOR,
    // ── CÓMO QUIERES TU DÍA (documento «El cuestionario», 7-09-2026) ──
    //
    // Las tres preguntas con las que se monta su menú de arranque, y hasta hoy NO SE HACÍAN.
    // Vivían solo como selectores dentro de Nutrición («Cambiar la configuración del día»),
    // así que el día con el que arranca cada cliente nuevo se montaba con lo que hubiera por
    // defecto -- cuatro comidas, entreno después de la comida 1 e intra + post -- sin
    // haberle preguntado nada. Al que entrena en ayunas o hace un bloque único le salía un
    // menú que no era el suyo, y tenía que ir a arreglarlo a una pantalla que todavía no
    // sabe que existe.
    //
    // VAN EN EL BÁSICO, y lo dice él: «el menú de arranque se monta en Calculadora, y un
    // Calculadora nunca rellena el completo. Sin esta pregunta, el perientreno no sabe dónde
    // colocarse y el menú sale mal».
    //
    // ESTE ORDEN NO ES EL DEL DOCUMENTO, Y SE QUEDA ASÍ (Francisco, 15-09). Su documento
    // pide el momento del entreno (23), luego las comidas (24) y luego el perientreno (25).
    // Aquí van las comidas primero porque las opciones del momento SE ARMAN con las comidas
    // ya elegidas: con 3 comidas no existe «después de la comida 3» y con un bloque único
    // solo cabe «en ayunas». Dándole la vuelta, el cliente puede guardar un momento de
    // entreno que no existe en su día, y ahí es donde se coloca el perientreno.
    //
    // Se probó al derecho del documento y se devolvió: «si afecta a la lógica no lo
    // modifiques, no dejes huecos solo por cumplir».
    // (Su portada de bloque se quitó el 18-10: el bloque lo dice la ceja de cada pregunta.)
    {
        type: 'choice', key: 'pref_num_comidas',
        title: '¿Cuántas comidas quieres hacer?',
        desc: 'Cuatro es lo ideal, pero si cumples con tus macros diarios puedes hacer tres, o incluso no hacer reparto y ajustarlo directamente con el total del día.',
        options: [
            { value: 4, label: '4 comidas' },
            { value: 3, label: '3 comidas' },
            { value: 1, label: 'No quiero reparto, prefiero todo en un bloque único y cuadrar directamente con los macros totales del día' },
        ],
    },
    // SOLO CON 4 O 3 COMIDAS (24-10): en bloque único no hay comidas entre las que colocar
    // el entreno, y la pantalla no sale.
    { type: 'momento', key: 'pref_momento',
      title: '¿En qué momento del día tienes previsto entrenar?',
      cond: a => a.pref_num_comidas !== 1 },
    {
        type: 'choice', key: 'pref_perientreno',
        title: '¿Qué perientreno quieres hacer?',
        // Por defecto solo la primera frase; «Ver más» enseña el texto entero (`descMas`).
        desc: 'Verás que en tu dieta hay un apartado llamado «perientreno».',
        descMas: 'Verás que en tu dieta hay un apartado llamado «perientreno» y se refiere a los '
            + 'momentos que van alrededor del entrenamiento: durante (intraentreno) y después '
            + '(postentreno). Ahí es especialmente interesante meter suplementos, tanto para mejorar '
            + 'el rendimiento dentro de la propia sesión como para acelerar la recuperación '
            + 'posterior. No es obligatorio, pero sí muy recomendable (solo serían dos productos). '
            + 'Entre el postentreno y la siguiente comida deja pasar al menos 90 minutos (2 horas máximo).',
        // Los textos cambian (20-10); los valores no, que son los de la configuración del día.
        options: [
            // Y otra vez el 24-10.
            { value: 'intra_post', label: 'Lo que me recomiendas (intra y post básicos)' },
            { value: 'solo_post', label: 'Solo post' },
            { value: 'solo_intra', label: 'Solo intra (después del entreno como directamente)' },
            { value: 'sin_peri', label: 'No quiero perientreno (esos macros no se pierden, se reparten entre las comidas normales)' },
        ],
    },
    // LAS DOS DE LA COMIDA VAN AQUÍ (documento del 7-09, puntos 26 y 27): lo que no
    // quiere comer y sus alergias cierran «cómo quieres tu día», detrás del momento del
    // entreno, las comidas y el perientreno. Estaban en un bloque propio de «Tu
    // alimentación», que su documento ya no tiene.
    //
    // Y EN SU ORDEN, EL 26 ANTES QUE EL 27 (Francisco, 15-09): primero lo que no quiere y
    // después las alergias. Iban al revés.
    // 26 · LO QUE NO QUIERE VER EN EL PLATO (doc del 23-08, punto 14). Sustituye a «¿Qué
    // proteínas comes habitualmente?»: para el menú de arranque lo único imprescindible es
    // no meterle lo que ha dicho que no quiere. Es la única de preferencias que hace falta
    // aquí; las de «me gusta» siguen en su pantalla de Preferencias. La clave de las
    // proteínas se queda en el modelo con lo ya contestado, solo deja de preguntarse.
    // DOS PANTALLAS DESDE EL 24-10: primero lo que no quiere (`exclusiones`, que se queda
    // con su tipo para que los borradores de antes retomen aquí) y luego lo que más le gusta
    // (`preferidos`). Las dos con el mismo selector de categorías (`components/alta`).
    {
        // El título, el de la hoja de Jesús (04-11); el subtítulo, el del 24-10.
        type: 'exclusiones',
        title: '¿Existe algún alimento o grupo de alimentos que no quieras / puedas introducir en ningún caso?',
        desc: 'Vamos primero a quitar lo que no quieras: selecciona los alimentos que te causen '
            + 'alergia, que no toleres bien o que simplemente no te gusten y directamente los '
            + 'borramos de tu calculadora para que no te salgan nunca. Puedes marcar una categoría '
            + 'entera o buscar un alimento concreto por su nombre.',
    },
    {
        // El 24-10 no trae título para esta: va uno corto y el subtítulo, literal.
        type: 'preferidos',
        title: 'Tus alimentos preferidos',
        desc: 'Ahora vamos con los que más te gustan: selecciona tus categorías preferidas y te saldrán siempre que te falten macros '
            + 'para terminar de cuadrar una comida. Algunas vienen marcadas por defecto, pero las '
            + 'puedes cambiar sin problema (salvo las grasas de buena calidad, que te saldrán '
            + 'siempre). Puedes seleccionar tantas como quieras, solo un requisito: que entre '
            + 'todas te aporten mínimo 20 g de proteínas y 20 g de hidratos.',
    },
    // 27 · «¿TIENES ALGUNA ALERGIA O INTOLERANCIA ALIMENTARIA?» SE QUITA DEL BÁSICO (04-11),
    // y con ella sus tres de detrás (lactosa, gluten y «¿Cuál?»), que solo salían según lo
    // marcado en ella. De los preferidos se pasa directo a «¿Cómo me has conocido?». Lo que no
    // tolera ya se quita en la pantalla de los excluidos, de arriba.
    // (Su portada de bloque se quitó el 18-10: el bloque lo dice la ceja de cada pregunta.)
    // 23 · cómo le conoció
    // VARIAS A LA VEZ Y CON «CONTINUAR» (24-10). Si marca «Otro», tiene que decir cuál
    // (`como_me_conociste_otro`). «Medios de comunicación» ya no se ofrece; quien la tenga
    // la conserva en su ficha. Lo guardado de antes era un texto suelto y se lee igual.
    {
        type: 'multiselect', key: 'como_me_conociste', title: '¿Cómo me has conocido?',
        otro: { valor: 'otro', clave: 'como_me_conociste_otro', etiqueta: '¿Cuál?' },
        options: (a) => [
            { value: 'newsletter', label: 'De tu newsletter diaria' },
            { value: 'instagram', label: 'Instagram' },
            { value: 'youtube', label: 'De tu canal de Youtube' },
            { value: 'ex_cliente', label: porSexo(a, 'Fui antiguo cliente de tus asesorías', 'Fui antigua clienta de tus asesorías') },
            { value: 'ex_alumno', label: porSexo(a, 'Fui antiguo alumno de una de tus formaciones', 'Fui antigua alumna de una de tus formaciones') },
            { value: 'recomendacion', label: 'Por recomendación de otros clientes o personas que te conocen' },
            { value: 'otro', label: 'Otro' },
        ],
    },
    // 29 · DESDE CUÁNDO LE SIGUE (documento «El cuestionario», 7-09). No existía: ni la
    // pregunta, ni la clave, ni el campo. Va pegada detrás de la anterior porque las dos
    // cuentan lo mismo: de dónde viene la gente y cuánto tarda en decidirse. Sin subtítulo,
    // como en el documento.
    {
        // TRAMOS NUEVOS (24-10), CON VALORES NUEVOS: los de antes partían por otros sitios
        // (mes y medio, 3 meses) y reutilizarlos haría decir a la ficha lo que no contestó.
        // Los viejos siguen en las fichas que los tienen. `anos` sí significa lo mismo.
        type: 'choice', key: 'tiempo_siguiendome', title: '¿Desde hace cuánto tiempo me conoces?',
        options: (a) => [
            { value: 'menos_1_semana', label: 'Acabo de conocerte ahora (menos de una semana)' },
            { value: '1_2m', label: 'Te sigo desde hace un mes o dos' },
            { value: '2_6m', label: 'Entre 2 y 6 meses más o menos' },
            { value: '6m_1a', label: 'Más de 6 meses y hasta un año' },
            { value: 'anos', label: porSexo(a, 'Soy seguidor tuyo de hace años', 'Soy seguidora tuya de hace años') },
        ],
    },
    // 30 y 31 · por qué a mí, y por qué ahora. Dos pantallas desde el 8-09.
    //
    // Y SOLO ESAS DOS (Francisco, 15-09). Detrás iba una tercera, `motivo_apuntarse`:
    // «dime el motivo principal de querer trabajar conmigo, qué esperas y por qué te decides
    // a hacerlo ahora». Es exactamente la suma de las dos anteriores, así que el cliente
    // contestaba lo mismo tres veces seguidas. No está en su documento del 7-09 y se cae del
    // básico; la pregunta sigue existiendo para quien ya la tenga contestada.
    q('por_que_a_mi'), q('por_que_ahora'),
    // 25 · LA PUERTA ABIERTA DEL FINAL (documento «El cuestionario», 7-09). No existía: el
    // básico saltaba del motivo al repaso. Es la última y no es obligatoria a propósito: el
    // que no tenga nada que añadir pasa de largo, y al que le queda algo dentro este es el
    // único sitio donde cabe.
    {
        type: 'text', key: 'algo_mas', textarea: true, opcional: true,
        title: 'Si se te ha pasado comentarme algo, este es el momento y el lugar',
    },
    // El cierre del básico: revisar y calcular.
    porTipo('final0'), porTipo('result'),
];

// EL COMPLETO NO REPITE NADA DEL BÁSICO (doc del 18-08). Estas seis subieron al básico y
// en el cuestionario largo dejan de preguntarse... pero SOLO a quien ya las tenga
// contestadas: los que entraron antes de esto no pasaron por el básico nuevo, y quitárselas
// del completo sería perder su respuesta para siempre. Por eso se condicionan al dato en vez
// de borrarlas.
//
// Se sustituyen por una copia con la condición puesta, no se muta el objeto: es el mismo que
// usa el básico, y mutarlo le pondría al básico la condición de no preguntar lo que no está
// contestado, que es justo lo contrario.
const YA_ESTAN_EN_EL_BASICO = [
    'birthdate', 'tiempo_intentandolo', 'motivo_apuntarse', 'por_que_a_mi', 'por_que_ahora',
    'dietas_previas',
    'lactosa', 'gluten', 'alergias',
    // Los hitos de peso, con sus años. SON DOS, no tres: «el peso más bajo» lo quitó Jesús
    // el 23-08 (punto 4 de su documento) y hoy no se pregunta en ningún sitio. `peso_minimo`
    // se queda en esta lista a propósito: es lo que impediría preguntárselo por duplicado en
    // el completo el día que él decida devolverlo al básico -- lo dejó `SIN DECIDIR` en su
    // documento del 7-09 --, y a quien lo tenga contestado de antes se lo sigue respetando.
    'peso_maximo', 'peso_mejor_momento', 'peso_minimo',
    // Las cinco que se quedaron en el básico porque mueven los macros (decisión del 18-08)
    // Las cuatro de la dieta YA NO están en el básico (punto 26 del doc del 19-08): al
    // cliente nuevo el completo se las pregunta siempre, porque llega sin contestarlas. En
    // esta lista siguen por lo mismo de todas: al que las contestó -- en el básico de
    // agosto o en un ajuste -- no se le repiten.
    'sigue_dieta', 'tiempo_dieta', 'como_va', 'hambre_saturacion',
    'deporte_extra', 'deporte_cual', 'deporte_en_descanso',
];

// «No contestada» NO ES «vacía». Media docena de estas respuestas valen `false` -- el que
// dice que no practica otro deporte, el que come sin control -- y darlas por no contestadas
// se las volvía a preguntar a quien las acababa de responder, que es justo lo que el
// documento no quiere.
const sinContestar = (a, k) => a[k] === undefined || a[k] === null || a[k] === '';

for (let i = 0; i < STEPS_NIVEL1.length; i++) {
    const paso = STEPS_NIVEL1[i];
    if (!YA_ESTAN_EN_EL_BASICO.includes(paso.key)) continue;
    // «ATRÁS» SE SALTABA LAS QUE ACABABA DE CONTESTAR (25-10). La condición miraba si la
    // respuesta estaba vacía EN VIVO: al contestar «¿Sigues algún tipo de dieta?» la pantalla
    // dejaba de existir, y Atrás desde la siguiente saltaba varias hacia atrás y no volvía a
    // enseñarla. Ahora se marca (`yaEnElBasico`) y la pantalla decide con lo que ya estaba
    // contestado ANTES de empezar el largo (`yaContestadasRef`, sacado de su ficha), que no
    // cambia mientras contesta.
    STEPS_NIVEL1[i] = { ...paso, yaEnElBasico: true };
}

// ═══════════════════════════════════════════════════════════════════════════════
// EL BÁSICO, PARA LOS QUE YA ESTABAN DENTRO
// ═══════════════════════════════════════════════════════════════════════════════
//
// «Todo esto solo lo contesta quien entre a partir de ahora. De los que ya tienes: 91 sin
// objetivo, 158 sin días de entreno, y ninguno con biotipo, zona de grasa, peso máximo,
// mejor forma ni preferencias. Es el hueco más caro que queda, porque son personas que ya
// pagan y es el dato que alimenta el modelo. Lo barato: pasarles el básico dentro de la app
// la próxima vez que entren, con la razón por delante.» (bloque 6 del doc del 18-08)
//
// No se le vuelve a pasar el básico entero: se le pasan SOLO las pantallas cuya respuesta no
// tenemos. A quien ya dio su altura no se le pregunta la altura otra vez, que es la forma más
// rápida de que cierre la pestaña. La regla, en `lib/huecosDelBasico`.

// LO QUE GANA, SEGÚN QUIÉN LE LLEVE LOS MACROS. Al de autogestión se le recalculan al
// terminar; al que los lleva el equipo NO se le tocan -- y está bien que no se le toquen --,
// así que prometerle un recálculo es prometerle lo que no va a pasar. Lo que gana él es que
// su entrenador trabaje con la ficha entera.
// CÓMO SE LLAMA CADA HUECO, EN CRISTIANO. Los títulos de las preguntas son preguntas
// («¿Cuánto mides?») y aquí hace falta el nombre del dato. Los mismos que usa la tarjeta de
// Inicio, para que el cliente lea lo mismo en los dos sitios.
const COMO_SE_LLAMA = {
    height: 'tu altura',
    weight: 'tu peso',
    body_fat: 'tu porcentaje de grasa',
    biotype: 'tu biotipo',
    peso_maximo: 'tu peso máximo',
    peso_mejor_momento: 'tu mejor forma',
    tiempo_intentandolo: 'cuánto llevas intentándolo',
    training_experience: 'tu experiencia entrenando',
    birthdate: 'tu fecha de nacimiento',
    goal: 'tu objetivo',
    phone: 'tu teléfono',
    sex: 'tu sexo',
    name: 'tu nombre',
};

// Y LOS PASOS QUE NO TIENEN `key` PORQUE PIDEN VARIAS COSAS A LA VEZ (12-09). La lista los
// dejaba fuera en silencio, y entonces decia «· tu altura · tu peso maximo · tu biotipo»
// con la fecha de nacimiento y el telefono delante, en el primer campo: lo que se pide y lo
// que se anuncia tienen que ser lo mismo.
const COMO_SE_LLAMA_EL_TIPO = {
    contacto: 'tus datos de contacto',
    bf: 'tu porcentaje de grasa',
    peso_hito: 'tu peso máximo',
    biotype: 'tu biotipo',
};

const nombresDeLosHuecos = (pasos = []) => [...new Set(
    pasos.map(p => COMO_SE_LLAMA[p?.key] || COMO_SE_LLAMA_EL_TIPO[p?.type]).filter(Boolean))];

// «NOS FALTA INFORMACIÓN» (Jesús, 11-09, punto 8).
//
// El título era «Nos faltan cosas tuyas» y el cuerpo se iba por las ramas explicando para
// qué sirven. Su versión dice qué pasa y va derecha a la lista: «Nos faltan algunos datos
// tuyos y los necesitamos. Concretamente: · tu altura · tu biotipo · tu peso máximo».
//
// Y la lista sale de los huecos DE VERDAD de esta persona, no de un texto escrito a mano:
// si un día se añade una pregunta, esto no se queda viejo.
const LA_PORTADA_DE_COMPLETAR = (huecos = []) => {
    const nombres = nombresDeLosHuecos(huecos);
    return {
        type: 'statement',
        title: 'Nos falta información',
        // LA LISTA VA APARTE, NO PEGADA AL PÁRRAFO (Francisco, 11-09, punto 8).
        //
        // Estaba todo en una sola frase y en pantalla se leía «· tu biotipo Con ellos tu
        // entrenador ajusta tus macros», con el último hueco y la frase siguiente sin nada
        // en medio: parecía que el biotipo era de quien ajusta los macros. Su maqueta pone
        // la lista debajo, en su renglón, y eso es lo que se pinta.
        lista: nombres,
        cta: 'Vamos',
    };
};

// LOS HUECOS, EN SU RENGLÓN. Solo la portada de «Nos falta información» trae `lista`, así
// que en las demás pantallas de título esto no pinta nada. Sin la frase de antes ni el
// cierre «con ellos...» (Francisco, 20-10: fuera los textos de ayuda): título y lista.
const ListaDeHuecos = ({ step }) => (
    step.lista?.length ? (
        <div className="max-w-md mx-auto mb-2" data-testid="huecos-que-faltan">
            <p className="text-foreground font-bold text-base mb-2">
                {step.lista.map(n => `· ${n}`).join('   ')}
            </p>
        </div>
    ) : null
);

// A nivel de módulo para que los inputs conserven el FOCO al teclear: definidos
// dentro del componente de la página se recrean en cada render (tipo nuevo para
// React = desmontar/montar el input) y el cursor se pierde con cada carácter.
// `aviso` dice QUE PASA con lo escrito, debajo del campo. No es lo mismo que el asterisco:
// el asterisco avisa de que hace falta, el aviso de que lo puesto no vale.
// ── LOS TOKENS DEL REDISEÑO (docs/diseño/design_handoff_onboarding, 18-10) ──────────────
// Solo aspecto: el botón principal, los campos y sus etiquetas, iguales en todas las
// pantallas del cuestionario. El deshabilitado no se apaga con opacidad: tiene sus colores.
const CTA = 'h-[52px] rounded-[14px] px-[26px] bg-[#ff6a1f] hover:bg-[#ff7d3a] text-[#140a04] '
    + 'text-base font-extrabold disabled:opacity-100 disabled:bg-[#3a2214] '
    + 'disabled:text-[#8a6a55] disabled:cursor-not-allowed disabled:pointer-events-auto';
const CAMPO = 'h-[52px] rounded-[12px] bg-[#161616] border border-[#2a2a2a] px-4 text-base '
    + 'text-[#f2f0ed] placeholder:text-[#5f5a55] focus-visible:ring-0 focus-visible:ring-offset-0 '
    + 'focus-visible:border-[#ff6a1f] focus:border-[#ff6a1f]';
const AREA = 'w-full rounded-[12px] bg-[#161616] border border-[#2a2a2a] px-4 py-3.5 text-base '
    + 'leading-snug text-[#f2f0ed] placeholder:text-[#5f5a55] resize-y focus:outline-none focus:border-[#ff6a1f]';
const ETIQUETA = 'block text-xs font-bold uppercase tracking-[0.1em] text-[#8a8580] mb-2';

const MiniInput = ({ k, label, type = 'text', unit, placeholder, answers, set, obligatorio, aviso }) => (
    <div>
        <label className={ETIQUETA}>
            {label}{obligatorio && <Obligatorio />}
        </label>
        {/* La unidad va dentro del campo, a la derecha. */}
        <div className="relative">
            <Input type={type} value={answers[k] ?? ''} onChange={e => set(k, e.target.value)}
                placeholder={placeholder || ''} className={`${CAMPO} ${unit ? 'pr-12' : ''}`} />
            {unit && (
                <span className="absolute right-4 top-1/2 -translate-y-1/2 font-semibold text-[#8a8580] pointer-events-none">{unit}</span>
            )}
        </div>
        {aviso && (
            <p className="text-sm text-amber-500 mt-1.5" data-testid={`aviso-${k}`}>{aviso}</p>
        )}
    </div>
);

// LA TARJETA DE UNA OPCIÓN (plantilla C del rediseño). Borde naranja al pasar por encima y
// al elegirla, con el punto de radio arriba a la derecha. Solo pinta: qué hace al pulsarla
// lo sigue decidiendo quien la usa.
const TarjetaOpcion = ({ selected, onClick, children, className = '', testId }) => (
    <button type="button" onClick={onClick} data-testid={testId}
        className={`relative w-full text-left rounded-[18px] p-5 pr-14 border-[1.5px] transition-colors text-[#f2f0ed] ${
            selected ? 'border-[#ff6a1f] bg-[#2a1b10]' : 'border-[#2a2a2a] bg-[#161616] hover:border-[#ff6a1f]'} ${className}`}>
        {children}
        <span aria-hidden="true"
            className={`absolute top-4 right-4 w-6 h-6 rounded-full border-2 ${
                selected ? 'border-[#ff6a1f] bg-[#ff6a1f]' : 'border-[#3a3a3a]'}`} />
    </button>
);

// «Muy sedentario: paso la mayor parte del día...» se lee mejor con la etiqueta corta en
// negrita y el resto debajo. Solo se parte por los dos puntos; el texto es el mismo.
// LAS OPCIONES SIN PUNTO FINAL (24-10). Se quita al pintar, en un solo sitio, para que
// ninguna lista se quede con el suyo (las del completo tampoco).
const sinPunto = (label) => String(label ?? '').replace(/\s*\.\s*$/, '');

const EtiquetaDeOpcion = ({ label: crudo }) => {
    const label = sinPunto(crudo);
    const m = String(label).match(/^([^:]{2,40}):\s+(.+)$/);
    if (!m) return <span className="text-base font-semibold leading-snug">{label}</span>;
    return (
        <span className="flex flex-col gap-1">
            <span className="text-base font-bold">{m[1]}</span>
            <span className="text-[15px] leading-snug text-[#b5b0aa]">{m[2]}</span>
        </span>
    );
};

const IconoObjetivo = ({ value }) => (value === 'volumen' ? (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
        <rect x="2" y="11" width="4" height="8" rx="1" fill="#ff6a1f" />
        <rect x="6" y="8" width="4" height="14" rx="1" fill="#ff6a1f" />
        <rect x="10" y="13.5" width="10" height="3" fill="#ff6a1f" />
        <rect x="20" y="8" width="4" height="14" rx="1" fill="#ff6a1f" />
        <rect x="24" y="11" width="4" height="8" rx="1" fill="#ff6a1f" />
    </svg>
) : (
    <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
        <path d="M15 3c2 5 7 7 7 13a7 7 0 0 1-14 0c0-3 1.5-5 3-6.5.3 2.5 1.6 3.5 2.7 3.5C13 9 15 6 15 3z" fill="#ff6a1f" />
    </svg>
));

// En las de opción única no hay botón: se elige y pasa sola.
const PistaAutomatica = () => (
    <span className="text-[13px] text-[#6f6a65]" data-testid="pista-avance-automatico">Elige una opción para continuar</span>
);

// EL STEPPER DEL PESO Y LA ALTURA (plantilla D). − y + mueven un paso fijo dentro de los
// rangos de siempre; el número se puede escribir a mano. Guarda texto, como el campo que
// sustituye, para que la validación y el envío lo lean igual que antes.
const Stepper = ({ value, onChange, onEnter, unit, paso, inicio, min, max, bloqueado }) => {
    const n = parseFloat(String(value ?? '').replace(',', '.'));
    const mover = (d) => {
        if (bloqueado) return;
        const base = isNaN(n) ? inicio - d : n;
        const v = Math.min(max, Math.max(min, base + d));
        onChange(String(Math.round(v * 10) / 10));
    };
    const boton = 'flex-none w-11 h-11 sm:w-[52px] sm:h-[52px] md:w-[60px] md:h-[60px] rounded-full bg-[#262626] hover:bg-[#303030] text-[#f2f0ed] text-[28px] leading-none disabled:opacity-40';
    return (
        <div className="flex items-center justify-center gap-2 sm:gap-3 md:gap-4 bg-[#161616] border border-[#262626] rounded-[20px] px-4 py-7" data-testid="stepper">
            <button type="button" onClick={() => mover(-paso)} disabled={bloqueado} className={boton} aria-label="Bajar">−</button>
            <div className="flex items-baseline justify-center gap-1.5 min-w-0">
                <input type="number" inputMode="decimal" value={value ?? ''} placeholder="0"
                    autoFocus={!bloqueado} disabled={bloqueado} readOnly={bloqueado}
                    onChange={e => onChange(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') onEnter(); }}
                    className="cifra-grande w-[128px] md:w-[190px] bg-transparent border-0 outline-none text-right text-[#f2f0ed] font-heading font-bold text-[68px] md:text-[92px] leading-none p-0 placeholder:text-[#5f5a55] [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none" />
                <span className="font-heading font-bold text-[32px] text-[#8a8580]">{unit}</span>
            </div>
            <button type="button" onClick={() => mover(paso)} disabled={bloqueado} className={boton} aria-label="Subir">+</button>
        </div>
    );
};
// Qué campos llevan stepper, con su paso y desde dónde arranca si está vacío.
const STEPPERS = { weight: { paso: 0.5, inicio: 70 }, height: { paso: 1, inicio: 170 } };

// ── EL BOTÓN APAGADO TIENE QUE DECIR POR QUÉ (ficha 02 del parte de Gonzalo, 4-09) ──────
//
// «Seguir» se quedaba gris y sin explicación: la única señal era el `disabled:opacity-40`,
// que en una pantalla de cinco campos no dice cuál falta ni cuántos. Se hace lo mismo que
// ya hace Solicitar un alimento desde el 27-08 (`components/nutrition/SuggestFoodModal`):
// un asterisco en los obligatorios y, debajo del botón, cuántos quedan. No es un error --
// no ha hecho nada mal todavía --, así que va en gris y sin icono de aviso.
const Obligatorio = () => <span className="text-brand ml-0.5" aria-hidden="true">*</span>;

const FaltanCampos = ({ n, testId = 'faltan-campos' }) => (n > 0 ? (
    <p className="text-[13px] text-[#8a8580] mt-3" data-testid={testId}>
        Te {n === 1 ? 'falta 1 campo' : `faltan ${n} campos`} por rellenar
    </p>
) : null);

// LO QUE NO QUIERE Y LO QUE MÁS LE GUSTA (24-10): las dos pantallas viven en
// `components/alta/PreferenciasDelAlta`.

const MiniChoice = ({ k, options, answers, set }) => (
    <div className="flex flex-wrap gap-2">
        {options.map(o => (
            <button key={o.value} onClick={() => set(k, o.value)}
                className={`px-4 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${answers[k] === o.value ? 'border-[#FF671F] bg-[#FF671F]/10 text-brand' : 'border-[#222222] text-foreground hover:border-white/30'}`}>
                {o.label}
            </button>
        ))}
    </div>
);

// Los macros que va teniendo, siempre a la vista, con lo que se mueve cada uno respecto a lo
// anterior. Es la pieza que hace que valga la pena contestar la pregunta siguiente.
const MacrosEnVivo = ({ macros, previos, calculando }) => {
    if (!macros) return null;
    const linea = (etiqueta, ahora, antes) => {
        const delta = (antes != null && ahora != null) ? ahora - antes : 0;
        return (
            <div className="flex items-baseline gap-1.5">
                <span className="text-[10px] uppercase tracking-wider text-foreground/40">{etiqueta}</span>
                {/* Con macro y unidad (punto 29 del 23-08): «TUS MACROS · ENTRENO 140»
                    era un número suelto que no decía de qué hablaba. */}
                <span className="font-heading font-extrabold text-xl text-brand tabular-nums">{ahora}<span className="text-xs font-bold text-brand/70"> g</span></span>
                {delta !== 0 && (
                    <span className={`text-[11px] font-bold ${delta > 0 ? 'text-emerald-500' : 'text-amber-500'}`}>
                        {delta > 0 ? '+' : ''}{delta}
                    </span>
                )}
            </div>
        );
    };
    const hcE = macros.entreno?.hidratos, hcD = macros.descanso?.hidratos;
    return (
        <div className={`flex items-center gap-5 flex-wrap transition-opacity ${calculando ? 'opacity-50' : ''}`}>
            <span className="text-[10px] uppercase tracking-wider text-foreground/30">Tus hidratos</span>
            {linea('Entreno', hcE, previos?.entreno?.hidratos)}
            {linea('Descanso', hcD, previos?.descanso?.hidratos)}
            {/* «Perientreno», el mismo nombre que en el resto de la app (punto 4.18). */}
            {linea('Perientreno', macros.perientreno?.hidratos, previos?.perientreno?.hidratos)}
        </div>
    );
};

// «ESCRIBIR AL EQUIPO» SE QUITA DE TODAS LAS PANTALLAS (24-10). Llevaba a Mensajes a quien
// ya pagó y no había terminado; el panel le sigue dejando entrar ahí
// (`lib/puertasDelCliente`), solo deja de ofrecerse desde aquí.

// overflow-x-hidden y no overflow-hidden: lo que sobra a los lados son los fondos difuminados
// y hay que recortarlo, pero el eje vertical tiene que poder moverse. Con overflow-hidden, en
// una pantalla baja el final de un paso largo quedaba fuera y sin forma de llegar a él.
//
// EL REDISEÑO DEL 18-10 (docs/diseño/design_handoff_onboarding). Siempre oscuro: la clase
// `dark` pone los colores de tema oscuro a todo lo de dentro, también a las pantallas que
// no tienen plantilla propia (los macros, la ficha, los menús), que así se leen sobre el
// fondo negro aunque la app esté en claro. Encima, la foto de la pantalla (`FondoFotos`).
//
// SIN «PASO X DE N» NI BARRA DE PROGRESO (18-10): la cabecera lleva el logo y nada más que
// diga cuánto queda. La foto del fondo cambia cada cuatro pantallas, en orden.
// `alCerrarSesion`: lo que hay que guardar antes de salir de la cuenta (24-10: al volver a
// entrar, sigue en la misma pantalla y con lo que ya había elegido).
const Shell = ({ giro = 0, children, tramo, cabecera, alCerrarSesion }) => (
    <div className="dark min-h-screen relative overflow-x-hidden flex flex-col text-[#f2f0ed]"
        style={{ background: '#0b0b0b', fontFamily: "'Barlow', sans-serif" }}>
        <FondoFotos pantalla={Math.max(giro - 1, 0)} />
        {/* Cabecera: logo, en qué tramo va y los macros en vivo */}
        <div className="relative z-10 flex items-center justify-between gap-4 min-h-16 px-5 md:px-7 pt-5 pb-3 flex-wrap">
            <div className="flex items-center gap-4">
                <Logo12EN12 size="sm" />
                {tramo && <span className="text-[11px] uppercase tracking-wider text-[#8a8580] font-semibold">{tramo}</span>}
            </div>
            {cabecera}
        </div>
        <div className="flex-1 flex justify-center px-6 pt-12 pb-8 relative z-10">
            <div className="w-full max-w-[600px]" data-contenido-del-cuestionario>{children}</div>
        </div>
        {/* LA SALIDA (10-10). El cuestionario va fuera del menú y sin él no se entra en la
            app: quien había entrado con otra cuenta no tenía cómo salir. Abajo y en pequeño,
            en todas sus pantallas; las respuestas ya dadas quedan guardadas. */}
        <div className="relative z-10 flex justify-center items-center gap-5 px-6 pb-16">
            <CerrarSesion antesDeSalir={alCerrarSesion} className="!text-[13px] !text-[#6f6a65] hover:!text-[#f2f0ed]" />
        </div>
    </div>
);

const QuestionnairePage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { api, refreshProfile, refreshUser, user, profile, can, token } = useAuth();
    const [idx, setIdx] = useState(0);
    const [answers, setAnswers] = useState({});
    // Espejo de las respuestas siempre al dia, para las comprobaciones que no pueden esperar al
    // siguiente render (ver `visible`).
    const answersRef = useRef({});
    const [loading, setLoading] = useState(false);
    // Resultado del motor v2 tras enviar el Nivel 0 (los 8 números + desglose).
    const [resultado, setResultado] = useState(null);
    // «LA PREGUNTA DEL ALTA» (24-09) SALIÓ DEL ALTA el 26-09 (César: «la recargaba»): ahora
    // sale al compartir la primera comida o al entrar en el ranking.
    // Como se le entrega el resultado: aplicado solo, o propuesta que revisa su coach (seccion 6).
    const [entrega, setEntrega] = useState(null);
    // El Nivel 0 se completó EN ESTA SESIÓN: seguimos en el flujo aunque el
    // perfil ya diga questionnaire_completed (para ver resultados y el Nivel 1).
    const [nivel0Enviado, setNivel0Enviado] = useState(false);
    // El día de hoy montado y guardado en cuanto salen sus macros: termina el alta y ya tiene
    // comida puesta, en vez de unos números y una pantalla vacía por delante.
    const [diaMontado, setDiaMontado] = useState(null);
    // El día de hoy tal como quedó guardado, para quien recarga en la pantalla de sus macros.
    const [diaGuardado, setDiaGuardado] = useState(null);
    // La pantalla de los macros y la de sus menús de hoy son la misma parada del recorrido.
    const [viendoMenus, setViendoMenus] = useState(false);
    // Las fotos que lleva subidas hoy en «Ahora tus fotos» (04-11): «Continuar» se enciende
    // con la primera. Lo cuenta `TresFotos` (`alCambiarHechas`).
    const [fotosAltaHoy, setFotosAltaHoy] = useState(0);
    // Los menús de arranque (doc 23-08, punto 18): mañana y pasado, escritos de verdad.
    // null = cargando; [] = no se pudieron montar.
    const [diasArranque, setDiasArranque] = useState(null);
    // A dónde se va tras enviar el completo; mientras tiene valor, la espera manda sobre todo.
    const [saliendoA, setSaliendoA] = useState(null);
    // Macros recalculados a cada respuesta, para verlos moverse. No se aplican: son un avance.
    const [vistaPrevia, setVistaPrevia] = useState(null);
    const [calculandoVivo, setCalculandoVivo] = useState(false);
    const progresoCargadoRef = useRef(false);
    // EL «ATRÁS» QUE FUNCIONABA UNA VEZ Y DEJABA DE FUNCIONAR (punto 4.12 del 09-08).
    //
    // Al elegir una opción se programa el avance con `setTimeout(goNext, 550)` -- esos 550 ms
    // son a propósito: dan tiempo a ver moverse los macros de la cabecera. Si dentro de ese
    // medio segundo largo el cliente pulsa Atrás, se retrocede y acto seguido el temporizador,
    // que seguía vivo, avanza otra vez. Desde fuera es exactamente lo que describe el punto:
    // el botón deja de responder, y de forma intermitente, según lo rápido que vayas.
    //
    // Retroceder cancela el avance pendiente: el último gesto del cliente manda sobre
    // cualquier cosa que estuviera programada.
    const avancePendienteRef = useRef(null);
    // «Toca encima» del repaso (doc 23-08, punto 16): guarda a qué pantalla volver después
    // de corregir una respuesta desde «Confirma tus respuestas». null = recorrido normal.
    const volverAlRepasoRef = useRef(null);
    // «Fue en otro momento» en la pantalla del máximo peso (rediseño «Tu historia», 04-11):
    // la opción elegida antes de escribir nada. Con algo escrito ya se sabe sola.
    const [hitoEnOtroMomento, setHitoEnOtroMomento] = useState(false);
    // Los macros de antes de la ultima respuesta, para poder mostrar cuanto se ha movido cada uno.
    const previosRef = useRef(null);
    // Punto de partida: fotos subidas y la ficha que se le entrega a cambio.
    const [ficha, setFicha] = useState(null);
    // P10: lo que hemos entendido de su dieta, pendiente de que lo confirme.
    const [lecturaDieta, setLecturaDieta] = useState(null);
    const [leyendoDieta, setLeyendoDieta] = useState(false);
    // Las medidas del encadenado del final del alta (doc 19-08, apartado 06).
    const [medidasAlta, setMedidasAlta] = useState({});
    // El «Ver más» de la explicación de una pregunta (`descMas`): el índice de la pantalla
    // que lo tiene abierto. Vive aquí y no en `Title`, que se vuelve a montar en cada render.
    const [verMasEn, setVerMasEn] = useState(null);
    // La pantalla de los preferidos (24-10): si lo marcado llega a los 20 g (true/false), o
    // null mientras se comprueba.
    const [preferidosCuadran, setPreferidosCuadran] = useState(null);
    // Lo que eligió en esas dos pantallas, para la de Preferencias del final del que no
    // lleva entrenador. De la FICHA, que a esas alturas ya tiene el alta guardada y los
    // vetos que salen de sus intolerancias fundidos (la lactosa total quita los lácteos);
    // de las respuestas si la ficha aún no los trae. Memorizado: esa pantalla se reinicia
    // cada vez que le llega una lista nueva, y una recién hecha en cada render no le
    // dejaría marcar nada.
    const prefsDelAlta = useMemo(() => {
        const deLaFicha = (k) => (Array.isArray(profile?.[k]) && profile[k].length ? profile[k] : null);
        return {
            preferencias: deLaFicha('food_preferences')
                || (Array.isArray(answers.food_preferences) ? preferidasDePartida(answers) : []),
            categorias: deLaFicha('avoided_categories') || answers.avoided_categories || [],
            palabras: deLaFicha('avoided_keywords') || answers.avoided_keywords || [],
            sueltos: deLaFicha('preferred_foods') || answers.preferred_foods || [],
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile?.food_preferences, profile?.avoided_categories, profile?.avoided_keywords, profile?.preferred_foods,
        answers.food_preferences, answers.avoided_categories, answers.avoided_keywords, answers.preferred_foods]);

    // Nivel 1 solo para planes con coach (calculadora == 'personalizado')...
    const conEntrenador = can(CAP.MACROS_PERSONALIZADOS);
    // ...Y PARA QUIEN COMPRA EL AJUSTE A MEDIDA. Es la última línea de la regla que ordena
    // el documento del cuestionario: «el que compra el ajuste de 87 € hace el completo,
    // exactamente igual que un Gold». Su plan no cambia -- sigue siendo el suyo -- pero el
    // cuestionario largo se le abre igual, porque es lo que ha pagado.
    //
    // Se mira `cobrado`, no `quiere`: querer no es haber pagado. Hoy nadie lo tiene a true
    // porque todavía no hay cobro montado (ver POST /clients/ajuste-a-medida), así que esto
    // no se lo abre a nadie por su cuenta; queda puesto para el día que se cobre.
    const tieneCoach = conEntrenador || !!profile?.ajuste_a_medida?.cobrado;

    // AQUI ARRIBA, Y NO ABAJO DEL TODO (13-09). `visible` vivia DESPUES de los tres cortes
    // de esta pantalla, y el efecto que retoma el borrador lo llama. Si la pantalla salia
    // por uno de esos cortes -- por ejemplo el del cuestionario ya hecho -- la linea nunca
    // se ejecutaba, pero el efecto si, y la pantalla se caia entera: «Cannot access
    // 'visible' before initialization», negro con el cartel rojo de React. Le pasaba a
    // quien tenia el cuestionario hecho y un borrador a medias guardado.
    // Lo que YA TIENE, para no pedírselo otra vez. Las medidas se marcan en el perfil y las
    // fotos se cuentan en su ficha; sin mirar las dos, a quien ya las subió se le seguía
    // diciendo «sube tus fotos y toma tus medidas», que es pedirle lo que acaba de dar.
    const yaTieneMedidas = !!(profile?.punto_de_partida_hecho || profile?.medidas_inicio);
    const yaTieneFotos = (ficha?.fotos_subidas || 0) > 0;
    // LO QUE YA ESTABA CONTESTADO ANTES DEL LARGO (25-10): las del básico que el largo no
    // repite. Sale de su ficha (lo siembra el efecto del perfil), no de lo que va
    // contestando, para que una pantalla del largo no desaparezca al responderla.
    const yaContestadasRef = useRef(new Set());
    // Y si ya tenía las medidas al entrar, mirado una vez (ver `visible`).
    const medidasAlEntrarRef = useRef(null);
    if (profile && medidasAlEntrarRef.current === null) medidasAlEntrarRef.current = yaTieneMedidas;

    const visible = (s) => {
        // Ni el encadenado del 19-08 ni el completo piden lo que ya está dado.
        if (s.type === 'fotos_alta' && yaTieneFotos) return false;
        if (s.type === 'medidas_alta' && yaTieneMedidas) return false;
        // Las medidas del largo, si ya las tenía al entrar (25-10). Se mira una vez: si se
        // mirara la ficha en vivo, al guardarlas la pantalla desaparecería debajo de él.
        if (s.type === 'partida' && medidasAlEntrarRef.current) return false;
        // Lo que ya contestó en el básico no se repite en el largo (ver `yaContestadasRef`).
        if (s.yaEnElBasico && yaContestadasRef.current.has(s.key)) return false;
        // Sin entrenador, las medidas del día 1 se piden en el encadenado («2 · Tomar las
        // medidas»), no aquí: con las dos pantallas se le pedían las mismas diez medidas
        // dos veces seguidas, y la segunda ni salía porque la primera ya la marcaba hecha.
        if (s.type === 'partida' && !tieneCoach) return false;
        return !s.cond || s.cond(answersRef.current);
    };

    // CUÁNDO EMPIEZA. Todo el mundo arranca en lunes, pague el día que pague, y ese lunes se
    // calcula y se guarda al cobrar (`current_period_start`, el que ancla su ciclo). Lo que
    // faltaba era decírselo. Y solo eso: el día de sus macros definitivos NO se le dice
    // (punto 46 del doc del 19-08), porque los recibe el miércoles anterior a su lunes y
    // cualquier fecha que le demos aquí le sonaría a que empieza antes de tenerlos.
    //
    // EL LUNES LO DICE EL SERVIDOR (`empieza_el`, de `core/calendario_arranque.cuando_empieza`),
    // que es el mismo de su ficha y de su reloj (10-10). Aquí había una copia de la regla,
    // con el reloj del navegador y las 48 horas de antes: le decía «lunes 12» a quien su
    // ficha y su reloj daban el 19. Si ya empezó no llega fecha, y se dice la frase sin ella.
    const arranque = useMemo(() => {
        const lunes = pintarDia(profile?.empieza_el, { day: 'numeric', month: 'long' });
        return lunes ? { lunes } : null;
    }, [profile?.empieza_el]);
    // Si ha pulsado "Ajustar macros" manda eso y nada más: sin esta comprobación, un cliente con
    // coach que le diera al botón acababa en el perfil largo en vez de en el cuestionario.
    const pidioAjustar = new URLSearchParams(location.search).get('ajustar') === '1';
    // Modo revisión (solo equipo): `?ver=alta`, `?ver=ajuste` o `?ver=perfil` abren el tramo
    // que se pida aunque el cuestionario ya esté hecho. Sin esto no hay forma de mirar el
    // alta una vez pasada, y es justo la que más textos tiene.
    const revision = verComo(user);
    const pidioCompletar = new URLSearchParams(location.search).get('completar') === '1';
    // LA VUELTA DE STRIPE DEL AJUSTE COMPRADO EN EL ALTA (26-10). Quien paga en la pantalla de
    // sus primeros macros vuelve aquí (`?pago_ajuste=1&session_id=...`). Se confirma el pago
    // -- en local no hay webhook, y en producción no se depende solo de él --, se refresca la
    // ficha y, con `ajuste_a_medida.cobrado`, el recorrido es el cuestionario largo
    // (`retomandoNivel1`), que empieza por «Tu salud». Si el pago no ha entrado, a la app.
    // Se lee UNA VEZ: al terminar se limpia la dirección.
    // Con el pago dentro, antes del cuestionario largo, la confirmación (29-10: «al pagar el
    // ajuste a medida no sale ninguna pantalla de confirmación»): `pagoConfirmado`.
    const vueltaDelPagoRef = useRef(undefined);
    if (vueltaDelPagoRef.current === undefined) {
        const q = new URLSearchParams(location.search);
        vueltaDelPagoRef.current = q.get('pago_ajuste') === '1' ? (q.get('session_id') || '') : null;
    }
    const [confirmandoPago, setConfirmandoPago] = useState(vueltaDelPagoRef.current !== null);
    const [pagoRevisado, setPagoRevisado] = useState(false);
    const [pagoConfirmado, setPagoConfirmado] = useState(false);
    useEffect(() => {
        const sesion = vueltaDelPagoRef.current;
        if (sesion === null) return;
        (async () => {
            try {
                if (sesion) await api.post('/billing/checkout-session/sync', { session_id: sesion });
            } catch (e) {
                console.error('[alta] no se pudo confirmar el pago del ajuste', e);
            }
            try { await refreshProfile(); } catch (e) { /* se decide con la ficha que haya */ }
            navigate(location.pathname, { replace: true });
            setConfirmandoPago(false);
            setPagoRevisado(true);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(() => {
        if (!pagoRevisado) return;
        // Sin el pago, `pagoRevisado` se queda puesto hasta salir: quitado, el borrador del
        // largo se retomaba (con su aviso) en el último render antes de ir a Inicio.
        if (!profile?.ajuste_a_medida?.cobrado) {
            toast.info('Lo tenemos apuntado. Te escribimos para cerrarlo.');
            navigate('/dashboard');
            return;
        }
        setPagoRevisado(false);
        setPagoConfirmado(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagoRevisado]);
    // EL CIERRE DEL ALTA A MEDIAS (ALT-06, 14-10; Francisco: «que no lo haga»). El que
    // recargó o salió con sus macros en pantalla tiene el alta enviada, pero no ha pasado lo
    // de después (medidas, ficha, menús de muestra y, según el plan, elegir o la oferta).
    // Vuelve a eso, por el tipo de pantalla que se apuntó en su ficha (`lib/cierreDelAlta`).
    // Se mira UNA VEZ, con la primera ficha: la que se refresca a mitad del cierre trae la
    // marca que esta misma sesión va escribiendo, y eso no es estar retomando.
    const cierreARetomarRef = useRef(undefined);
    if (profile && cierreARetomarRef.current === undefined) {
        cierreARetomarRef.current = cierreDelAltaPendiente(profile);
    }
    const retomandoCierre = !revision && !pidioAjustar && !pidioCompletar && !!cierreARetomarRef.current;
    // En el cierre, en esta sesión (acaba de enviar) o retomándolo: para las puertas de abajo
    // es lo mismo.
    const enElCierre = nivel0Enviado || retomandoCierre;
    // Retomar: Nivel 0 hecho en otra sesión pero Nivel 1 pendiente.
    //
    // EL QUE VIENE A COMPLETAR SUS DATOS NO ESTÁ RETOMANDO EL LARGO (26-08). La puerta del
    // cuestionario largo (la de «se abre el viernes a las 10:00», que se quitó el 20-10) colgaba de aquí, y el
    // comentario de esa puerta ya decía que «el completar-huecos pasa siempre»... pero la
    // condición solo dejaba fuera al que venía a ajustar. Resultado: el aviso de macros
    // provisionales te mandaba a arreglar tu edad y te encontrabas un «vuelve el viernes»,
    // con el aviso todavía puesto. Es el mismo callejón que el de más abajo, por otra puerta.
    const retomandoNivel1 = revision
        ? revision === 'perfil'
        : (!pidioAjustar && !pidioCompletar && !!profile?.questionnaire_completed && !enElCierre
           && tieneCoach && !profile?.questionnaire_nivel1_completed);

    // Dos modos, como pide el doc del 29-07:
    //   ALTA   -> cuatro preguntas y macros provisionales. Es lo que ve quien acaba de entrar.
    //   AJUSTE -> el cuestionario que ajusta, detras del boton "Ajustar macros". Se llega con
    //             ?ajustar=1, o solo con el alta ya hecha (por si vuelve por el enlace).
    const modoAjuste = revision
        ? revision === 'ajuste'
        : (pidioAjustar
           || (!!profile?.questionnaire_completed && !enElCierre && !retomandoNivel1));

    // Un solo recorrido (punto 15 del doc del 07-08). Quien se da de alta contesta los datos
    // de la tabla y sigue de largo con lo que ajusta los hidratos, sin cortes y sin un segundo
    // cuestionario: por eso el alta es el mismo flujo que el ajuste con las cuatro preguntas
    // de partida delante. Quien vuelve más adelante por el botón "Ajustar macros" ya tiene
    // esos cuatro datos en su ficha, así que entra directo por el tramo de ajuste.
    // EL FINAL SE PARTE POR PLAN, no por momento (doc del cuestionario, 18-08: «la regla que
    // ordena todo»). Hasta aquí llegan todos igual, con sus macros y su primer día montado.
    // A partir de aquí: quien lleva entrenador elige entre empezar ya o terminar su perfil;
    // quien no lo lleva elige en la misma pantalla de los macros entre quedarse con su plan
    // o pagar el ajuste a medida (26-10).
    // CON ENTRENADOR, DE LOS MACROS AL LARGO SIN NADA EN MEDIO (25-10). La pantalla de los
    // macros ofrece «Probar la aplicación» (sale a la app; el Inicio le recuerda el resto) o
    // «Continuar con el cuestionario», que sigue por la salud. Las medidas («Apunta tus
    // medidas») se van al final del largo, detrás de las máquinas; los dos menús de muestra
    // (`magia`) no se le enseñan.
    //
    // SIN ENTRENADOR, LOS MACROS SON LA ÚLTIMA PANTALLA DEL ALTA (26-10). Detrás iban las
    // medidas, los dos menús de muestra, «Tres cosas y terminamos» (fotos, medidas,
    // preferencias), «Ya está todo» y la oferta de los 87 €. La oferta sube a la pantalla de
    // sus primeros macros, con dos salidas: «Me vale con la primera opción» entra en la app
    // y «Quiero el ajuste a medida» va a Stripe; si paga, sigue por el cuestionario largo
    // (`tieneCoach` pasa a true con `ajuste_a_medida.cobrado`). El resto se quita.
    const elCierre = tieneCoach
        ? [...STEPS_ONBOARD.filter(s => s.type !== 'partida' && s.type !== 'magia'), ...STEPS_NIVEL1]
        : [];
    // LO QUE LE FALTA DE LA BASE, PREGUNTADO ANTES DE AJUSTAR NADA.
    //
    // El ajuste lee del perfil los cuatro datos de la tabla (peso, sexo, grasa y objetivo) y
    // no los pregunta, porque se dan en el alta. Pero hay 124 clientes activos a los que les
    // falta alguno -- 104 sin objetivo, 63 sin grasa, 8 sin peso, casi todos de la migración
    // de Calma -- y esos contestaban el cuestionario entero para estrellarse en el botón de
    // calcular con «Faltan tus datos de partida. Completa el alta primero». El alta no se
    // puede repetir (contesta 409), así que era un callejón sin salida: no había forma de
    // arreglarlo desde la app.
    //
    // Ahora, si falta alguno, se le pregunta AQUÍ, delante de todo, y se guarda con la misma
    // puerta que usa «nos faltan cosas tuyas»: rellena huecos y no pisa nada.
    // SE MIRA UNA VEZ Y NO SE VUELVE A MIRAR. Lo que le falta decide qué pantallas tiene su
    // recorrido, y al terminar se guarda y se refresca su ficha: si esto se recalculara ahí,
    // el recorrido cambiaría DEBAJO del cliente en el último clic y le devolvía a la primera
    // pantalla en vez de enseñarle sus macros.
    const loQueLeFaltabaRef = useRef(null);
    if (profile && !loQueLeFaltabaRef.current) {
        loQueLeFaltabaRef.current = ['goal', 'weight', 'body_fat'].filter(
            k => profile[k] === undefined || profile[k] === null || profile[k] === '');
    }
    const faltaLaBase = loQueLeFaltabaRef.current || [];

    /**
     * LO QUE ESTÁ PUESTO PERO NO PUEDE SER (26-08). Y esto era un callejón sin salida.
     *
     * El aviso de «macros provisionales» sale de `datos_dudosos`, que marca dos cosas: lo
     * que FALTA y lo que es IMPOSIBLE (edad 0, estatura de 1 cm, los datos que entraron por
     * los scripts de importación sin pasar por la validación de la API). Pero el recorrido
     * de «Completar mis datos» solo mira lo que falta: `falta()` pregunta cuando el campo
     * está vacío, y un valor imposible NO está vacío.
     *
     * Resultado, en producción y con la cuenta de Francisco: el aviso dice «revisa tu edad»,
     * pulsas «Completar mis datos», el cuestionario no encuentra nada que preguntar y salta
     * directo a «y ya estaría, estos son tus macros iniciales»; entras, y el aviso sigue ahí.
     * Otra vez, y otra. El aviso te mandaba a un sitio que no podía arreglar lo que denuncia.
     *
     * Son 3 clientes en el bucle y 10 con algún valor imposible. Ahora esos campos también
     * abren su pantalla, con el valor malo delante para que se vea qué hay que corregir.
     */
    const loQueEstaMalRef = useRef(null);
    if (profile && !loQueEstaMalRef.current) {
        loQueEstaMalRef.current = (profile.datos_dudosos || [])
            .filter(d => d.motivo === 'imposible')
            .map(d => d.campo);
    }
    const estaMal = loQueEstaMalRef.current || [];

    const laBaseQueFalta = useMemo(() => {
        const pantallas = [];
        const hayQuePreguntar = (campo) => faltaLaBase.includes(campo) || estaMal.includes(campo);
        // El sexo antes que el objetivo: de él salen las opciones del objetivo.
        if (estaMal.includes('sex')) pantallas.push(q('sex'));
        if (hayQuePreguntar('goal')) pantallas.push(q('goal'), q('_confirm'));
        if (hayQuePreguntar('weight')) pantallas.push(q('weight'));
        if (hayQuePreguntar('body_fat')) pantallas.push(porTipo('bf'));
        // Estos tres solo llegan por «imposible»: si faltaran, el básico ya los pide.
        if (estaMal.includes('height')) pantallas.push(q('height'));
        // La edad se corrige donde se pregunta, que es la fecha de nacimiento.
        //
        // Y TAMBIEN POR LA FECHA, NO SOLO POR LA EDAD (Jesus, 9-09). `estaMal` mira `age`,
        // que es un campo aparte y puede estar vacio mientras la fecha guardada es
        // imposible: entonces no salia esta pantalla, la fecha viajaba tal cual y el
        // servidor la rechazaba sin que hubiera donde corregirla. Se mira la fecha, que es
        // lo que se manda. Solo si HAY una guardada y no puede ser: al que no tiene ninguna
        // no se le abre una pantalla que nadie le ha pedido.
        const laFechaNoPuedeSer = !!profile?.birthdate
            && !fechaDeNacimientoUsable(profile.birthdate);
        if (estaMal.includes('age') || laFechaNoPuedeSer) {
            pantallas.push({ type: 'contacto', title: 'Revisa tus datos' });
        }
        return pantallas;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [faltaLaBase.join(','), estaMal.join(','), profile?.birthdate]);

    const preguntasDeAjuste = [...laBaseQueFalta, ...STEPS_AJUSTE, ...elCierre];

    // EL BÁSICO PARA EL QUE YA ESTABA (?completar=1). Solo lo que falte, con la portada que
    // dice por qué se le pregunta y terminando en sus macros nuevos, que es lo que gana.
    // Se calcula una vez con las respuestas ya sembradas del perfil: si se recalculara en
    // cada tecla, la pantalla en la que está escribiendo desaparecería al contestarla.
    // (`pidioCompletar` se declara arriba, junto a `retomandoNivel1`, que también lo mira.)

    // LAS PANTALLAS DEL BÁSICO QUE ESTE CLIENTE NO HA CONTESTADO NUNCA. Se calcula una vez,
    // con las respuestas ya sembradas del perfil: si se recalculara en cada tecla, la
    // pantalla en la que está escribiendo desaparecería al contestarla.
    // Igual que arriba: se decide una vez, con la ficha tal y como estaba al entrar.
    const delBasicoRef = useRef(null);
    // LAS PREFERENCIAS DEL DÍA NO VIVEN EN EL PERFIL (César, Trello 21-09: «la segunda
    // fase vuelve a preguntar lo que en la primera hay que responder sí o sí»). Cuántas
    // comidas, cuándo entrena y qué peri se guardan en la configuración de la dieta
    // (`/user/diet-config`), no en la ficha, así que aquí `falta()` las veía vacías y las
    // volvía a preguntar. Se leen de donde están antes de decidir qué falta.
    const [dietConfig, setDietConfig] = useState(undefined);
    // Hasta colocar el borrador del largo no se enseña ninguna pregunta (31-10): salía la
    // primera y a los segundos saltaba a donde iba.
    const [borradorColocado, setBorradorColocado] = useState(false);
    useEffect(() => {
        if (!profile) return;
        api.get('/user/diet-config')
            .then(r => setDietConfig(r.data || {}))
            .catch(() => setDietConfig({}));
    }, [profile?.id]);
    const loQueFaltaDelBasico = useMemo(() => {
        if (!profile || dietConfig === undefined) return [];
        if (delBasicoRef.current) return delBasicoRef.current;
        // CON LA FICHA, NO CON EL ESPEJO DE RESPUESTAS (23-08). Los efectos que siembran
        // `answersRef` desde el perfil corren DESPUÉS de este cálculo, así que aquí el
        // espejo llegaba vacío y TODO parecía sin contestar: al que volvía a por el
        // completo (el de los 87 €, un Gold que lo dejó a medias) se le volvía a abrir el
        // básico entero por «Antes de empezar». Lo tapaba el borrador del alta, que
        // saltaba por encima con su número de paso... aterrizándole a mitad del completo.
        // Se mira lo mismo que siembra el efecto: el perfil plano y sus ajustes de macros.
        const a = { ...(profile.ajustes_macros || {}), ...profile,
                    phone: profile.phone || user?.phone,
                    pref_num_comidas: profile.pref_num_comidas ?? dietConfig.num_comidas,
                    pref_momento: profile.pref_momento ?? dietConfig.momento_entreno,
                    pref_perientreno: profile.pref_perientreno ?? dietConfig.opcion_peri };
        // Sin las de la base: esas van por su cuenta, delante de todo, y preguntarle el
        // objetivo dos veces seguidas es lo que hace que cierre la pestaña.
        //
        // Por CLAVE y por TIPO. Las pantallas compuestas -- «Antes de empezar», que pide la
        // fecha de nacimiento -- no tienen clave, así que mirando solo la clave se colaban
        // dos veces desde que la base también las pone cuando la edad guardada es imposible.
        const yaVanDelante = new Set(laBaseQueFalta.map(p => p.key).filter(Boolean));
        const tiposDelante = new Set(laBaseQueFalta.filter(p => !p.key).map(p => p.type));
        const huecos = EL_BASICO.filter(
            p => falta(p, a) && !yaVanDelante.has(p.key) && !(!p.key && tiposDelante.has(p.type)));
        // Y SOLO LAS QUE LE TOCABAN (27-10). Una pregunta con condición que no se le hizo
        // queda vacía y no es un hueco: a quien conoce a Jesús desde hace menos de una semana
        // no se le pregunta «¿Por qué ahora y no antes?», y el largo se la ponía la primera,
        // delante de «Tu salud» (la primera pantalla no pasa por `visible`). Se quita solo
        // si no hay nada delante que pueda cambiar su condición: con otro hueco antes («¿haces
        // otro deporte?» antes de «¿cuál?»), la decide `visible` al llegar, con lo contestado.
        let hayAlgoDelante = laBaseQueFalta.length > 0;
        delBasicoRef.current = huecos.filter(p => {
            if (p.cond && !hayAlgoDelante && !p.cond(a)) return false;
            hayAlgoDelante = true;
            return true;
        });
        return delBasicoRef.current;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [profile, laBaseQueFalta, dietConfig]);

    const elBasicoQueFalta = useMemo(() => {
        if (!pidioCompletar || !profile) return null;
        if (!laBaseQueFalta.length && !loQueFaltaDelBasico.length) return [];
        // CON LAS DE LA BASE DELANTE. `loQueFaltaDelBasico` las deja fuera a propósito -- para
        // no preguntar el objetivo dos veces cuando van por su cuenta -- y aquí no iba nadie a
        // ponerlas: al cliente de Calma sin objetivo no se le preguntaba, y al calcular se
        // estrellaba con «Revisa tu objetivo: no hemos podido guardarlo así», que es el
        // servidor diciendo que le llegó vacío.
        // «Y QUE SE PUEDAN RELLENAR AHÍ MISMO» (Francisco, 11-09, punto 8). La portada era
        // una pantalla que solo informaba: le decías a alguien que le faltan tres datos y
        // le hacías pulsar «Empezar» para que apareciera el primero. Ahora lo que falta va
        // ENCIMA del primer hueco, en una banda, y el campo está delante desde el primer
        // segundo. La pantalla que solo informa se cae.
        const huecos = [...laBaseQueFalta, ...loQueFaltaDelBasico];
        const portada = LA_PORTADA_DE_COMPLETAR(huecos);
        const [primero, ...resto] = huecos;
        return [{ ...primero, loQueFalta: portada }, ...resto,
                porTipo('final0'), porTipo('result')];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pidioCompletar, profile, laBaseQueFalta, loQueFaltaDelBasico, conEntrenador]);

    // Y EL COMPLETO EMPIEZA POR AHÍ CUANDO HACE FALTA (18-08).
    //
    // El cuestionario largo da por hecho que el básico está contestado: son las 21 pantallas
    // que NO se repiten. Pero el cliente que viene de Calma nunca pasó por el básico -- de
    // los 140 con acceso vivo, a la mayoría le faltan las cinco cosas -- y a ese la app le
    // metía directo en el largo. Salía de él con su ficha igual de coja: sin objetivo, sin
    // biotipo, sin sus pesos y sin saber qué come. Y sin macros nuevos, porque el largo no
    // los toca.
    //
    // Así que delante van lo que le falte de la base y lo que le falte del básico. Al que
    // hizo el alta aquí no le cambia nada: no le falta ninguna y esto queda en cero.
    const flow = elBasicoQueFalta?.length
        ? elBasicoQueFalta
        : retomandoNivel1
            ? [...laBaseQueFalta, ...loQueFaltaDelBasico, ...STEPS_NIVEL1]
            : modoAjuste
                ? preguntasDeAjuste
                : [...EL_BASICO, ...elCierre];
    // ¿Esa pantalla es ya del cuestionario largo, la segunda parte? Al volver a por él, todo;
    // en la misma sesión del alta, lo que va pasado el cierre.
    const estaEnElLargo = (paso) => retomandoNivel1
        || (enElCierre && tramoDelCierre(flow.map(s => s.type), paso) === 'despues');

    // PreferencesSetup espera el helper estilo fetch (endpoint, {method, body}).
    const fetchApi = useCallback(async (endpoint, options = {}) => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
            ...options,
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
                ...options.headers,
            },
        });
        if (!res.ok) {
            const error = await res.json().catch(() => ({ detail: 'Error de red' }));
            throw new Error(error.detail || 'Error');
        }
        return res.json();
    }, [token]);

    // CÓMO QUIERE SU DÍA, tal como lo contestó: cuántas comidas, cuándo entrena y qué
    // perientreno. Solo lo contestado: lo que falte lo resuelve el servidor con su ficha
    // (`core/comidas_del_dia`), no esta pantalla poniendo cuatro comidas por su cuenta.
    const configDelDia = () => {
        const a = answersRef.current;
        const cfg = {};
        if (a.pref_num_comidas != null) cfg.num_comidas = a.pref_num_comidas;
        // En bloque único el momento no se pregunta (24-10) y va el que era su única opción,
        // «En ayunas» (0). Uno que quedara de antes de cambiar a bloque único no vale.
        if (a.pref_num_comidas === 1) cfg.momento_entreno = 0;
        else if (a.pref_momento != null) cfg.momento_entreno = a.pref_momento;
        // Y el perientreno, que se pregunta desde el 8-09: Nutrición lo lee de la ficha
        // (`GET /user/diet-config`), así que con guardarlo ya sale bien la primera vez.
        if (a.pref_perientreno) cfg.opcion_peri = a.pref_perientreno;
        return cfg;
    };
    // Y a su ficha, que es de donde lo leen Nutrición, el día que se monta solo y el panel.
    // No bloquea nada si falla: el cliente lo puede cambiar después en Nutrición.
    const guardarConfigDelDia = async () => {
        const cfg = configDelDia();
        if (!Object.keys(cfg).length) return;
        try { await api.patch('/user/diet-config', cfg); }
        catch (e) { console.error('[alta] no se guardó cómo quiere su día', e); }
    };

    // Al llegar a "Tus alimentos": persistir la config elegida (comidas, momento
    // del entreno, días) para que el banco y Nutrición ya la usen. El envío del
    // cuestionario ya la guarda antes de montar el primer día (10-10); esto queda para
    // quien llega a estas pantallas sin pasar por el envío (retoma el alta a medias).
    const prefsPersistedRef = useRef(false);
    useEffect(() => {
        const s = flow[idx];
        if (!s || prefsPersistedRef.current || (s.type !== 'prefs' && s.type !== 'magia')) return;
        prefsPersistedRef.current = true;
        guardarConfigDelDia();
        // Los días de entreno ya no se preguntan aquí (bloque 5 del doc del 18-08: son cuatro
        // siempre y la ficha nace con cuatro), así que no hay nada que persistir.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx]);

    // La ficha se pide ANTES DE PEDIRLE FOTOS Y MEDIDAS: de ahí sale cuántas fotos tiene
    // subidas, que es lo que decide si esa pantalla le sobra o le falta media. (Su pantalla,
    // «Tu punto de partida», se quitó el 20-10.)
    useEffect(() => {
        if (!['fotos_alta', 'medidas_alta'].includes(flow[idx]?.type) || ficha) return;
        api.get('/clients/mi-ficha')
            .then(r => setFicha(r.data))
            .catch(() => setFicha({ composicion: null, referencia: null }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx]);

    // «PERFIL COMPLETO.» YA NO SE ENSEÑA (20-10, prompt del rediseño, punto 33): al llegar
    // a su pantalla se envía solo, como si hubiera pulsado «Enviar». Si el envío falla, la
    // pantalla lo dice y deja reintentar (`envioDelCompleto`). En revisión no se envía nada.
    // El envío llega por una referencia: se define más abajo, detrás de las pantallas que
    // cortan el render antes, y en esos renders no existe.
    const [envioDelCompleto, setEnvioDelCompleto] = useState(null);   // null | 'enviando' | 'fallo'
    const enviarCompletoRef = useRef(null);
    useEffect(() => {
        const enviar = enviarCompletoRef.current;
        if (revision || flow[idx]?.type !== 'final1' || envioDelCompleto || !enviar) return;
        setEnvioDelCompleto('enviando');
        enviar().then(ok => { if (!ok) setEnvioDelCompleto('fallo'); });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, revision]);

    // LOS DOS MENÚS DE ARRANQUE (doc del 23-08, punto 18). Antes eran tres recetas
    // sueltas cuadradas a su C1; ahora son los días de MAÑANA y PASADO enteros, escritos
    // de verdad (`montar-dia` con fecha y guardar): «para que los tengas de referencia».
    // El de hoy ya lo escribe el envío del cuestionario, como siempre. El tipo de día
    // sale de sus días de entreno; sin ellos, entrenamiento, que es el conservador.
    useEffect(() => {
        const s = flow[idx];
        if (s?.type !== 'magia' || diasArranque !== null) return;
        const DIAS_JS = ['domingo', 'lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado'];
        const fechaEn = (mas) => { const d = ahoraDeLaApp(); d.setDate(d.getDate() + mas); return d; };
        const iso = (mas) => fechaEn(mas).toLocaleDateString('en-CA');
        const tipoDe = (mas) => {
            const dias = answersRef.current.training_weekdays;
            if (!Array.isArray(dias) || !dias.length) return 'entrenamiento';
            return dias.includes(DIAS_JS[fechaEn(mas).getDay()]) ? 'entrenamiento' : 'descanso';
        };
        // SOLO LOS QUE TRAEN COMIDA (10-10). En bloque único el recetario no tiene
        // menús que cuadren con el día entero en una sola comida, y el día vuelve
        // montado pero vacío: se pintaban dos tarjetas «Menú 1» y «Menú 2» sin nada
        // dentro. Sin comida no es un menú de muestra: sale la frase del recetario.
        const conComida = (d) => Object.values(d?.comidas || {})
            .some(c => (c.alimentos || []).length);
        // AL RETOMAR EL CIERRE (ALT-06), EL DÍA QUE YA ESTÁ PUESTO SE ENSEÑA, NO SE PISA: o se
        // montó antes de recargar, o lo ha tocado él en Nutrición entre medias.
        const yaPuesto = (mas) => (retomandoCierre
            ? api.get(`/diets/${iso(mas)}`).then(r => (r.data?.exists && conComida(r.data) ? r.data : null))
                .catch(() => null)
            : Promise.resolve(null));
        (async () => {
            try {
                const montados = await Promise.all([1, 2].map(async mas => {
                    const puesto = await yaPuesto(mas);
                    if (puesto) return { ...puesto, tipo_dia: puesto.tipo_dia || tipoDe(mas) };
                    return api.post('/calculator/montar-dia', {
                        fecha: iso(mas), guardar: true, tipo_dia: tipoDe(mas),
                        // Con lo que contestó (comidas, momento y perientreno). Lo que no
                        // haya contestado lo pone el servidor desde su ficha.
                        ...configDelDia(),
                    }).then(r => ({ ...r.data, tipo_dia: tipoDe(mas) })).catch(() => null);
                }));
                setDiasArranque(montados.filter(conComida));
            } catch {
                setDiasArranque([]);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx]);

    // AL RECARGAR EN LOS MACROS, SUS MENÚS SIGUEN AHÍ (31-10): lo que devolvió `montar-dia` se
    // pierde con la página, y lo guardado es lo mismo que va a ver en Nutrición.
    useEffect(() => {
        if (flow[idx]?.type !== 'result' || !retomandoCierre || diaGuardado) return;
        api.get(`/diets/${hoyLocal()}`)
            .then(r => { if (r.data?.exists) setDiaGuardado(r.data); })
            .catch(e => console.error('[alta] no se pudo leer el día de hoy', e));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, retomandoCierre]);

    // Al pasar del alta al cuestionario de ajuste, el flujo es OTRA lista: hay que volver al
    // primer paso. Sin esto, el numero de paso que traia del alta apuntaba a una pregunta
    // cualquiera de la lista nueva y el cuestionario arrancaba por la mitad.
    //
    // Se compara contra el modo anterior en un ref (y no con la lista de dependencias) porque
    // este componente ya reinicia `idx` por otras vias y un setState suelto en un efecto se
    // encadenaba con ellas.
    // SE MIRA EL RECORRIDO, NO EL MODO (19-08). Estaba atado a `modoAjuste`, y ese cambia
    // solo al ENVIAR -- en cuanto se marca que el cuestionario ya está mandado --, aunque las
    // pantallas sean exactamente las mismas. Resultado en la pasada de «nos faltan cosas
    // tuyas»: el cliente contestaba las quince, pulsaba «Calcular mis macros», sus respuestas
    // se guardaban... y la pantalla volvía a la primera pregunta en vez de enseñarle sus
    // macros. Comparando la lista de pasos, el reinicio solo ocurre cuando de verdad es otro
    // cuestionario.
    const recorridoAnteriorRef = useRef(null);
    useEffect(() => {
        const firma = flow.map(s => s.key || s.type).join('|');
        if (recorridoAnteriorRef.current === null) { recorridoAnteriorRef.current = firma; return; }
        if (recorridoAnteriorRef.current !== firma) {
            recorridoAnteriorRef.current = firma;
            setIdx(0);
        }
    });

    // EL CIERRE DEL ALTA, APUNTADO Y RETOMADO (ALT-06, 14-10). Ver `lib/cierreDelAlta`.
    //
    // Retomar: al montar, y cada vez que el recorrido cambie (el de justo arriba lo devuelve
    // a la primera pantalla; el plan llega a veces después que la ficha), se le pone en la
    // última pantalla del cierre en la que estaba. Va DETRÁS del de arriba a propósito: en el
    // mismo render, el último `setIdx` es el que vale.
    const tiposDelRecorrido = flow.map(s => s.type);
    const firmaDelRecorrido = flow.map(s => s.key || s.type).join('|');
    const ultimaDelCierreRef = useRef(null);
    useEffect(() => {
        if (!retomandoCierre) return;
        const i = dondeRetomarElCierre(tiposDelRecorrido, ultimaDelCierreRef.current || cierreARetomarRef.current);
        if (i >= 0) setIdx(i);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [firmaDelRecorrido, retomandoCierre]);

    // Apuntar: en cada pantalla del cierre se guarda cuál es (su tipo), con cómo se le
    // entregaron los macros y su día de hoy, que son lo que la pantalla de los macros no
    // puede volver a pedir. Pasado el cierre (el completo del que lleva entrenador) se borra.
    const marcaDelCierreRef = useRef(null);       // lo último apuntado; null = nada apuntado
    const firmaApuntadaRef = useRef(null);
    const terminarElCierre = async () => {
        if (!marcaDelCierreRef.current) return;
        marcaDelCierreRef.current = null;
        try {
            await api.put('/clients/ajuste-progreso', { flujo: 'cierre', pantalla: null });
            // La ficha en memoria puede traer la marca (se refresca a mitad del cierre): sin
            // esto, el panel le devolvería aquí al salir.
            await refreshProfile();
        } catch (e) {
            console.error('[alta] no se pudo dar por terminado el cierre', e);
        }
    };
    useEffect(() => {
        if (revision || !enElCierre) return;
        // Con el recorrido recién cambiado, `idx` todavía es el de la lista vieja.
        if (firmaApuntadaRef.current !== firmaDelRecorrido) { firmaApuntadaRef.current = firmaDelRecorrido; return; }
        const tramo = tramoDelCierre(tiposDelRecorrido, idx);
        if (tramo === 'antes') return;          // no ha llegado, o está aterrizando al retomar
        // Pasado el cierre empieza el largo, que guarda su borrador (`nivel1`, 24-10). Se
        // escribe DESPUÉS de borrar la marca del cierre: si no, ese borrado se lo llevaba.
        if (tramo === 'despues') {
            terminarElCierre().then(() => guardarProgreso(answersRef.current, idx));
            return;
        }
        const pantalla = tiposDelRecorrido[idx];
        ultimaDelCierreRef.current = pantalla;
        const cuerpo = {
            flujo: 'cierre', pantalla, entrega: entrega || null,
            dia_de_hoy: diaMontado?.montadas?.map(m => ({ comida: m.comida, menu: m.menu })) || null,
        };
        const clave = JSON.stringify(cuerpo);
        if (clave === marcaDelCierreRef.current) return;
        marcaDelCierreRef.current = clave;
        api.put('/clients/ajuste-progreso', cuerpo)
            .catch(e => console.error('[alta] no se apuntó la pantalla del cierre', e));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, enElCierre, firmaDelRecorrido, entrega, diaMontado]);

    // Y si se sale de la pantalla con un avance programado, que no quede vivo (punto 4.12).
    useEffect(() => () => {
        if (avancePendienteRef.current) clearTimeout(avancePendienteRef.current);
    }, []);

    // EL FOCO, EN EL PRIMER CAMPO QUE QUEDA POR RELLENAR (18-10). Al llegar a cada pantalla se
    // pone el cursor en el primer campo de escribir (texto, número, fecha...) que esté vacío,
    // o en el primero si ya están todos; así se contesta sin tocar el ratón. Las pantallas sin
    // campos (opciones, portadas) se quedan como están. Si ya hay un campo con el foco -- uno
    // con su `autoFocus` --, se respeta.
    useEffect(() => {
        const t = setTimeout(() => {
            const zona = document.querySelector('[data-contenido-del-cuestionario]');
            if (!zona) return;
            const activo = document.activeElement;
            if (activo && zona.contains(activo) && activo.matches('input, textarea')) return;
            const NO_SE_ESCRIBEN = ['hidden', 'file', 'checkbox', 'radio', 'range', 'button', 'submit', 'color'];
            const campos = [...zona.querySelectorAll('input, textarea')].filter(el =>
                !el.disabled && !el.readOnly && !NO_SE_ESCRIBEN.includes(el.type) && el.offsetParent !== null);
            const campo = campos.find(el => !el.value) || campos[0];
            if (campo) campo.focus({ preventScroll: true });
        }, 60);
        return () => clearTimeout(t);
    }, [idx]);

    // Retomar el cuestionario de ajuste donde lo dejó, y arrancar la cabecera con los macros
    // que tiene ahora mismo (los provisionales del alta) para que se vea de dónde parte.
    useEffect(() => {
        // En modo revisión NO se retoma nada: se viene a ver el cuestionario desde el
        // principio, y reanudarlo por la mitad deja fuera justo las pantallas que se quieren
        // repasar. Las respuestas guardadas siguen intactas: aquí solo no se cargan.
        if (revision) return;
        // Volviendo de pagar el ajuste, con la ficha ya confirmada (26-10): antes, el
        // recorrido todavía no es el suyo. Y tampoco mientras se decide si entró el pago ni
        // mientras se ve «Pago confirmado» (30-10): retomado aquí, su «Seguimos donde lo
        // dejaste» salía encima de la confirmación, o justo antes de mandarle a Inicio.
        if (confirmandoPago || pagoRevisado || pagoConfirmado) return;
        // En el largo, con su lista entera (29-10): las preguntas del básico que le falten se
        // ponen delante cuando llega `dietConfig`, y ese cambio de lista devolvía a la primera
        // pantalla al que acababa de retomarse. Esperando, este efecto corre en el mismo
        // render que el reinicio y, al ir detrás, su `setIdx` es el que vale.
        if (retomandoNivel1 && dietConfig === undefined) return;
        if (progresoCargadoRef.current || !profile) return;
        progresoCargadoRef.current = true;
        setBorradorColocado(true);
        const guardado = profile.ajuste_macros_progreso;
        // EL CIERRE NO TRAE RESPUESTAS (ALT-06): trae la pantalla, que la coloca el efecto de
        // `firmaDelRecorrido`, y lo que la de los macros enseña y no se puede volver a pedir.
        if (retomandoCierre) {
            if (guardado?.entrega) setEntrega(guardado.entrega);
            if (guardado?.dia_de_hoy?.length) setDiaMontado({ montadas: guardado.dia_de_hoy });
            toast.info('Seguimos donde lo dejaste');
            return;
        }
        // CADA RECORRIDO RETOMA EL SUYO (doc del cuestionario, 18-08). El alta y el ajuste
        // guardan en el mismo sitio, y el número de pantalla de uno no significa nada en el
        // otro: sin esta comprobación, quien dejó el ajuste por la séptima aterrizaría en la
        // séptima del alta, que es otra pregunta. Lo guardado sin `flujo` es de antes de
        // esto y solo puede ser del ajuste, que era el único que se guardaba.
        //
        // Y EL DE RETOMAR EL COMPLETO NO RETOMA NINGUNO (23-08): su lista es otra (los
        // huecos + el nivel 1) y el paso del borrador del alta apuntaba a mitad del
        // completo. El que compró los 87 € volvía al cuestionario y aterrizaba en «¿Qué
        // suplementos tomas ahora?» sin haber visto ni la portada.
        // Y EL QUE VIENE A COMPLETAR HUECOS TAMPOCO RETOMA NADA (12-09). Es el mismo
        // mordisco del 23-08 por la tercera puerta: `?completar=1` va en modo ajuste, así
        // que el borrador del ajuste le parecía suyo, y el que tenía uno a medias aterrizaba
        // en la pantalla del biotipo sin haber visto la portada de «Nos falta información».
        // Su lista es otra -- solo los huecos --, y el número de pantalla del ajuste no
        // significa nada en ella.
        const flujoDeAhora = retomandoNivel1 ? 'nivel1'
            : pidioCompletar ? 'completar'
                : (modoAjuste ? 'ajuste' : 'alta');
        const suyo = (guardado?.flujo || 'ajuste') === flujoDeAhora;
        if (suyo && guardado?.respuestas && Object.keys(guardado.respuestas).length) {
            // EL TELÉFONO VACÍO DEL BORRADOR NO PISA EL DE LA CUENTA (probadores, 13-10: "el
            // cuestionario debería ofrecer el teléfono que el cliente ya dio"). El borrador
            // guarda todas las respuestas, también el teléfono en blanco de quien aún no
            // había llegado a esa pantalla, y al restaurarlo borraba el que se acababa de
            // sembrar desde su cuenta. Uno escrito por él en el borrador sí manda.
            const delBorrador = { ...guardado.respuestas };
            if (!`${delBorrador.phone ?? ''}`.trim()) delete delBorrador.phone;
            answersRef.current = { ...answersRef.current, ...delBorrador };
            setAnswers(a => ({ ...a, ...delBorrador }));
            // AL REANUDAR, SALTARSE LAS QUE YA NO APLICAN (punto 4.12 del 09-08).
            //
            // Aquí se hacía `setIdx(paso)` a secas, y el paso guardado puede ser una pregunta
            // cuya condición ya no se cumple. Jesús aterrizaba en «¿Habría posibilidad de que
            // lo hicieras en días en que no vayas al gimnasio?» -- que solo sale si practica
            // otro deporte -- teniendo contestado que NO. Doblemente mal: la pregunta no le
            // tocaba, y ese «lo» se refiere a una pregunta que no está en pantalla.
            //
            // Se avanza hasta la primera que sí aplique con las respuestas ya restauradas,
            // que es exactamente lo que hace `goNext` durante el recorrido normal.
            //
            // Y POR EL NOMBRE DE LA PANTALLA, NO POR SU NÚMERO (18-10, `lib/pantallaDelBorrador`):
            // el número se descolocaba en cuanto cambiaba la lista de pantallas.
            const paso = pasoDelBorrador(flow, guardado);
            if (paso > 0 && paso < flow.length) {
                let j = paso;
                while (j < flow.length - 1 && !visible(flow[j])) j++;
                setIdx(Math.min(j, flow.length - 1));
            }
            recalcularEnVivo(guardado.respuestas);
            toast.info('Seguimos donde lo dejaste');
        } else {
            recalcularEnVivo({});
            // Y el que vuelve a por el largo sin haber contestado nada, que sepa dónde entra.
            if (retomandoNivel1) toast.info('Empiezas la segunda parte de tu perfil');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modoAjuste, profile, confirmandoPago, pagoRevisado, pagoConfirmado, dietConfig]);

    // Nombre y email ya los tenemos del login: autocompletar (el email no es editable).
    useEffect(() => {
        if (!user) return;
        setAnswers(a => ({
            ...a,
            name: a.name ?? user.name ?? '',
            email: user.email ?? a.email ?? '',
            // El teléfono vive en la cuenta, no en la ficha: sin sembrarlo, al que ya lo dio
            // se le volvía a pedir al completar su ficha.
            // Con `||` y no `??` (13-10): un teléfono en blanco no es una respuesta, y con
            // `??` el campo se quedaba vacío aunque la cuenta lo tuviera.
            phone: a.phone || user.phone || '',
        }));
        if (user.phone && !`${answersRef.current.phone ?? ''}`.trim()) {
            answersRef.current = { ...answersRef.current, phone: user.phone };
        }
    }, [user]);

    // El sexo y el objetivo se contestaron en el ALTA y viven en el perfil, no en las
    // respuestas de este cuestionario. Se siembran aquí porque hay preguntas que dependen
    // de ellos: las opciones cambian según el objetivo, y la pantalla del biotipo no se
    // le enseña a una mujer (las siete fotos son de hombre). Sin esto, en el cuestionario
    // de ajuste `answers.sex` venía vacío y la condición no se cumplía nunca.
    useEffect(() => {
        if (!profile) return;
        // También en la ref: quien decide qué pantallas se saltan (`visible`) lee de
        // answersRef, no del estado, así que sembrar solo el estado no cambiaba nada.
        // Y LO QUE YA CONTESTÓ DEL BÁSICO, por lo mismo: el cuestionario largo no repite lo
        // que ya está en su ficha, y para saberlo tiene que verlo aquí. Al que entró antes
        // del básico nuevo esto le llega vacío y sus preguntas siguen saliendo, que es lo
        // que se quiere.
        const delBasico = {};
        for (const clave of ['tiempo_intentandolo', 'motivo_apuntarse',
                             'por_que_a_mi', 'por_que_ahora', 'dietas_previas',
                             'lactosa', 'gluten', 'alergias', 'peso_maximo', 'peso_minimo',
                             'peso_mejor_momento', 'mejor_forma_pasada', 'profesion', 'como_me_conociste',
                             'proteinas_habituales', 'birthdate', 'height', 'biotype',
                             'training_experience',
                             'entrenador_anterior', 'entrenador_anterior_que_tal',
                             // Y los números que ya tiene, para que al completar su ficha no
                             // se le vuelva a preguntar el peso y la grasa que ya constan.
                             'weight', 'body_fat', 'profesion']) {
            const v = profile[clave];
            // LA FECHA IMPOSIBLE NO SE SIEMBRA (Jesus, 9-09). Sembrarla la daba por
            // contestada, el completo se saltaba la pregunta y al terminar la mandaba tal
            // cual: el servidor la rechazaba y no habia ninguna pantalla donde corregirla.
            // Sin sembrarla, la pregunta vuelve a salir vacia y el la escribe bien.
            if (clave === 'birthdate' && !fechaDeNacimientoUsable(v)) continue;
            // NI EL PESO, NI LA ALTURA, NI LA GRASA IMPOSIBLES (punto 46, 9-09). Lo mismo
            // con los otros cuatro campos con rango. En produccion hay fichas de la
            // importacion de Calma con una grasa del 71,8 % que el servidor no acepta: se
            // sembraba, la pantalla la daba por contestada -- y en el carrusel de grasa ni
            // siquiera se veia, porque un valor que no esta en la escala deja el carrusel
            // en el de por defecto -- y al terminar viajaba el 71,8 y el envio se caia.
            // Sin sembrarlo, la pregunta vuelve a salir y el contesta.
            if (RANGOS_PERFIL[clave] && !datoDelPerfilUsable(clave, v)) continue;
            if (v !== null && v !== undefined && v !== '') delBasico[clave] = v;
        }
        // Los días de entreno (7.1 del 21-08), solo si están guardados COMO NOMBRES: las
        // fichas heredadas de Calma traen enteros sin semántica verificable, y sembrarlos
        // daría la pregunta por contestada con un dato que no sirve para colocar grupos.
        if (Array.isArray(profile.training_weekdays)
            && profile.training_weekdays.some(d => typeof d === 'string')) {
            delBasico.training_weekdays = profile.training_weekdays.filter(d => typeof d === 'string');
        }
        // Y LAS CINCO QUE MUEVEN LOS MACROS, que no viven sueltas en el perfil sino dentro de
        // `ajustes_macros`. Sin esto el completo se las volvía a preguntar a todo el mundo,
        // porque aquí no las veía: son las mismas preguntas, guardadas en otro cajón.
        for (const clave of ['sigue_dieta', 'tiempo_dieta', 'como_va', 'hambre_saturacion',
                             'deporte_extra', 'deporte_cual', 'deporte_en_descanso',
                             'actividad_diaria', 'apetito', 'facilidad_engordar',
                             'cuesta_definir']) {
            const v = (profile.ajustes_macros || {})[clave];
            if (v !== null && v !== undefined && v !== '') delBasico[clave] = v;
        }
        // Lo que de esto ya está en su ficha, el largo no lo repite (`yaEnElBasico`). Solo se
        // añade: lo que contesta dentro del largo no llega a la ficha hasta enviarlo.
        for (const clave of YA_ESTAN_EN_EL_BASICO) {
            if (!sinContestar(delBasico, clave)) yaContestadasRef.current.add(clave);
        }
        if (profile.mejor_forma_pasada) yaContestadasRef.current.add('peso_mejor_momento');
        answersRef.current = {
            ...delBasico,
            ...answersRef.current,
            sex: answersRef.current.sex ?? profile.sex ?? undefined,
            goal: answersRef.current.goal ?? profile.goal ?? undefined,
        };
        setAnswers(a => ({
            ...delBasico,
            ...a,
            sex: a.sex ?? profile.sex ?? undefined,
            goal: a.goal ?? profile.goal ?? undefined,
        }));
    }, [profile]);

    // ── Macros en vivo (doc 29-07) ────────────────────────────────────────────
    // Tras cada respuesta se recalcula y el cliente ve moverse los numeros. Si contesta y no
    // pasa nada visible, no contesta la siguiente. Se calcula sin aplicar nada: lo definitivo
    // se guarda al terminar el cuestionario.
    const recalcularEnVivo = useCallback(async (respuestas) => {
        // Con lo que haya contestado EN EL PROPIO CUESTIONARIO por delante de la ficha:
        // al retomar un borrador, el peso y el % graso que acaba de escribir todavía no
        // están en el perfil, y la barra no llegaba a montarse nunca (hallazgo del P29,
        // recorrido del 23-08). Sin peso u objetivo no hay cálculo posible y se calla.
        const r = respuestas || {};
        const peso = r.weight ?? profile?.weight;
        const objetivo = r.goal ?? profile?.goal;
        const grasa = r.body_fat ?? profile?.body_fat;
        if (!modoAjuste || !peso || !objetivo) return;
        setCalculandoVivo(true);
        try {
            const res = await api.post('/calculator/targets', {
                peso,
                sexo: r.sex || profile?.sex || 'hombre',
                porcentaje_graso: grasa,
                objetivo,
                ajustes: ajustesDe(respuestas),
            });
            // El valor anterior se guarda desde dentro del setState: asi es el real y no uno
            // capturado en un closure viejo.
            setVistaPrevia(anterior => {
                previosRef.current = anterior?.macros || null;
                return res.data || null;
            });
        } catch (e) {
            /* el recalculo es un extra: si falla, se sigue contestando */
        } finally {
            setCalculandoVivo(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, modoAjuste, profile]);

    // SI EL PASO EN EL QUE ESTÁ DEJA DE TENER SENTIDO, SE PASA SOLO.
    //
    // Qué pantallas se saltan se mira AL AVANZAR, así que una que se vuelve innecesaria con
    // el cliente ya dentro se queda pintada. Pasa con las fotos y las medidas: cuántas fotos
    // tiene se pide al llegar, y hasta que llega la respuesta la pantalla ya está en la
    // pantalla, así que a quien las tenía todas se le seguía pidiendo lo que ya había dado.
    //
    // VA AQUÍ ARRIBA, con los demás hooks, y no junto a `visible`: más abajo hay un `return`
    // (el «ya completaste el cuestionario»), y un hook detrás de un return se llama unas
    // veces sí y otras no. React no lo permite y el proyecto entero deja de compilar.
    useEffect(() => {
        if (!flow.length || idx >= flow.length - 1) return;
        const s = flow[idx];
        // Las de las fotos y las medidas se saltan solas si ya las tiene. Va aquí además de
        // en `visible` porque la ficha y el perfil llegan del servidor DESPUÉS de montar el
        // recorrido: sin esto, al que ya las dio le salía la pantalla y desaparecía sola un
        // segundo después.
        // Y con la misma regla que `visible`, que es la que usan Siguiente y Atrás: si aquí
        // se mirara otra cosa, una pantalla podría saltarse sola al volver a ella (25-10).
        const sobra = (s.type === 'fotos_alta' && (ficha?.fotos_subidas || 0) > 0)
                   || (s.type === 'medidas_alta'
                       && !!(profile?.punto_de_partida_hecho || profile?.medidas_inicio))
                   || !visible(s);
        if (sobra) setIdx(i => Math.min(i + 1, flow.length - 1));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, ficha, profile]);

    // El progreso se guarda a cada respuesta: si se sale y vuelve, sigue donde lo dejo.
    //
    // TAMBIÉN EN EL ALTA desde el 18-08. Antes esto se cortaba en seco si no era el
    // cuestionario de ajuste, así que el alta -- que es el recorrido largo, y el único que
    // se hace una sola vez en la vida -- era justo el que no se guardaba. Va con el nombre
    // del recorrido para que cada uno retome el suyo.
    const guardarProgreso = useCallback((respuestas, paso) => {
        if (revision) return;   // en modo revisión no se escribe nada
        // Con el alta ya enviada, el borrador sobra: el recorrido que queda (el cierre)
        // no se reanuda, y seguir escribiéndolo dejaba un paso enorme que luego
        // descolocaba al que volvía a por el completo. Y tampoco al retomar el cierre: lo
        // que se guarda ahí es la pantalla en la que va, no un número de paso (ALT-06).
        //
        // SALVO EN EL CUESTIONARIO LARGO DE DETRÁS (24-10): pasado el cierre, el que lleva
        // entrenador sigue con el completo en la misma sesión, y eso sí se retoma -- como
        // `nivel1`, el mismo borrador que lee quien vuelve a por él otro día --. Sin esto,
        // «Cerrar sesión» a mitad del largo le devolvía a su primera pregunta en blanco.
        const enElLargo = estaEnElLargo(paso);
        if (enElCierre && !enElLargo) return;
        // Las fotos NO viajan en el borrador: son base64 de megas y esto se guarda a cada
        // avance. Van una sola vez, en el envío final. Quien salga a mitad pierde solo la
        // foto (se le vuelve a pedir), no las respuestas.
        const { foto_grasa, foto_mejor_momento, dieta_imagen, ...ligeras } = respuestas;
        // Devuelve la promesa: «Cerrar sesión» espera a que se guarde antes de salir.
        return api.put('/clients/ajuste-progreso',
                { respuestas: ligeras, paso, ...pantallaParaGuardar(flow, paso),
                  flujo: enElLargo ? 'nivel1' : (modoAjuste ? 'ajuste' : 'alta') }).catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [api, modoAjuste, revision, enElCierre, retomandoNivel1, flow]);

    // ── P10: leer la dieta que trae el cliente ────────────────────────────────
    // (La puerta «Un día mío» se quitó el 23-08: en el alta nadie tiene días creados,
    // así que `mis-dias` ya no se consulta desde aquí.)

    // SALIENDO, TRAS ENVIAR EL COMPLETO. «Próximos pasos» se quitó el 20-10 (prompt del
    // rediseño, punto 34): se va directo a la bienvenida. Mientras se refresca la ficha, esta
    // pantalla manda sobre el recorrido, que con el largo ya enviado se recompone debajo.
    // Volviendo de pagar el ajuste: hasta saber si el pago entró, ningún recorrido (26-10).
    if (confirmandoPago) {
        return (
            <Shell>
                <p className="flex items-center justify-center gap-2 py-16 text-foreground/60" data-testid="confirmando-pago-ajuste">
                    <Loader2 className="w-5 h-5 animate-spin text-brand" /> Estamos confirmando tu pago…
                </p>
            </Shell>
        );
    }

    if (esperaElBorrador({ retomandoNivel1, revision, pagoRevisado, pagoConfirmado, borradorColocado,
                           flujoGuardado: profile?.ajuste_macros_progreso?.flujo })) {
        return (
            <Shell>
                <p className="flex items-center justify-center py-16" data-testid="colocando-borrador">
                    <Loader2 className="w-5 h-5 animate-spin text-brand" />
                </p>
            </Shell>
        );
    }

    if (pagoConfirmado) {
        return (
            <Shell>
                <div className="w-full max-w-[760px]" data-testid="pago-ajuste-confirmado">
                    <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#ff6a1f] mb-3">
                        Pago confirmado
                    </p>
                    {/* LOS TEXTOS DE LA BATERÍA DE JESÚS (prueba 1.11, 03-11). «Ya tienes tu
                        ajuste a medida» era falso: lo que tiene es el pago; el ajuste llega
                        cuando termine el cuestionario. */}
                    <h2 className="font-heading font-extrabold uppercase text-[36px] md:text-[52px] leading-[1.02] text-[#f2f0ed] [text-wrap:balance] mb-3">
                        Pago recibido correctamente
                    </h2>
                    <div className="rounded-[18px] border border-[#2a2a2a] bg-[#1a1a1a] px-5 py-5 mt-8 max-w-[620px]">
                        <p className="text-base leading-relaxed text-[#f2f0ed]">
                            Hemos recibido tu pago correctamente. El siguiente paso es que termines de rellenar el cuestionario.
                        </p>
                    </div>
                    <div className="mt-8">
                        <Button onClick={() => setPagoConfirmado(false)} data-testid="pago-ajuste-continuar" className={CTA}>
                            Continuar con el cuestionario <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                    </div>
                </div>
            </Shell>
        );
    }

    if (saliendoA) {
        return (
            <Shell>
                <div className="flex justify-center py-16" data-testid="saliendo-del-completo">
                    <Loader2 className="w-8 h-8 animate-spin text-brand" />
                </div>
            </Shell>
        );
    }

    // El ALTA no se puede repetir (ni por el enlace). El cuestionario de AJUSTE sí: si cambia de
    // trabajo o empieza a hacer otro deporte, lo vuelve a pasar y sus macros se recalculan.
    // Y tampoco corta al que viene a COMPLETAR lo que le falta (?completar=1): ese no está
    // repitiendo el alta, está contestando por primera vez lo que no llegamos a preguntarle.
    if (!revision && profile?.questionnaire_completed && !enElCierre && !retomandoNivel1
            && !pidioAjustar && !elBasicoQueFalta?.length) {
        return (
            <Shell>
                <div className="text-center">
                    <div className="w-16 h-16 rounded-full bg-brand/10 flex items-center justify-center mx-auto mb-6">
                        <Check className="w-8 h-8 text-brand" />
                    </div>
                    <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-2 leading-tight">
                        Ya completaste el cuestionario inicial
                    </h2>
                    <div className="flex justify-center">
                        <Button onClick={() => navigate('/dashboard')}
                            className={CTA}>
                            Ir al inicio <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                    </div>
                </div>
            </Shell>
        );
    }

    const step = flow[idx] || flow[0];
    // El progreso se calcula abajo, sobre los pasos que aplican de verdad (ver `visible`).

    // «LO HAGO LUEGO» (doc 19-08): salta directo a la oferta de los 87 €. Si por lo que
    // sea no hay oferta en el recorrido, al panel. El aviso «Completar preferencias» que
    // dejaba en la campana se eliminó el 17-10.
    const saltarAOferta = () => {
        const i = flow.findIndex(s => s.type === 'oferta_ajuste');
        if (i > idx) {
            setIdx(i);
            return;
        }
        // Y EN EL COMPLETO NO HAY OFERTA DETRÁS (desde el 8-09, que las fotos y las medidas
        // se piden dentro). Ahí «lo hago luego» tiene que SEGUIR el recorrido hasta el
        // repaso, que es donde se guarda el cuestionario: saliendo al panel se perdían las
        // respuestas de veinte pantallas sin decir nada.
        if (idx < flow.length - 1) { goNext(); return; }
        salirDelAlta();
    };

    const set = (key, value) => {
        // El ref se actualiza a la vez que el estado para que `visible()` vea la respuesta que
        // se acaba de dar, sin esperar al siguiente render.
        answersRef.current = { ...answersRef.current, [key]: value };
        setAnswers(a => ({ ...a, [key]: value }));
    };


    // ── Punto de partida: las medidas del dia 1 ──────────────────────────────
    //
    // Aquí también se le pedían las fotos, y se han quitado (punto 19 del doc del 07-08). El
    // motivo: acaba de darse de alta, todavía no sabe qué es un reporte ni para qué sirven
    // esas tres poses, y pedírselas ahí es pedirle que se desnude delante del móvil sin
    // haberle explicado nada. Las fotos van con el reporte, que es donde tienen sentido: se
    // le enseñan las del mes pasado al lado para que se coloque igual, y ahí sí entiende que
    // son para comparar.
    //
    // Lo que se pierde, y conviene saberlo: la foto "inicial" pasa a ser la de su primer
    // reporte y no la del día 1. Las medidas sí se siguen pidiendo aquí, con el vídeo de
    // Jesús explicando cómo se toman los perímetros, porque de esas no hay pudor que valga.

    const guardarPuntoDePartida = async () => {
        // Las MISMAS diez que en el reporte. Había tres listas distintas en la app -- aquí
        // cuatro (cintura, abdomen, cadera y la altura), en el check-in cinco y en el
        // reporte otras cinco -- y ninguna era la suya. Con listas distintas, la medida
        // del punto de partida no se puede comparar con la del mes siguiente, que es lo
        // único para lo que sirve tomarla.
        //
        // La altura NO va aquí: es un dato fijo, no una medida de seguimiento. Se pregunta
        // una vez en el cuestionario y se manda aparte.
        const medidas = {};
        for (const { key } of MEDIDAS) {
            const n = num(answers[`medida_${key}`]);
            if (n) medidas[key] = n;
        }
        // El servidor descarta callado la que no es creíble (12-10): se le dice antes de
        // enviar, con la misma regla que el reporte.
        const chequeo = revisarMedidas(medidas, { todas: false });
        if (!chequeo.ok) { toast.error(chequeo.error); return; }
        try {
            await api.post('/clients/punto-de-partida', { medidas, altura: num(answers.height) });
            // LAS MEDIDAS SE PIDEN UNA VEZ (10-10). Más adelante, el perfil completo tiene
            // su pantalla «Tus medidas», que se salta sola si ya están dadas... mirando la
            // ficha que hay en memoria, y esa no se refrescaba aquí: al que acababa de
            // escribirlas se le volvían a pedir, vacías. Se refresca la ficha (y la
            // pantalla ya no sale) y, por si sale igual, lleva puesto lo que escribió.
            if (Object.keys(medidas).length) {
                setMedidasAlta(m => ({ ...medidas, ...m }));
                await refreshProfile();
            }
        } catch (e) {
            // No bloquea el alta: las puede apuntar después en Seguimiento.
            console.error('[alta] las medidas del día 1 no se guardaron', e);
        }
        goNext();
    };

    const elegirFotoDieta = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = (ev) => {
            const file = ev.target.files?.[0];
            if (!file) return;
            if (file.size > 8 * 1024 * 1024) { toast.error('La foto pesa demasiado (máximo 8 MB)'); return; }
            const reader = new FileReader();
            reader.onload = (e) => set('dieta_imagen', e.target.result);
            reader.readAsDataURL(file);
        };
        input.click();
    };

    const leerMiDieta = async () => {
        const modo = answers.dieta_modo || 'texto';
        const cuerpo = modo === 'foto' ? { imagen: answers.dieta_imagen }
            : { texto: answers.dieta_texto };
        const vacio = modo === 'foto' ? !answers.dieta_imagen
            : !(answers.dieta_texto || '').trim();
        if (vacio) {
            toast.error(modo === 'foto' ? 'Sube la foto de tu dieta'
                : 'Cuéntanos qué comes en un día');
            return;
        }
        setLeyendoDieta(true);
        try {
            const r = await api.post('/clients/leer-dieta', cuerpo);
            setLecturaDieta(r.data);
        } catch (e) {
            toast.error(mensajeDeError(e, 'No hemos podido leer tu dieta'));
        } finally {
            setLeyendoDieta(false);
        }
    };

    // Confirmada: ya puede entrar en el cálculo. Los hidratos que manda el método son los del
    // día de entreno (comidas + peri), que es lo que el cliente acaba de validar.
    const confirmarDieta = () => {
        const m = lecturaDieta?.macros || {};
        const respuestas = {
            ...answers,
            dieta_hc_entreno: m.hidratos ?? null,
            dieta_grasa_entreno: m.grasa ?? null,
            dieta_confirmada: true,
        };
        answersRef.current = respuestas;
        setAnswers(respuestas);
        recalcularEnVivo(respuestas);
        guardarProgreso(respuestas, idx);
        setLecturaDieta(null);
        goNext();
    };

    // La oferta del final: se apunta lo que conteste y se le lleva a su panel. No cobra
    // nada todavía (ver POST /clients/ajuste-a-medida): falta decidir cómo se cobra.
    // SE COBRA AQUÍ MISMO (Francisco, 18-08). Antes solo se apuntaba lo que contestaba y el
    // equipo lo veía en su campana: quien decía que sí se quedaba esperando un correo que no
    // salía de ningún sitio. Ahora el sí va derecho a Stripe, y al volver el ajuste ya está
    // en la cola del lunes.
    //
    // El no se sigue guardando: saber cuánta gente lo rechaza vale igual que saber quién lo
    // compra, y es lo que dice si la oferta está bien puesta o no.
    //
    // DESDE LA PANTALLA DE SUS PRIMEROS MACROS (26-10). «Me vale con la primera opción»
    // entra directo en la app. «Quiero el ajuste a medida» va a Stripe con `origen: alta`: al
    // volver (`?pago_ajuste=1`) sigue por el cuestionario largo, que hace de reporte del
    // ajuste (`core/reporte_del_ajuste`). Si no paga -- cancela en Stripe o el cobro no
    // arranca --, también entra en la app.
    const entrarEnLaApp = async () => {
        await terminarElCierre();
        navigate('/dashboard');
    };
    const responderOferta = async (quiere) => {
        setLoading(true);
        try {
            await api.post('/clients/ajuste-a-medida', { quiere });
            if (quiere) {
                const { url } = await abrirPagoDelAjuste(api, {
                    origen: 'alta',
                    success_path: '/questionnaire?pago_ajuste=1',
                    cancel_path: '/dashboard',
                });
                if (url) {
                    // Contestada la oferta, el cierre está hecho: a la vuelta de Stripe no se
                    // le vuelve a preguntar (ALT-06).
                    await terminarElCierre();
                    window.location.href = url;   // se va a Stripe
                    return;
                }
                toast.success(AJUSTE_YA_EN_MARCHA, { siempre: true });
                await refreshProfile();
            } else {
                toast.success('Perfecto, seguimos con tu plan.');
            }
        } catch (e) {
            // Al usuario nunca la traza: si el cobro no arranca, se le dice que lo tenemos
            // apuntado -- que es verdad, la respuesta sí se guardó -- y el equipo lo ve.
            console.error('[alta] la oferta del ajuste a medida', e);
            if (quiere) {
                toast.info('Lo tenemos apuntado. Te escribimos para cerrarlo.');
            }
        }
        setLoading(false);
        entrarEnLaApp();
    };

    // Una respuesta contestada: se recalcula y se guarda, las dos cosas con lo de este instante.
    const trasResponder = (key, value) => {
        const respuestas = { ...answers, [key]: value };
        recalcularEnVivo(respuestas);
        guardarProgreso(respuestas, idx);
    };

    // Pasos condicionales (p.ej. las preguntas de la dieta solo si sigue_dieta).
    //
    // Se leen de un ref y no del estado a proposito: al pasar de pantalla se comprueba que pasos
    // hay que saltar, y esa comprobacion ocurre justo despues de contestar, cuando el estado de
    // React todavia tiene el valor anterior. Con `answers` a secas, decir "si, sigo una dieta"
    // se saltaba las cuatro preguntas de la dieta, que es justo lo que acababa de habilitar.
    // Solo en desarrollo: deja a mano el recorrido y en qué paso va, para poder mirar desde
    // la consola por qué una pantalla no avanza. En producción no se ejecuta.
    if (process.env.NODE_ENV === 'development') {
        window.__quiz = { idx, pasos: flow.map(s => s.key || s.type), respuestas: answersRef.current };
    }

    // PEDIR «SIGUIENTE» EN LA ÚLTIMA PANTALLA ES HABER TERMINADO.
    //
    // `goNext` se topaba con el final de la lista y se quedaba donde estaba, en silencio. Con
    // eso, cualquier botón de la última pantalla es un botón muerto: el cliente lo pulsa, no
    // pasa nada y no tiene forma de salir salvo el botón de atrás del navegador. Pasaba en la
    // pantalla de macros de «nos faltan cosas tuyas», que ahí es la última.
    // Y SALIR ES TERMINAR EL CIERRE (ALT-06): todas sus salidas pasan por aquí.
    // A MI DÍA, SIN LA BIENVENIDA (04-11): la pantalla «Tus macros están calculados» se quitó
    // (prueba 5.3); sus macros y el aviso del ajuste pagado ya salen en Mi día.
    const salirDelAlta = async () => {
        await terminarElCierre();
        navigate('/dashboard');
    };

    // Si vino del repaso a corregir una respuesta, sale de la pregunta de vuelta al repaso.
    const volverAlRepaso = () => {
        const destino = volverAlRepasoRef.current;
        if (destino == null) return false;
        volverAlRepasoRef.current = null;
        guardarProgreso(answersRef.current, destino);
        setIdx(destino);
        return true;
    };

    const goNext = () => {
        if (idx >= flow.length - 1) { salirDelAlta(); return; }
        if (volverAlRepaso()) return;
        let j = idx + 1;
        while (j < flow.length - 1 && !visible(flow[j])) j++;
        const destino = Math.min(j, flow.length - 1);
        // EL BORRADOR SE GUARDA AL AVANZAR, CON TODO (regla 3 del doc del 23-08). Antes
        // solo guardaban las preguntas de opciones (pickChoice): lo escrito en textos,
        // números y multiselects se quedaba en memoria, y quien salía a mitad volvía a una
        // pantalla anterior a la suya. Se guarda el índice de DESTINO, no el actual: al
        // volver se aterriza en la primera pregunta sin contestar, no en la última que
        // acababa de responder.
        guardarProgreso(answersRef.current, destino);
        setIdx(destino);
    };

    const cancelarAvancePendiente = () => {
        if (avancePendienteRef.current) {
            clearTimeout(avancePendienteRef.current);
            avancePendienteRef.current = null;
        }
    };
    // «PASA SOLA» EN UN SOLO SITIO (18-10). Las opciones únicas, la ocupación y el biotipo
    // avanzaban cada una con su temporizador, y el del biotipo no se cancelaba: un Atrás en
    // esos 280 ms acababa igual en la pantalla siguiente. Aquí se cancela el anterior y lo
    // cancela también el Atrás. 550 ms en el ajuste, 280 en el alta (rediseño del 18-10).
    const pasarSola = () => {
        cancelarAvancePendiente();
        avancePendienteRef.current = setTimeout(() => {
            avancePendienteRef.current = null;
            goNext();
        }, modoAjuste ? 550 : 280);
    };
    const goBack = () => {
        cancelarAvancePendiente();
        // ATRÁS TAMBIÉN VUELVE AL REPASO (Jesús, 31-10): llevaba a la pregunta anterior del
        // recorrido y le obligaba a pasar otra vez por todas para cambiar una sola respuesta.
        if (volverAlRepaso()) return;
        const anterior = pantallaAnterior(flow, idx, visible);
        if (anterior >= 0) setIdx(anterior);
    };

    const num = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n; };

    // TODO LO QUE TRAE EL BÁSICO, en un solo sitio: lo mandan el alta y también el cierre del
    // cuestionario largo cuando el cliente no había pasado nunca por el básico (los de Calma).
    // Escrito dos veces se quedaría viejo en una de las dos el día que se añada una pregunta.
    const cuerpoDelBasico = () => ({
        name: answers.name,
        email: answers.email,
        phone: answers.phone,
        goal: answers.goal,
        sex: answers.sex,
        weight: parseFloat(answers.weight),
        body_fat: parseFloat(answers.body_fat),
        birthdate: answers.birthdate || null,
        height: num(answers.height),
        biotype: answers.biotype || null,
        training_experience: answers.training_experience || null,
        // Sus días de entreno (7.1 del 21-08): nombres de día, los guarda el perfil.
        training_weekdays: answers.training_weekdays || null,
        profesion: answers.profesion || null,
        // Una lista desde el 24-10 (se pueden marcar varias). La de antes era un texto suelto
        // y se manda tal cual. «Otro» lleva su texto.
        como_me_conociste: (Array.isArray(answers.como_me_conociste)
            ? (answers.como_me_conociste.length ? answers.como_me_conociste : null)
            : (answers.como_me_conociste || null)),
        como_me_conociste_otro: (Array.isArray(answers.como_me_conociste)
            && answers.como_me_conociste.includes('otro'))
            ? (`${answers.como_me_conociste_otro ?? ''}`.trim() || null) : null,
        // El botón «Ahora estoy en mi peor momento de forma» (24-10; el texto, 04-11).
        peor_forma_ahora: answers.peor_forma_ahora === true ? true : null,
        // Lo que más le gusta (24-10): categorías y alimentos sueltos. Lo que no quiere va
        // más abajo, con `avoided_*`.
        food_preferences: Array.isArray(answers.food_preferences) ? answers.food_preferences : null,
        preferred_foods: Array.isArray(answers.preferred_foods) ? answers.preferred_foods : null,
        // Las dos nuevas del bloque «Y para terminar» (documento del 7-09). Van aquí porque
        // esto es una lista cerrada: lo que no se nombra, no se guarda.
        tiempo_siguiendome: answers.tiempo_siguiendome || null,
        algo_mas: answers.algo_mas || null,
        proteinas_habituales: answers.proteinas_habituales || null,
        peso_maximo: num(answers.peso_maximo),
        peso_maximo_ano: num(answers.peso_maximo_ano),
        peso_maximo_nota: answers.peso_maximo_nota || null,
        peso_mejor_momento: num(answers.peso_mejor_momento),
        peso_mejor_momento_ano: num(answers.peso_mejor_momento_ano),
        peso_mejor_momento_nota: answers.peso_mejor_momento_nota || null,
        // El «pásala» viaja como respuesta: sin él, la ficha no distingue «no he estado
        // en forma» de «no llegó a contestar».
        mejor_forma_pasada: answers.mejor_forma_pasada === true ? true : null,
        foto_mejor_momento: answers.foto_mejor_momento || null,
        peso_minimo: num(answers.peso_minimo),
        peso_minimo_ano: num(answers.peso_minimo_ano),
        peso_minimo_nota: answers.peso_minimo_nota || null,
        alergias: answers.alergias || null,
        lactosa: answers.lactosa || null,
        gluten: answers.gluten || null,
        alergia_otra: answers.alergia_otra || null,
        // La foto del carrusel de grasa (doc 23-08, punto 1) y lo que no quiere ver en el
        // plato (punto 14). El backend guarda la foto aparte y funde las exclusiones con
        // las que salen de sus intolerancias.
        foto_grasa: answers.foto_grasa || null,
        avoided_categories: answers.avoided_categories || null,
        avoided_keywords: answers.avoided_keywords || null,
        dietas_previas: answers.dietas_previas || null,
        tiempo_intentandolo: answers.tiempo_intentandolo || null,
        motivo_apuntarse: answers.motivo_apuntarse || null,
        // Las dos en que se partio, desde el 8-09.
        por_que_a_mi: answers.por_que_a_mi || null,
        por_que_ahora: answers.por_que_ahora || null,
        entrenador_anterior: answers.entrenador_anterior || null,
        // El relato solo si ha dicho que sí: si contesta «sí», escribe, y luego se vuelve
        // atrás y lo cambia a «no», lo escrito deja de tener a qué referirse.
        entrenador_anterior_que_tal: answers.entrenador_anterior === 'si'
            ? (answers.entrenador_anterior_que_tal || null) : null,
    });

    // Las respuestas que ajustan los macros, en el formato que espera el backend.
    // Recibe las respuestas por parametro para poder calcular con las de ESTE instante: el
    // estado de React aun no se ha actualizado cuando se acaba de pulsar una opcion.
    const ajustesDe = (a) => ({
        actividad_diaria: a.actividad_diaria ?? null,
        deporte_extra: a.deporte_extra ?? null,
        facilidad_engordar: a.facilidad_engordar ?? null,
        cuesta_definir: a.cuesta_definir ?? null,
        sigue_dieta: a.sigue_dieta ?? null,
        tiempo_dieta: traeDieta(a) ? (a.tiempo_dieta ?? null) : null,
        como_va: traeDieta(a) ? (a.como_va ?? null) : null,
        hambre_saturacion: traeDieta(a) ? (a.hambre_saturacion ?? null) : null,
        dieta_texto: traeDieta(a) ? (a.dieta_texto || null) : null,
        dieta_hc_entreno: traeDieta(a) ? num(a.dieta_hc_entreno) : null,
        dieta_grasa_entreno: traeDieta(a) ? num(a.dieta_grasa_entreno) : null,
        dieta_confirmada: a.dieta_confirmada === true,
    });

    // La respuesta de farmacología, siempre como lista (multiselect): la mandan el cálculo
    // de macros y el envío del largo (20-10).
    const farmacologiaUso = () => (Array.isArray(answers.farmacologia_uso)
        ? answers.farmacologia_uso
        : (answers.farmacologia_uso ? [answers.farmacologia_uso] : null));
    const ajustesDelCuestionario = () => ({
        actividad_diaria: answers.actividad_diaria ?? null,
        deporte_extra: answers.deporte_extra ?? null,
        // Las dos del deporte solo tienen sentido si ha dicho que sí practica alguno.
        deporte_cual: answers.deporte_extra === true ? (answers.deporte_cual || null) : null,
        deporte_en_descanso: answers.deporte_extra === true ? (answers.deporte_en_descanso ?? null) : null,
        facilidad_engordar: answers.facilidad_engordar ?? null,
        apetito: answers.apetito ?? null,
        cuesta_definir: answers.cuesta_definir ?? null,
        training_experience: answers.training_experience ?? null,
        sigue_dieta: answers.sigue_dieta ?? null,
        // Lo que solo tiene sentido si trae dieta. `parecido` cuenta como que la trae.
        tiempo_dieta: conDieta() ? (answers.tiempo_dieta ?? null) : null,
        hambre_saturacion: conDieta() ? (answers.hambre_saturacion ?? null) : null,
        dieta_texto: conDieta() ? (answers.dieta_texto || null) : null,
        dieta_hc_entreno: conDieta() ? num(answers.dieta_hc_entreno) : null,
        dieta_grasa_entreno: conDieta() ? num(answers.dieta_grasa_entreno) : null,
        dieta_confirmada: answers.dieta_confirmada === true,
        // Esta va SIEMPRE: ahora se le pregunta a los tres, también al que come lo que
        // surge, y ahí es donde más dice. Condicionarla a que siga una dieta era tirar la
        // respuesta de quien no la sigue.
        como_va: answers.como_va ?? null,
        // El biotipo y la altura ya no están en el perfil largo: se contestan aquí, en el
        // cuestionario que ven los tres planes, así que aquí se guardan.
        biotype: answers.biotype || null,
        height: num(answers.height),
        // La de farmacología (20-10): este cálculo va antes del envío del largo, que es el
        // que la guarda, y sin ella saldrían sin los +10 g de descanso (`core.farmacologia`).
        farmacologia_uso: farmacologiaUso(),
    });

    // Trae dieta: la mide (true) o come parecido sin medirla ('parecido'). Solo el
    // "no, como lo que surge" (false) se queda fuera.
    const conDieta = () => traeDieta(answers);

    // CALCULAR. En el alta van los cuatro datos de la tabla y salen macros provisionales; en el
    // cuestionario de ajuste van las respuestas que ajustan y salen los definitivos.
    const submitNivel0 = async () => {
        setLoading(true);
        try {
            // Los cuatro datos de la tabla van primero, porque son los que crean la ficha y
            // sin ficha no hay nada que ajustar. Pero no se le enseña ningún número todavía:
            // el resultado que ve es uno solo, el de después, ya con los modificadores.
            // Y TAMBIÉN CUANDO VIENE A COMPLETAR: ese ya tiene el cuestionario dado por
            // hecho, así que sin esto su pasada no llegaba a guardarse (el servidor la
            // rechazaba con un 409) y se quedaba mirando el botón de calcular.
            //
            // Y cuando le falta algo de la base (peso, grasa u objetivo): esas respuestas
            // TIENEN que estar escritas antes de pedir el ajuste, porque el ajuste las lee
            // del perfil. Sin esto contestaba las preguntas nuevas y el servidor seguía
            // diciendo que le faltaban.
            const rellenandoHuecos = !!elBasicoQueFalta?.length || !!faltaLaBase.length;
            // Si esta pasada es la del alta: el servidor no la cuenta como haber pedido un
            // ajuste de macros (12-10). Se mira antes de guardar, que es cuando es verdad.
            const esElAlta = !profile?.questionnaire_completed;
            if (!profile?.questionnaire_completed || rellenandoHuecos) {
                await api.post('/clients/questionnaire', {
                    // SIEMPRE `true` cuando se van a rellenar huecos, sin mirar la bandera del
                    // perfil que hay en memoria: si esa llega vieja -- y llega, justo después
                    // del alta -- se mandaba `false` sobre un cuestionario ya guardado y el
                    // servidor contestaba «el cuestionario ya fue completado». El servidor solo
                    // le hace caso si de verdad estaba completado, así que mandarlo de más no
                    // abre ninguna puerta.
                    completar: rellenandoHuecos,
                    ...cuerpoDelBasico(),
                });
            }
            // EL AJUSTE PUEDE DECIR QUE NO, Y NO ES UN ERROR.
            //
            // Al cliente cuyos macros lleva el equipo se le contesta 403 -- «tus macros los
            // llevamos nosotros» -- y eso es correcto: nadie va a machacar lo que le puso su
            // entrenador. Lo que no puede pasar es que se le caiga encima al final de veinte
            // pantallas, en rojo y sin dejarle salir, cuando sus respuestas YA se han
            // guardado en la línea de arriba. Se sigue adelante y ve los macros que tiene.
            let res = null;
            try {
                res = await api.post(`/clients/ajustar-macros${esElAlta ? '?del_alta=1' : ''}`, ajustesDelCuestionario());
            } catch (e) {
                if (e?.response?.status !== 403) throw e;
                console.info('[alta] sus macros los lleva el equipo: no se recalculan', e);
            }
            setResultado(res?.data?.resultado || null);
            // Con los macros ya calculados, se le deja el día de hoy montado. Va en segundo
            // plano y sin bloquear: si falla, se queda como estaba (con su día por montar) y
            // no se le estropea el final del alta por esto.
            // Con LA FECHA DEL CLIENTE (bloque F, 23-08): sin ella el servidor montaba el
            // dia en su propio reloj y un alta de madrugada estrenaba la app con el hoy
            // vacio y la dieta puesta en ayer.
            // Y CON LO QUE CONTESTÓ DE SU DÍA YA GUARDADO (10-10). Se pedía sin
            // configuración y antes de guardarla (eso pasaba dos pantallas después): al que
            // contestó «1 bloque único» o «3 comidas» se le montaba el día de hoy con
            // cuatro, entrenando después de la comida 1 y con intra + post.
            await guardarConfigDelDia();
            api.post('/calculator/montar-dia',
                     { guardar: true, fecha: ahoraDeLaApp().toLocaleDateString('en-CA'),
                       ...configDelDia() })
                .then(r => setDiaMontado(r.data || null))
                .catch(() => {});
            setEntrega(res?.data?.entrega || null);
            setNivel0Enviado(true);
            // LA CUENTA TAMBIÉN, NO SOLO LA FICHA (02-11, Francisco con encargo-l). El teléfono
            // de «Antes de empezar» se guarda en la cuenta, y la sesión seguía con la de antes
            // del alta: al volver por «Te queda la segunda parte», el largo le veía sin teléfono
            // y le ponía delante otra vez «Antes de empezar», con el campo vacío. `refreshUser`
            // trae las dos.
            await refreshUser();
            // Y lo que se le dice, según lo que haya pasado de verdad: al que se los llevamos
            // nosotros no se le canta «macros calculados», porque no se le han calculado.
            toast.success(res ? '¡Macros calculados!' : 'Guardado. Tu entrenador lo tiene ya.');
            goNext(); // -> pantalla de resultados
        } catch (e) {
            toast.error(mensajeDeError(e, 'Error al enviar el cuestionario'));
        } finally {
            setLoading(false);
        }
    };

    // Nivel 1 -> guardar perfil largo (no toca macros).
    //
    // `destino` es a dónde se le manda después. Por defecto a su Inicio, pero desde la última
    // pantalla del completo -- las fotos y las medidas -- se le manda a subirlas, y ahí hay
    // que GUARDAR ANTES de sacarle del cuestionario: si no, se va a la pantalla de reportes y
    // las veinte respuestas que acaba de dar se quedan sin escribir en su ficha.
    // Se comprueba que sea texto porque este mismo método es el `onClick` del botón de
    // enviar, y un onClick recibe el evento del clic como primer argumento.
    const submitNivel1 = async (destino) => {
        const irA = typeof destino === 'string' ? destino : '/dashboard';
        setLoading(true);
        try {
            // LO DEL BÁSICO PRIMERO, si este cliente nunca pasó por él (los que vienen de
            // Calma). El cuestionario largo no escribe el objetivo, ni el peso, ni las
            // proteínas, ni recalcula macros: eso lo hace la puerta del alta, y sin esta
            // llamada el cliente contestaba veinte pantallas más y su ficha seguía coja.
            if (laBaseQueFalta.length || loQueFaltaDelBasico.length) {
                await api.post('/clients/questionnaire', {
                    completar: true,     // ver el comentario en el envío del alta
                    ...cuerpoDelBasico(),
                });
            }
            // LAS QUE MUEVEN LOS MACROS, TAMBIÉN DESDE AQUÍ (punto 26 del doc del 19-08).
            // Las cuatro de la dieta ya no se preguntan en el alta: el que lleva entrenador
            // las contesta en este cuestionario, y el envío de abajo no las lleva -- el
            // nivel1 no toca macros --. Van por el mismo camino que en el alta: ese
            // endpoint guarda las respuestas ANTES de su candado, así que al de plan
            // personalizado le contesta 403 sin recalcular nada -- que es lo que se quiere
            // -- pero su `ajustes_macros` queda escrito y su entrenador lo puede leer.
            // Si falla por otra cosa no se le estropea el cierre del completo: sus veinte
            // respuestas van en la llamada de abajo, que es la que manda.
            try {
                await api.post('/clients/ajustar-macros', ajustesDelCuestionario());
            } catch (e) {
                console.info('[completo] los ajustes no se recalcularon', e?.response?.status, e);
            }
            await api.post('/clients/questionnaire/nivel1', {
                biotype: answers.biotype || null,
                height: num(answers.height),
                birthdate: answers.birthdate || null,
                training_experience: answers.training_experience || null,
                peso_maximo: num(answers.peso_maximo),
                peso_minimo: num(answers.peso_minimo),
                peso_mejor_momento: num(answers.peso_mejor_momento),
                dietas_previas: answers.dietas_previas || null,
                // Los días de entreno y la hora ya no se preguntan: van por defecto y se
                // cambian en Preferencias (bloque 5 del doc del 18-08).
                cardio: answers.cardio || null,
                // Bloque 3: lo que hace falta para montarle la rutina. Entrenar AHORA no es
                // lo mismo que llevar años, y lo que NO tiene pesa tanto como lo que tiene.
                entrena_ahora: answers.entrena_ahora || null,
                entrena_ahora_dias: answers.entrena_ahora_dias ?? null,
                maquinas_que_faltan: answers.maquinas_que_faltan || null,
                // Su día tipo (25-10).
                dia_tipo: answers.dia_tipo || null,
                // Pantalla 22: la lesión y sus dos detalles.
                lesion: answers.lesion || null,
                lesion_cual: answers.lesion_cual || null,
                ejercicios_imposibles: answers.ejercicios_imposibles || null,
                // Pantallas 6 y 7: lo médico, que faltaba entero.
                patologia: answers.patologia || null,
                patologia_detalle: answers.patologia_detalle || null,
                medicacion: answers.medicacion || null,
                medicacion_detalle: answers.medicacion_detalle || null,
                // Pantallas 10 y 11: el descanso, con los tramos de Jesús.
                horas_sueno: answers.horas_sueno || null,
                // «¿Y descansas bien?», la que faltaba: va delante de la de la ayuda y es la
                // que dice si el problema es conciliar, mantener o que el sueño no repara.
                descansas_bien: answers.descansas_bien || null,      // la de antes, si la contestó
                sueno_suficiente: answers.sueno_suficiente || null,
                // Pantallas 12, 13 y 14: su suplementación. Sin esto se le pauta a ciegas.
                suplementos_ahora: answers.suplementos_ahora || null,  // la de antes, si la contestó
                para_dormir: answers.para_dormir || null,
                // Si al final dijo «No me pongas nada», lo que hubiera escrito antes no vale.
                suplementos_veto: answers.quiere_pauta_suplementos === 'no' ? null : (answers.suplementos_veto || null),
                quiere_pauta_suplementos: answers.quiere_pauta_suplementos || null,
                // Pantalla 02 del completo: la farmacología entera, con el TRT dentro. Va
                // como LISTA desde el 15-09; lo viejo era un texto y se sigue leyendo.
                farmacologia_uso: farmacologiaUso(),
                aviso_dopaje: answers.aviso_dopaje || null,
                farmacologia_detalle: answers.farmacologia_detalle || null,
                lactosa: answers.lactosa || null,
                gluten: answers.gluten || null,
                alergias: answers.alergias || null,
                // Pantalla 8: no mueve macros, sirve para emparejarlo con casos anteriores.
                trt: answers.trt || null,
                tiempo_intentandolo: answers.tiempo_intentandolo || null,
                motivo_apuntarse: answers.motivo_apuntarse || null,
                // Las dos en que se partio, desde el 8-09.
                por_que_a_mi: answers.por_que_a_mi || null,
                por_que_ahora: answers.por_que_ahora || null,
            });
            toast.success('¡Perfil completo! El equipo ya tiene toda la información.');
            // DIRECTO A MI DÍA (20-10: fuera «Próximos pasos»; 04-11: fuera la bienvenida). La espera tapa el
            // recorrido mientras se refresca la ficha (el largo pasa a completado).
            setSaliendoA(irA);
            try { await refreshProfile(); } catch (e) { /* la app refresca sola al entrar */ }
            navigate(irA);
            return true;
        } catch (e) {
            toast.error(mensajeDeError(e, 'Error al guardar el perfil'));
            return false;
        } finally {
            setLoading(false);
        }
    };
    enviarCompletoRef.current = submitNivel1;

    // LO QUE ESTÁ MAL, DICHO EN SU PANTALLA Y NO AL FINAL.
    //
    // El servidor tiene rangos para el peso, la altura y la grasa, pero solo los comprueba al
    // enviar: quien escribía 80 en la altura contestaba veinte pantallas más y se estrellaba
    // en el botón de calcular con «Revisa el campo height», sin saber a cuál de las veinte
    // tenía que volver. Ahora se dice aquí, con el rango que esperamos, y el botón no deja
    // pasar hasta que cuadre.
    // Los de la ficha salen de `RANGOS_PERFIL` (lib/rangosDelPerfil), que son los del
    // servidor: escritos a mano aquí se separaron de los suyos y abrieron una banda donde
    // la pantalla decía que sí y el envío se caía (9-09).
    const [PESO_MIN, PESO_MAX] = RANGOS_PERFIL.weight;
    const enKilos = `En kilos, entre ${PESO_MIN} y ${PESO_MAX}.`;
    const RANGOS = {
        height: [...RANGOS_PERFIL.height, porQueNoValeElDato('height', 0)],
        weight: [...RANGOS_PERFIL.weight, porQueNoValeElDato('weight', 0)],
        // Los pesos de su historia no son campos del perfil y el servidor no les pone
        // rango: aquí se les pide lo mismo que a un peso, que es lo que son.
        peso_maximo: [PESO_MIN, PESO_MAX, enKilos],
        peso_minimo: [PESO_MIN, PESO_MAX, enKilos],
        peso_mejor_momento: [PESO_MIN, PESO_MAX, enKilos],
    };

    // El aviso de este paso, o null si lo que ha escrito está bien. Se enseña solo cuando ya
    // ha escrito algo: en blanco no hay nada que corregir todavía.
    const avisoDelPaso = (() => {
        // La fecha de nacimiento no tiene rango propio: lo tiene la edad que produce, y es
        // la misma regla del servidor (`validar_fecha_de_nacimiento`). Se dice AQUI, con la
        // fecha delante, y no al guardar veinte pantallas despues.
        if (step.key === 'birthdate') return porQueNoValeLaFecha(answers.birthdate);
        const rango = RANGOS[step.key];
        if (!rango) return null;
        const v = answers[step.key];
        if (v === undefined || v === null || `${v}`.trim() === '') return null;
        const n = parseFloat(String(v).replace(',', '.'));
        const [min, max, texto] = rango;
        return (isNaN(n) || n < min || n > max) ? texto : null;
    })();

    // Validación del paso actual (para inputs de texto/número).
    // ¿ESTÁ VACÍO EL CAMPO DE ESTE PASO? (ficha 02). `inputValid` apaga el botón por dos
    // motivos distintos -- el campo en blanco y el valor que no vale (un email sin arroba,
    // un peso negativo) -- y solo el primero es «te falta un campo por rellenar»; para el
    // segundo ya está `avisoDelPaso`, que dice qué pasa con lo escrito.
    const campoVacio = () => {
        if (!step.key || !step.required) return false;
        const v = answers[step.key];
        return v === undefined || v === null || `${v}`.trim() === '';
    };

    const inputValid = () => {
        if (avisoDelPaso) return false;
        if (!step.key || !step.required) return true;
        const v = answers[step.key];
        if (v === undefined || v === null || `${v}`.trim() === '') return false;
        if (step.type === 'email') return esUnCorreo(v);
        if (step.type === 'number') return !isNaN(parseFloat(v)) && parseFloat(v) > 0;
        return true;
    };

    // Lo que hace falta para poder calcular unos macros que signifiquen algo (punto 12 del
    // doc del 07-08). Antes se podía llegar al botón de calcular sin haber contestado, y la
    // app devolvía unos números como si fueran suyos: los cuatro datos de la tabla salían del
    // perfil y los tres modificadores viajaban vacíos, así que no movían nada.
    //
    // Solo se exige lo que de verdad cambia el número. El resto de preguntas del recorrido
    // (las que sirven para conocerle) se pueden dejar en blanco, que para eso están ahí.
    const OBLIGATORIAS = [
        { key: 'sex', label: 'tu sexo' },
        { key: 'goal', label: 'tu objetivo' },
        { key: 'weight', label: 'tu peso' },
        { key: 'body_fat', label: 'tu porcentaje de grasa' },
        { key: 'actividad_diaria', label: 'tu actividad diaria' },
        { key: 'deporte_extra', label: 'si practicas otro deporte' },
        { key: 'facilidad_engordar', label: 'con qué facilidad engordas' },
    ];

    // Las que este recorrido pregunta de verdad: en el ajuste los cuatro datos de partida ya
    // están en la ficha y no se vuelven a preguntar, así que no se pueden exigir aquí.
    const clavesDelFlujo = new Set(flow.map(s => s.key).filter(Boolean));
    const faltanPorContestar = OBLIGATORIAS.filter(o =>
        clavesDelFlujo.has(o.key) &&
        (answers[o.key] === undefined || answers[o.key] === null || `${answers[o.key]}`.trim() === ''));

    // Selección de una opción de tipo choice → guarda y avanza.
    const pickChoice = (value) => {
        if (step.confirm) {
            // EL «NO» LE DEVUELVE A ELEGIR (Jesús, 11-09, punto 10).
            //
            // Antes le cambiaba el objetivo AL CONTRARIO él solo, sin preguntar nada más:
            // el cliente decía «no» a una confirmación y se encontraba con el plan montado
            // sobre lo opuesto, que no es lo que había pedido en ningún momento. Su
            // arreglo: «que el no le lleve a elegir otra vez, no que le cambie el objetivo
            // solo».
            //
            // Se borra el objetivo y se vuelve a la pregunta: así la pantalla de elegir
            // sale limpia y es él quien decide.
            if (value === 'no') {
                set('goal', null);
                const donde = flow.findIndex(p => p.key === 'goal');
                if (donde >= 0) setIdx(donde);
                return;
            }
            goNext();
            return;
        }
        set(step.key, value);
        trasResponder(step.key, value);
        // Un poco mas de pausa que antes: da tiempo a ver moverse los macros de la cabecera
        // antes de pasar a la siguiente pregunta. Se guarda para poder cancelarlo: si el
        // cliente pulsa Atrás mientras corre, manda el Atrás (punto 4.12).
        pasarSola();
    };

    const confirmOptions = () => {
        const isVol = answers.goal === 'volumen';
        return [
            // «masa muscular» y «perder grasa» en minúscula (24-10).
            { value: 'si', label: isVol ? 'Sí, lo tengo claro, quiero ganar masa muscular (VOLUMEN)' : 'Sí, lo tengo claro, quiero que nos centremos en perder grasa (DEFINICIÓN)' },
            // EL «NO», CON SU TEXTO (documento del 7-09, preguntas 03 y 04). Decía «No,
            // déjame elegir otra vez», que no dice a qué está cambiando. El suyo nombra el
            // otro objetivo, que es lo que hace que se lo piense de verdad. Sigue haciendo
            // lo mismo: devolverle a la pregunta del objetivo, sin elegir por él.
            { value: 'no', label: isVol
                ? 'No, me equivoqué, en realidad quiero perder grasa (DEFINICIÓN)'
                : 'No, me lo he pensado mejor y ahora prefiero centrarme en ganar masa muscular (VOLUMEN) para definir después' },
        ];
    };

    // El titulo, la descripcion y las opciones pueden ser funcion de las respuestas: hay preguntas
    // que se formulan distinto segun el objetivo (definicion o volumen).
    const segunRespuestas = (v) => (typeof v === 'function' ? v(answers) : v);

    // EL TAMAÑO, SEGÚN LO LARGA QUE SEA LA PREGUNTA. Los enunciados de Jesús son de dos y de
    // cuatro líneas -- el de la maquinaria tiene 250 caracteres -- y a tamaño de titular
    // llenaban la pantalla entera: el campo para contestar y el botón se iban por debajo, y
    // el cliente veía un muro de letras sin nada que hacer.
    // LA CEJA DEL TÍTULO (rediseño del 18-10): de qué bloque es la pregunta. Las del
    // principio, que no tienen portada de bloque, la llevan por su clave; el resto, la de la
    // última portada que ha pasado («Tu entrenamiento.» → TU ENTRENAMIENTO).
    const CEJAS = {
        contacto: 'Sobre ti', goal: 'Tu objetivo', _confirm: 'Tu objetivo', tiempo_intentandolo: 'Tu objetivo',
        sex: 'Tu cuerpo', weight: 'Tu cuerpo', height: 'Tu cuerpo', body_fat: 'Tu cuerpo',
        peso_maximo: 'Tu historia', peso_mejor_momento: 'Tu historia',
        ocupacion: 'Tu día a día', actividad_diaria: 'Tu día a día',
        // Los bloques que tenían portada (quitadas el 18-10), con el nombre que llevaba.
        apetito: 'Cómo respondes a la comida',
        facilidad_engordar: 'Cómo respondes a la comida', cuesta_definir: 'Cómo respondes a la comida',
        // Prompt del rediseño (20-10): el biotipo y las medidas son «Tu cuerpo», y la
        // experiencia entrenando abre «Tu entrenamiento».
        biotype_intro: 'Tu cuerpo', biotype: 'Tu cuerpo', partida: 'Tu cuerpo',
        // Las del 24-10: nivel, otros deportes, más detalles, experiencias previas,
        // organiza tu día, perientreno, preferencias y alergias.
        training_experience: 'Nivel de entrenamiento', deporte_extra: 'Otros deportes', deporte_cual: 'Más detalles',
        training_weekdays: 'Entrenamiento actual', entrenador_anterior: 'Experiencias previas',
        entrenador_anterior_que_tal: 'Experiencias previas',
        pref_num_comidas: 'Organiza tu día', pref_momento: 'Organiza tu día',
        pref_perientreno: 'Nutrición perientreno',
        exclusiones: 'Preferencias de alimentos', preferidos: 'Preferencias de alimentos',
        alergias: 'Alergias / Intolerancias', lactosa: 'Alergias / Intolerancias', gluten: 'Alergias / Intolerancias',
        alergia_otra: 'Alergias / Intolerancias',
        como_me_conociste: 'Y para terminar', tiempo_siguiendome: 'Y para terminar',
        por_que_a_mi: 'Y para terminar', por_que_ahora: 'Y para terminar', algo_mas: 'Y para terminar',
        // EL CUESTIONARIO LARGO (20-10, prompt del rediseño, puntos 14 a 32).
        // 25-10: «Medicación» pasa a «Tu salud», el detalle de la patología lleva la suya,
        // «Tu dieta» es «Dieta actual» y «Tu entrenamiento», «Entrenamiento actual».
        patologia: 'Tu salud', patologia_detalle: 'Tu salud', farmacologia_uso: 'Tu salud', farmacologia_detalle: 'Tu salud',
        aviso_dopaje: 'Tu salud',
        horas_sueno: 'Tu descanso', sueno_suficiente: 'Tu descanso', para_dormir: 'Tu descanso',
        quiere_pauta_suplementos: 'Suplementación', suplementos_veto: 'Suplementación',
        sigue_dieta: 'Dieta actual', tiempo_dieta: 'Dieta actual',
        entrena_ahora: 'Entrenamiento actual', entrena_ahora_dias: 'Entrenamiento actual', cardio: 'Entrenamiento actual',
        lesion: 'Entrenamiento actual', lesion_cual: 'Entrenamiento actual',
        ejercicios_imposibles: 'Entrenamiento actual', maquinas_que_faltan: 'Entrenamiento actual',
        fotos_alta: 'Tus fotos',
        medidas_alta: 'Tu cuerpo',
    };
    const cejaDeLaPantalla = (() => {
        if (step.type === 'titulo') return 'Siguiente bloque';
        if (step.ceja) return step.ceja;       // la que trae la propia pantalla (25-10)
        const porClave = CEJAS[step.key] || CEJAS[step.type];
        if (porClave) return porClave;
        for (let i = idx - 1; i >= 0; i--) {
            if (flow[i]?.type === 'titulo') return String(flow[i].title || '').replace(/\.$/, '');
        }
        return null;
    })();
    // «SEGUNDA PARTE» DELANTE, EN TODO EL LARGO (02-11, Francisco). Sin portada desde el 18-10,
    // su primera pregunta tenía la misma cara que las del alta y parecía que el cuestionario
    // volvía a empezar.
    const ceja = estaEnElLargo(idx)
        ? ['Segunda parte', cejaDeLaPantalla].filter(Boolean).join(' · ')
        : cejaDeLaPantalla;

    const Title = () => {
        const texto = segunRespuestas(step.title) || '';
        const tam = texto.length > 160 ? 'text-[24px] md:text-[28px] leading-tight'
            : texto.length > 90 ? 'text-[28px] md:text-[34px] leading-[1.08]'
                : 'text-[36px] md:text-[46px] leading-[1.02]';
        // LO QUE FALTA, ENCIMA DEL PRIMER CAMPO (punto 8): «y que se puedan rellenar ahí
        // mismo». Antes esto era una pantalla suya, que solo informaba y había que pasar.
        const falta = step.loQueFalta;
        return (
            <>
                {falta && (
                    <div className="surface p-3 mb-5" data-testid="lo-que-falta">
                        <p className="font-heading font-bold text-lg text-foreground">{falta.title}</p>
                        {!!falta.lista?.length && (
                            <p className="text-sm font-bold text-foreground mt-1.5">
                                {falta.lista.map(n => `· ${n}`).join('   ')}
                            </p>
                        )}
                    </div>
                )}
                <div className="flex flex-col gap-2.5 mb-7">
                    {ceja && (
                        <span className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#ff6a1f]" data-testid="ceja">{ceja}</span>
                    )}
                    <h2 className={`font-heading font-bold ${tam} text-[#f2f0ed] [text-wrap:balance]`}>{texto}</h2>
                    {/* `whitespace-pre-line`: los subtítulos con varios párrafos (25-10) llevan
                        sus saltos de línea dentro del texto. */}
                    {step.desc && (
                        <p className="text-base leading-normal text-[#a8a29c] whitespace-pre-line">
                            {step.descMas && verMasEn === idx ? step.descMas : segunRespuestas(step.desc)}
                            {step.descMas && verMasEn !== idx && (
                                <>
                                    {' '}
                                    <button type="button" onClick={() => setVerMasEn(idx)} data-testid="ver-mas"
                                        className="font-semibold text-[#f2f0ed] underline underline-offset-[3px] hover:text-brand">
                                        Ver más
                                    </button>
                                </>
                            )}
                        </p>
                    )}
                </div>
            </>
        );
    };

    const hayAtras = idx > 0
        && (volverAlRepasoRef.current != null || pantallaAnterior(flow, idx, visible) >= 0);
    const BackBtn = () => (hayAtras ? (
        <Button variant="ghost" onClick={goBack}
            className="h-[52px] px-4 rounded-[14px] text-[15px] font-bold text-[#a8a29c] hover:text-[#f2f0ed] hover:bg-transparent">
            <ArrowLeft className="w-4 h-4 mr-1" /> Atrás
        </Button>
    ) : null);

    // Props comunes de los inputs de pasos compuestos (dieta, pesos, salud).
    const mini = { answers, set };

    let body;
    if (step.type === 'statement' && idx === 0 && !retomandoNivel1) {
        // Portada: logo de marca grande + flecha, estilo Typeform.
        body = (
            <div className="text-center">
                {/* SIN EL SEGUNDO 12EN12 (Jesús, 11-09, punto 8): «fuera el 12EN12 de
                    encima». La cabecera de la pantalla ya lo lleva unos píxeles más arriba,
                    así que aquí salía la marca dos veces seguidas. */}
                <h2 className="font-heading font-bold text-4xl md:text-5xl uppercase tracking-tight text-foreground mb-3">{step.title}</h2>
                {step.desc && <p className="text-foreground/60 mb-3 text-base max-w-md mx-auto">{segunRespuestas(step.desc)}</p>}
                <ListaDeHuecos step={step} />
                <div className="mb-10" />
                <Button onClick={goNext}
                    className={CTA}>
                    {/* w-4: la flecha ya no trae aire dentro, así que con el tamaño de antes
                        se veía más grande que el texto del botón. */}
                    Empezar <BrandArrow className="w-4 h-4 ml-2" />
                </Button>
            </div>
        );
    } else if (step.type === 'statement' || step.type === 'titulo' || step.type === 'aviso_salud') {
        // `aviso_salud` (25-10): el «En breve nos pondremos en contacto contigo...» detrás de
        // «Sí, me interesa». Solo el texto, «Atrás» y «Continuar».
        body = (
            <div>
                {/* Las portadas de bloque (doc 23-08) llevan una ceja que dice qué son:
                    sin ella, un título suelto parece una pregunta a la que le falta algo.
                    Desde el 18-10 la pone `Title`, que es donde va la de todas. */}
                <Title />
                {/* Si la portada de los huecos no cae la primera (por ejemplo al volver
                    atrás), se pinta aquí con la misma lista. */}
                <ListaDeHuecos step={step} />
                <div className="flex gap-3">
                    <BackBtn />
                    <Button onClick={goNext} data-testid={step.type === 'titulo' ? 'titulo-continuar' : undefined}
                        className={CTA}>
                        {step.cta || 'Continuar'} <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            </div>
        );
    } else if (step.type === 'final1' && !revision) {
        // Se envía solo al llegar (ver `envioDelCompleto`): mientras, la espera; si falla,
        // el aviso con «Atrás» y «Reintentar».
        body = envioDelCompleto === 'fallo' ? (
            <div data-testid="envio-completo-fallo">
                <p className="text-base text-[#a8a29c] mb-6">
                    No hemos podido enviar tus respuestas. Están guardadas: vuelve a intentarlo.
                </p>
                <div className="flex items-center gap-3 flex-wrap">
                    <BackBtn />
                    <Button disabled={loading} data-testid="envio-completo-reintentar"
                        onClick={async () => {
                            setEnvioDelCompleto('enviando');
                            if (!(await submitNivel1())) setEnvioDelCompleto('fallo');
                        }}
                        className={CTA}>
                        Reintentar
                    </Button>
                </div>
            </div>
        ) : (
            <div className="flex justify-center py-16" data-testid="envio-completo">
                <Loader2 className="w-8 h-8 animate-spin text-brand" />
            </div>
        );
    } else if (step.type === 'final0' || step.type === 'final1') {
        const isN0 = step.type === 'final0';
        // Sin las respuestas que mueven el número no hay nada que calcular: el botón espera y
        // se dice cuáles faltan, en vez de devolver unos macros que no son de nadie.
        const bloqueado = isN0 && faltanPorContestar.length > 0;
        // LA REJILLA DEL REPASO (doc del 23-08, punto 16): lo contestado, tarjeta a
        // tarjeta; tocar una lleva a su pregunta y contestar devuelve aquí. Solo salen
        // las que su recorrido de verdad preguntó (en mujer no hay biotipo, por ejemplo).
        const cap = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
        const etiquetaDe = (paso, valor) => {
            const opts = segunRespuestas(paso?.options) || [];
            const o = opts.find(x => x.value === valor);
            // La etiqueta corta: lo de antes de los dos puntos («Muy sedentario: paso...»).
            return o ? String(o.label).split(':')[0] : null;
        };
        const conAno = (v, ano) => (v == null || v === '') ? null : `${v} kg${ano ? ` (${ano})` : ''}`;
        // La edad que sale de su fecha de nacimiento, que es lo que de verdad se repasa: una
        // fecha suelta no dice nada y «0 años» canta a la primera.
        const laEdad = (() => {
            if (!answers.birthdate) return null;
            const b = new Date(`${String(answers.birthdate).slice(0, 10)}T12:00:00`);
            if (isNaN(b)) return null;
            const h = ahoraDeLaApp();
            const cumplioYa = h.getMonth() > b.getMonth()
                || (h.getMonth() === b.getMonth() && h.getDate() >= b.getDate());
            const anos = h.getFullYear() - b.getFullYear() - (cumplioYa ? 0 : 1);
            return `${anos} años`;
        })();
        const REPASO = [
            // LA FECHA DE NACIMIENTO, EN EL REPASO. No estaba, y por eso el que venía a
            // corregir su edad se encontraba «Revisa tus datos» CON LA REJILLA VACÍA: ni una
            // tarjeta, solo Atrás y Continuar (Francisco, 26-08). El repaso solo enseña los
            // pasos que están en el recorrido, y en «completar» el recorrido puede ser una
            // sola pantalla -- justo esta --, que no tenía tarjeta que enseñar.
            ['contacto', 'Edad', laEdad],
            ['goal', 'Objetivo', answers.goal === 'volumen' ? 'Ganar masa muscular'
                : answers.goal === 'definicion' ? 'Perder grasa'
                : answers.goal === 'tonificacion' ? 'Tonificar' : null],
            ['weight', 'Peso', answers.weight ? `${answers.weight} kg` : null],
            ['height', 'Altura', answers.height ? `${answers.height} cm` : null],
            ['body_fat', 'Grasa', answers.body_fat != null ? `${answers.body_fat} %` : null],
            ['actividad_diaria', 'Actividad', etiquetaDe(q('actividad_diaria'), answers.actividad_diaria)],
            ['biotype', 'Biotipo', (BIOTIPOS.find(b => b.value === answers.biotype)?.label || '').split(' (')[0] || null],
            ['peso_maximo', 'Tu máximo', conAno(answers.peso_maximo, answers.peso_maximo_ano)],
            ['peso_mejor_momento', 'Tu mejor forma', conAno(answers.peso_mejor_momento, answers.peso_mejor_momento_ano)],
            ['training_weekdays', 'Cuándo entrenas',
                Array.isArray(answers.training_weekdays) && answers.training_weekdays.length
                    // Con sus tildes: los valores van sin ellas a propósito (backend), pero
                    // aquí se enseñan las etiquetas de la pregunta.
                    ? answers.training_weekdays
                        .map(v => ({ miercoles: 'Miércoles', sabado: 'Sábado' }[v] || cap(v)))
                        .join(', ')
                    : null],
        ];
        const irACorregir = (clave) => {
            const destino = flow.findIndex(s => s.key === clave || s.type === clave);
            if (destino < 0) return;
            volverAlRepasoRef.current = idx;
            setIdx(destino);
        };
        // «Su recorrido de verdad»: el paso tiene que existir Y aplicarle (en mujer el
        // biotipo está en la lista pero su condición lo apaga; enseñarle esa tarjeta
        // «Sin contestar» es enseñarle un hueco que no puede rellenar).
        const enElFlujo = (clave) => flow.some(s => (s.key === clave || s.type === clave) && visible(s));
        body = (
            <div>
                <Title />
                {isN0 && (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-6" data-testid="repaso-respuestas">
                        {REPASO.filter(([clave]) => enElFlujo(clave)).map(([clave, rotulo, valor]) => (
                            <button key={clave} type="button" onClick={() => irACorregir(clave)}
                                data-testid={`repaso-${clave}`}
                                className="text-left rounded-xl border-2 border-[#222222] bg-card px-3.5 py-3 hover:border-brand/60 transition-all">
                                <p className="text-[10px] uppercase tracking-wider text-foreground/40 font-bold">{rotulo}</p>
                                <p className={`text-sm font-semibold mt-0.5 ${valor ? 'text-foreground' : 'text-foreground/35'}`}>
                                    {valor || 'Sin contestar'}
                                </p>
                            </button>
                        ))}
                    </div>
                )}
                {bloqueado && (
                    <p className="text-sm text-amber-500 mb-5" data-testid="faltan-obligatorias">
                        Antes de calcular falta que nos digas {faltanPorContestar.map(o => o.label).join(', ')}.
                        Toca la tarjeta y complétalo.
                    </p>
                )}
                <div className="flex gap-3">
                    <BackBtn />
                    <Button onClick={isN0 ? submitNivel0 : submitNivel1} disabled={loading || bloqueado}
                        data-testid="calcular-macros-btn"
                        className={CTA}>
                        {loading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Check className="w-5 h-5 mr-2" />}
                        {loading ? 'Enviando...' : isN0 ? 'Confirmar' : 'Enviar'}
                    </Button>
                </div>
            </div>
        );
    } else if (step.type === 'result') {
        // Los 8 números del motor v2 + desglose explicable.
        //
        // Y SI EL SERVIDOR NO LOS RECALCULA, LOS QUE YA TIENE (Francisco, 23-09: «el
        // cuestionario anuncia los macros pero no los muestra; los muestra en la pantalla
        // siguiente»). Al que ya tenía macros puestos por su entrenador el ajuste le contesta
        // 403 -- bien, no se le pisan --, `resultado` llega vacío y la pantalla decía «Estos
        // son tus macros» sin un solo número. Se pintan los de su ficha, que son los que va a
        // ver en el panel un segundo después.
        const deLaFicha = (x) => x && (x.protein ?? x.proteinas) != null
            ? { proteina: Math.round(x.protein ?? x.proteinas), hidratos: Math.round(x.carbs ?? x.hidratos),
                grasa: Math.round(x.fat ?? x.grasas) }
            : null;
        const mFicha = (() => {
            const e = deLaFicha(profile?.macros_training);
            const d = deLaFicha(profile?.macros_rest);
            if (!e || !d) return null;
            const pe = profile?.macros_periworkout;
            const peri = pe && (pe.protein ?? pe.proteinas) != null
                ? { proteina: Math.round(pe.protein ?? pe.proteinas), hidratos: Math.round(pe.carbs ?? pe.hidratos) }
                : null;
            return { entreno: e, descanso: d, perientreno: peri };
        })();
        const mCrudo = resultado?.macros || mFicha;
        // «NO QUIERO PERIENTRENO» Y LA PANTALLA LE ENSEÑABA UNA TARJETA «PERIENTRENO» (12-10).
        // El cálculo es el mismo para todos: esos gramos son parte de su día de entreno, y a
        // quien no hace intra ni post se le reparten entre sus comidas (macro_distribution,
        // modo `sin_peri`). Lo que estaba mal era pintárselo como una toma aparte que acaba
        // de decir que no quiere: se le da el día de entreno entero, que es lo que va a ver
        // en Inicio y en Nutrición, y se le dice en una línea dónde van esos gramos.
        // Y AL RECARGAR TAMBIÉN (30-10): las respuestas se pierden y la ficha no lleva este
        // dato. Lo guardado está en la configuración de la dieta, que esta pantalla ya lee.
        const sinPeri = quiereSinPeri(answers, dietConfig);
        const periRepartido = sinPeri && mCrudo?.perientreno ? mCrudo.perientreno : null;
        const m = periRepartido ? {
            ...mCrudo,
            entreno: { ...mCrudo.entreno,
                proteina: Math.round((Number(mCrudo.entreno.proteina) || 0) + (Number(periRepartido.proteina) || 0)),
                hidratos: Math.round((Number(mCrudo.entreno.hidratos) || 0) + (Number(periRepartido.hidratos) || 0)) },
            perientreno: null,
        } : mCrudo;
        // CON ENTRENADOR Y EL LARGO DETRÁS: EL DISEÑO DEL PANTALLAZO DEL 25-10. Sus macros
        // provisionales en tres tarjetas y dos salidas: probar ya la app (el Inicio le
        // recordará lo que falta, «Completa tu perfil...») o seguir con la segunda parte, que
        // empieza por la salud.
        const conSegundaParte = tieneCoach && flow.slice(idx + 1).some(s => STEPS_NIVEL1.includes(s));
        // SIN ENTRENADOR, EN EL ALTA: LA OFERTA EN ESTA MISMA PANTALLA (26-10). Es la última
        // del alta (`elCierre` vacío). El mismo diseño que la de arriba, con su texto y dos
        // salidas: quedarse con su plan (entra en la app) o el ajuste a medida (Stripe; si
        // paga, sigue por el cuestionario largo). Solo en el alta: el «Ajustar macros» de
        // después y el completar-huecos siguen con su pantalla de siempre.
        const ofertaEnLosMacros = !tieneCoach && !modoAjuste && !pidioCompletar
            && (enElCierre || revision === 'alta');
        const probarLaApp = async () => {
            await terminarElCierre();
            navigate('/dashboard');
        };
        // EL NOMBRE ENCIMA DEL NÚMERO (04-11, prueba 5.4): al lado, en la tarjeta estrecha del
        // ordenador, «Hidratos de carbono» partía en dos líneas. Una raya entre filas y otra
        // debajo de la última.
        // EL NOMBRE, EN EL COLOR DE SU MACRO (04-11, prueba 5.5): los mismos de Mi día
        // (`COLOR_MACRO`): proteína roja, hidratos amarillos y grasa turquesa.
        const COLOR_DE = { 'Proteínas': COLOR_MACRO.P, 'Hidratos de carbono': COLOR_MACRO.H, 'Grasas': COLOR_MACRO.G };
        const Fila = ({ nombre, valor }) => (
            <div className="flex flex-col gap-1.5 py-4 border-t border-[#2a2a2a] last:border-b">
                <span className="text-[15px] font-semibold whitespace-nowrap" style={{ color: COLOR_DE[nombre] || '#d9d5d0' }}>{nombre}</span>
                <span className="font-heading font-extrabold text-[44px] leading-none text-[#ff6a1f] tabular-nums">
                    {valor ?? '—'}<span className="text-base text-[#8a8580] ml-1">g</span>
                </span>
            </div>
        );
        // Al retomar, `diaMontado` trae solo los nombres del cierre: las comidas, de lo guardado.
        const menusDeHoy = menusDelDia(diaMontado?.comidas ? diaMontado : diaGuardado);
        // MACROS Y MENÚS, CADA UNO EN SU PANTALLA (31-10). Cambiar de una a otra sube arriba:
        // el botón queda a media página en el móvil.
        const cambiarDePantalla = (verMenus) => {
            setViendoMenus(verMenus);
            window.scrollTo({ top: 0 });
        };
        const CAMBIO = 'w-full sm:w-auto mt-4 h-[52px] rounded-[14px] px-[26px] border-[1.5px] border-[#ff6a1f] '
            + 'bg-[#ff6a1f]/10 text-[#f2f0ed] text-base font-bold hover:bg-[#ff6a1f]/20 hover:text-[#f2f0ed]';
        // Las dos salidas del que lleva entrenador, iguales en las dos pantallas.
        const salidasConEntrenador = (
            <div className="flex flex-col sm:flex-row gap-3">
                {/* Seguir con el cuestionario, el principal (25-10); probar la app, el
                    secundario. Cada uno hace lo de siempre. */}
                <Button onClick={() => { setViendoMenus(false); goNext(); }} data-testid="continuar-cuestionario" className={CTA}>
                    Continuar con el cuestionario <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button variant="outline" onClick={probarLaApp} data-testid="probar-la-app"
                    className="h-[52px] rounded-[14px] px-[26px] border-[1.5px] border-[#3a3a3a] bg-transparent text-[#f2f0ed] text-base font-bold hover:bg-[#161616] hover:text-[#f2f0ed]">
                    Probar la aplicación
                </Button>
            </div>
        );
        if ((conSegundaParte || ofertaEnLosMacros) && viendoMenus) {
            body = (
                <div data-testid="menus-de-hoy">
                    <h2 className="font-heading font-extrabold uppercase text-[36px] md:text-[52px] leading-[1.02] text-[#f2f0ed] [text-wrap:balance] mb-6">
                        Tus menús de hoy
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                        {menusDeHoy.map(c => (
                            <div key={c.clave} className="rounded-[14px] border border-[#2a2a2a] bg-[#1a1a1a] px-4 py-3.5">
                                <p className="text-[11px] font-extrabold uppercase tracking-[0.1em] text-[#a8a29c] mb-1">{c.nombre}</p>
                                <p className="text-[15px] leading-snug font-semibold text-[#f2f0ed]">{c.alimentos}</p>
                            </div>
                        ))}
                    </div>
                    <div>
                        <Button variant="outline" onClick={() => cambiarDePantalla(false)}
                            data-testid="volver-a-mis-macros" className={CAMBIO}>
                            Volver a mis macros
                        </Button>
                    </div>
                    {conSegundaParte && (
                        <div className="border-t border-[#2a2a2a] mt-7 pt-7">{salidasConEntrenador}</div>
                    )}
                </div>
            );
        } else if (conSegundaParte || ofertaEnLosMacros) {
            const tarjetas = m ? [
                { titulo: 'Día de entreno', filas: [['Proteínas', m.entreno.proteina], ['Hidratos de carbono', m.entreno.hidratos], ['Grasas', m.entreno.grasa]] },
                ...(m.perientreno ? [{ titulo: 'Perientreno', filas: [['Proteínas', m.perientreno.proteina], ['Hidratos de carbono', m.perientreno.hidratos]] }] : []),
                { titulo: 'Día de descanso', filas: [['Proteínas', m.descanso.proteina], ['Hidratos de carbono', m.descanso.hidratos], ['Grasas', m.descanso.grasa]] },
            ] : [];
            body = (
                <div data-testid="primeros-macros">
                    <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-[#ff6a1f] mb-3">Y ya estaría</p>
                    <h2 className="font-heading font-extrabold uppercase text-[36px] md:text-[52px] leading-[1.02] text-[#f2f0ed] [text-wrap:balance] mb-3">
                        Estos son tus primeros macros
                    </h2>
                    {conSegundaParte ? (
                        <p className="text-base leading-normal text-[#a8a29c] max-w-[620px] mb-7">
                            Son provisionales, hasta que revisemos tu formulario completo y te hagamos
                            tu plan de macros 100% a medida.
                        </p>
                    ) : <div className="mb-7" />}
                    {m ? (
                        <div className={`grid grid-cols-1 gap-3 ${tarjetas.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                            {tarjetas.map(t => (
                                <div key={t.titulo} className="rounded-[18px] border border-[#2a2a2a] bg-[#1a1a1a] px-6 pt-6 pb-6">
                                    <p className="font-heading font-bold uppercase text-lg tracking-[0.02em] text-[#f2f0ed] pb-4">{t.titulo}</p>
                                    {t.filas.map(([nombre, valor]) => <Fila key={nombre} nombre={nombre} valor={valor} />)}
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-foreground/60">Tus macros se han guardado y los verás en tu panel.</p>
                    )}
                    {periRepartido && (
                        <p className="text-xs text-foreground/60 mt-3" data-testid="resultado-sin-peri">
                            Como no haces perientreno, lo que iría en él ({periRepartido.proteina} g de
                            proteína y {periRepartido.hidratos} g de hidratos) va repartido en las
                            comidas de tu día de entreno.
                        </p>
                    )}
                    {menusDeHoy.length > 0 && (
                        <div>
                            <Button variant="outline" onClick={() => cambiarDePantalla(true)}
                                data-testid="ver-menus-de-hoy" className={CAMBIO}>
                                Ver mis menús de hoy
                            </Button>
                        </div>
                    )}
                    {ofertaEnLosMacros ? (
                    <div className="border-t border-[#2a2a2a] mt-8 pt-7" data-testid="oferta-en-los-macros">
                        {resultado?.revision?.requiere_revision && (
                            <p className="text-xs text-amber-500 font-medium mb-5">
                                Lo que comes ahora no cuadra con lo esperado para tu % graso: el equipo lo revisará.
                            </p>
                        )}
                        {/* EL TEXTO DE JESÚS (26-10), tal cual. Resaltados «IMPORTANTE» y el
                            precio. */}
                        <div className="space-y-4 text-base leading-relaxed text-[#d9d5d0] max-w-[620px] mb-7">
                            <p>
                                <strong className="font-extrabold text-[#ff6a1f]">IMPORTANTE:</strong>{' '}
                                estos macros no son personalizados 100% (eso sería otro plan con otras
                                condiciones).
                            </p>
                            <p>
                                Es una sugerencia en base a tus respuestas y tomando como referencia la
                                evolución de otros perfiles parecidos al tuyo con los que ya hemos aplicado
                                el método antes.
                            </p>
                            <p>
                                Todos los meses recibirás un informe basado en los datos que vayas
                                registrando en la aplicación y podrás solicitar una nueva revisión para que
                                los vayamos ajustando según tu propia evolución.
                            </p>
                            <p>
                                A más datos registres y más tiempo estés, mejores sugerencias podrá
                                ofrecerte la aplicación, que irá “aprendiendo” de lo que mejor te funciona.
                            </p>
                            <p>
                                Ahora puedes empezar a usar la Calculadora con estos macros adaptados a tu
                                perfil o puedes solicitar tus macros personalizados y recibir un ajuste a
                                medida. En ese caso, necesitaríamos más información para entender mejor tu
                                contexto, además de tus fotos y tus perímetros corporales.
                            </p>
                            <p className="text-[15px] text-[#a8a29c]">
                                (Esta segunda opción no está incluida en tu plan actual, tiene un coste
                                adicional de{' '}
                                <strong className="font-extrabold text-[#ff6a1f]">87 euros</strong>{' '}
                                e incluye como extra tu plan personalizado de suplementación)
                            </p>
                        </div>
                        {/* Dos salidas, una sola elección: cada botón actúa al pulsarlo. */}
                        <div className="flex flex-col gap-3 max-w-[620px]">
                            <button type="button" data-testid="oferta-no" disabled={loading}
                                onClick={() => responderOferta(false)}
                                className="w-full text-left rounded-[14px] border-[1.5px] border-[#3a3a3a] bg-[#1a1a1a] px-5 py-4 text-base font-bold text-[#f2f0ed] transition-colors hover:border-[#ff6a1f]/60 hover:bg-[#161616] disabled:opacity-50">
                                Me vale con la primera opción, la que está incluida en mi plan
                            </button>
                            <button type="button" data-testid="oferta-si" disabled={loading}
                                onClick={() => responderOferta(true)}
                                className="w-full text-left rounded-[14px] border-[1.5px] border-[#ff6a1f] bg-[#ff6a1f]/10 px-5 py-4 text-base font-bold text-[#f2f0ed] transition-colors hover:bg-[#ff6a1f]/20 disabled:opacity-50 inline-flex items-center justify-between gap-3">
                                <span>Quiero el ajuste a medida + programa de suplementación</span>
                                {loading ? <Loader2 className="w-5 h-5 animate-spin text-[#ff6a1f] flex-none" />
                                    : <ArrowRight className="w-5 h-5 text-[#ff6a1f] flex-none" />}
                            </button>
                        </div>
                    </div>
                    ) : (
                    <div className="border-t border-[#2a2a2a] mt-8 pt-7">
                        <p className="text-base leading-relaxed text-[#d9d5d0] max-w-[620px] mb-6">
                            Hasta entonces puedes empezar a probar la aplicación o puedes continuar con
                            la segunda parte del cuestionario. Para esto último vas a necesitar que te
                            ayude alguien, que medirse y hacerse fotos uno mismo es complicado.
                        </p>
                        {salidasConEntrenador}
                    </div>
                    )}
                </div>
            );
        } else body = (
            <div>
                {/* Ya no hay macros "provisionales" que decir: desde que el alta es un solo
                    recorrido (punto 15 del doc del 07-08), lo que se calcula aquí ya lleva
                    dentro las respuestas que mueven los hidratos. Así que el mensaje solo
                    depende de si hay un entrenador detrás que los vaya a repasar. */}
                {/* LOS TEXTOS DEL DOC DEL 23-08 (punto 17) para quien no lleva entrenador:
                    «Y ya estaría» de ceja, «iniciales» en el título -- van a mejorar con el
                    tiempo, y se le dice --, y la próxima revisión con sus semanas y su
                    fecha. Al del plan con entrenador se le mantiene su cierre (el aviso de
                    que son provisionales va debajo de los números, punto 4.1). */}
                {!entrega?.con_entrenador && (
                    <p className="text-[11px] uppercase tracking-[0.2em] text-brand font-bold mb-2">Y ya estaría</p>
                )}
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-2 leading-tight">
                    {/* Al del plan con entrenador, el título del doc del 23-08 (punto 23):
                        «primeros», porque los definitivos se los manda su equipo. */}
                    {entrega?.con_entrenador ? 'Ya tienes tus primeros macros' : 'Estos son tus macros iniciales'}
                </h2>
                {!entrega?.con_entrenador && entrega?.proxima_revision && (
                    <p className="mb-4 text-sm text-foreground font-semibold" data-testid="proxima-revision">
                        {`Próxima revisión${entrega?.revision_en_dias ? ` en ${Math.round(entrega.revision_en_dias / 7)} semana${Math.round(entrega.revision_en_dias / 7) === 1 ? '' : 's'}` : ''}: ${entrega.proxima_revision}.`}
                    </p>
                )}
                {m ? (
                    /* Punto 13 del doc del 07-08: los números tienen que verse enteros sin
                       mover nada. Es el momento más importante del alta y antes había que
                       bajar para llegar a ellos, así que todo lo de esta pantalla va apretado
                       (títulos más pequeños, menos aire entre bloques) para que las tres
                       tarjetas y los botones entren de una vez. */
                    <div className="space-y-3">
                        <div className={`grid ${m.perientreno ? 'grid-cols-3' : 'grid-cols-2'} gap-2 sm:gap-3 text-center`}>
                            {[
                                ['Día de entreno', m.entreno.proteina, m.entreno.hidratos, m.entreno.grasa],
                                ...(m.perientreno ? [['Perientreno', m.perientreno.proteina, m.perientreno.hidratos, null]] : []),
                                ['Día de descanso', m.descanso.proteina, m.descanso.hidratos, m.descanso.grasa],
                            ].map(([lbl, p, h, g]) => (
                                <div key={lbl} className="rounded-xl border-2 border-[#222222] bg-card py-3 px-2">
                                    <p className="text-[10px] sm:text-[11px] text-foreground/50 uppercase font-bold mb-1.5 leading-tight">{lbl}</p>
                                    <p className="font-heading font-extrabold text-xl sm:text-2xl text-brand">{p}<span className="text-foreground/40 text-sm sm:text-base">P</span></p>
                                    <p className="font-heading font-extrabold text-xl sm:text-2xl text-brand">{h}<span className="text-foreground/40 text-sm sm:text-base">H</span></p>
                                    {g != null && <p className="font-heading font-extrabold text-xl sm:text-2xl text-brand">{g}<span className="text-foreground/40 text-sm sm:text-base">G</span></p>}
                                </div>
                            ))}
                        </div>
                        {periRepartido && (
                            <p className="text-xs text-foreground/60" data-testid="resultado-sin-peri">
                                Como no haces perientreno, lo que iría en él ({periRepartido.proteina} g de
                                proteína y {periRepartido.hidratos} g de hidratos) va repartido en las
                                comidas de tu día de entreno.
                            </p>
                        )}
                        {/* Las etiquetas de colores del desglose («Otro deporte +20 %»,
                            «Engordas enseguida: sin subidas»...) SE VAN de esta pantalla
                            (doc del 23-08, punto 17: «sobra»). El desglose sigue vivo en
                            la calculadora de macros, que es pantalla de trabajo. */}
                        {resultado?.revision?.requiere_revision && (
                            <p className="text-xs text-amber-500 font-medium">
                                Lo que comes ahora no cuadra con lo esperado para tu % graso: el equipo lo revisará.
                            </p>
                        )}
                    </div>
                ) : (
                    <p className="text-foreground/60">Tus macros se han guardado y los verás en tu panel.</p>
                )}

                {/* MOMENTO 1 de la revisión suelta (documento del 06-08-2026): al recibir sus
                    macros de inicio. Línea pequeña y sin botón, nunca un popup: si interrumpe,
                    es publicidad aunque el texto sea suave. */}
                {modoAjuste && !entrega?.con_entrenador && seLeOfreceLaRevision(profile, can) && (
                    <p className="text-xs text-foreground/50 mt-4" data-testid="revision-partida">
                        Si consideras que tu caso necesita una revisión más profunda, puedes{' '}
                        <button onClick={() => navigate('/dashboard/revision')}
                            className="underline text-brand hover:text-brand/80 font-medium">
                            solicitar tu revisión de partida
                        </button>.
                    </p>
                )}

                {/* «Y TU DÍA DE HOY YA ESTÁ CREADO» SE QUITA DE AQUÍ (18-10). El día se sigue
                    montando y guardando al enviar (`diaMontado`); solo deja de enseñarse en esta
                    pantalla. */}

                {/* Un solo botón: el recorrido continúa donde estaba. Antes, al venir del
                    alta, aquí se le mandaba a un SEGUNDO cuestionario ("Ajustar mis macros");
                    ya no hay segundo cuestionario que ofrecer. */}
                <div className="flex flex-col sm:flex-row gap-3 mt-5">
                    <Button onClick={goNext}
                        data-testid="resultado-continuar"
                        className={CTA}>
                        Continuar <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            </div>
        );
    } else if (step.type === 'momento') {
        // Opciones adaptadas al nº de comidas elegido (coloca el perientreno).
        const n = answers.pref_num_comidas || 4;
        const opciones = [{ value: 0, label: 'En ayunas' }];
        for (let i = 1; i <= Math.min(3, n - 1); i++) {
            opciones.push({ value: i, label: `Después de la comida ${i}` });
        }
        body = (
            <div>
                <Title />
                <div className="flex flex-col gap-3">
                    {opciones.map(o => (
                        <TarjetaOpcion key={o.value} selected={answers[step.key] === o.value} onClick={() => pickChoice(o.value)}>
                            <EtiquetaDeOpcion label={o.label} />
                        </TarjetaOpcion>
                    ))}
                </div>
                <div className="flex items-center gap-3 flex-wrap mt-7"><BackBtn /><PistaAutomatica /></div>
            </div>
        );
    } else if (step.type === 'prefs') {
        // Selector completo de alimentos (mostrar + evitar): mismo componente que
        // "Mis preferencias" de Nutrición; al guardar, sigue el flujo.
        // DENTRO DEL MARCO (hallazgo del mapeo del 23-08): esto hacía `return` a secas y
        // era la única pantalla del alta sin barra de progreso ni logo, como si el
        // cliente se hubiera salido del cuestionario a mitad.
        // Con «Atrás» debajo, como todas (20-10, prompt del rediseño).
        body = (
            <div>
                {/* CON LO QUE YA ELIGIÓ EN EL ALTA (24-10). Arrancaba en blanco, y al guardar
                    pisaba lo que acababa de elegir en las dos pantallas de preferencias: las
                    exclusiones se perdían y los preferidos volvían a los de por defecto. */}
                <PreferencesSetup
                    api={fetchApi}
                    initialPreferences={prefsDelAlta.preferencias}
                    initialAvoidedCategories={prefsDelAlta.categorias}
                    initialAvoidedKeywords={prefsDelAlta.palabras}
                    preferredFoods={prefsDelAlta.sueltos}
                    onSave={() => goNext()}
                />
                <div className="mt-6"><BackBtn /></div>
            </div>
        );
    } else if (step.type === 'magia') {
        // Momento mágico: comida real, suya, que ya cuadra. Cierra con comida,
        // no con un tutorial.
        // ÚLTIMA ES LA ÚLTIMA, no «la del que no lleva entrenador» (18-08). Esto estaba
        // atado al plan, y desde que el final se parte por plan al que no lleva entrenador
        // le quedan dos pantallas detrás -- las fotos y la oferta --, así que el botón decía
        // «Ir a mi panel» y se las saltaba las dos. Se vio recorriendo el alta entera con una
        // cuenta de dev; por API no se ve, porque el recorrido lo compone la pantalla.
        const esUltimo = idx >= flow.length - 1;
        // «Sin el CUADRA CONTIGO en verde: eso fuera» (doc 23-08). Y los menús ya no son
        // recetas sueltas: son mañana y pasado, escritos, con sus comidas.
        body = (
            <div>
                <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground mb-2 leading-tight">
                    Ahora, una muestra de lo que podrías comer con estos macros
                </h2>
                {diasArranque === null ? (
                    <div className="flex justify-center py-10">
                        <div className="animate-spin rounded-full h-9 w-9 border-4 border-brand border-t-transparent" />
                    </div>
                ) : diasArranque.length === 0 ? (
                    <p className="text-foreground/60 text-sm mb-4">
                        El recetario te espera en <span className="font-bold text-foreground">Nutrición</span>:
                        en cada comida, pulsa "Sugiéreme un menú" y elige la receta que te apetezca.
                    </p>
                ) : (
                    <div className="space-y-3 mb-2 max-h-[46vh] overflow-y-auto pr-1" data-testid="menus-arranque">
                        {diasArranque.map((dia, i) => (
                            <div key={dia.fecha} className="rounded-xl border-2 border-[#222222] bg-card p-4">
                                <div className="flex items-baseline justify-between gap-2 mb-2">
                                    <p className="text-base font-black text-foreground">
                                        Menú {i + 1} · {i === 0 ? 'mañana' : 'pasado mañana'}
                                    </p>
                                    <span className="text-[10px] uppercase tracking-wider text-foreground/40 font-bold">
                                        {dia.tipo_dia === 'descanso' ? 'Día de descanso' : 'Día de entreno'}
                                    </span>
                                </div>
                                <div className="space-y-2">
                                    {menusDelDia(dia).map(c => (
                                        <div key={c.clave}>
                                            <p className="text-sm text-foreground">
                                                <span className="text-foreground/40 font-semibold">{c.nombre}</span>
                                                {c.menuNombre && <span className="font-semibold"> · {c.menuNombre}</span>}
                                            </p>
                                            <p className="text-xs text-foreground/60">{c.alimentos}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                {/* «Aceptar» en vez de «Empezar con el menú 1» (18-10): solo cambia el texto; el
                    botón hace lo de siempre. */}
                <div className="flex items-center gap-3 flex-wrap mt-7">
                    <BackBtn />
                    {esUltimo ? (
                        <Button onClick={salirDelAlta} data-testid="empezar-menu-1"
                            className={CTA}>
                            Aceptar <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                    ) : (
                        <Button onClick={goNext} data-testid="empezar-menu-1"
                            className={CTA}>
                            {diasArranque?.length ? 'Aceptar' : 'Continuar'} <ArrowRight className="w-5 h-5 ml-2" />
                        </Button>
                    )}
                </div>
            </div>
        );
    } else if (step.type === 'contacto') {
        // PANTALLA 1 DEL BÁSICO: cinco campos juntos, que es la excepción que hace el
        // propio documento a lo de una pregunta por pantalla.
        //
        // EL NOMBRE SALE SIEMPRE (Francisco, 15-09). Se escondía a quien ya lo tenía en la
        // cuenta, y entonces la pantalla prometía «cinco datos» y enseñaba cuatro. Sale con
        // el nombre que ya tenemos escrito dentro, que es lo que hace falta: la mitad de los
        // que vienen del pago figuran con el nombre a medias o en minúsculas, y este es el
        // único momento en que pueden corregirlo.
        //
        // Y EL SEXO, IGUAL. Escondiéndolo pasaba lo mismo por el otro lado: al que ya lo
        // tenía apuntado le salían cuatro datos, no cinco. Sale con el suyo ya marcado.
        // Son los CINCO del documento, siempre los cinco, y el que no tenga nada que
        // cambiar pasa de largo en dos segundos.
        // Lo que falta, contado (ficha 02): los cinco campos son obligatorios, y el botón
        // apagado tiene que decir cuántos quedan en vez de quedarse gris sin más.
        const porRellenar = [
            !`${answers.name ?? ''}`.trim(),
            !answers.sex,
            // UNA FECHA IMPOSIBLE CUENTA COMO SIN RELLENAR (Jesus, 9-09). Esta pantalla es
            // justo donde se viene a corregir la edad cuando la guardada no puede ser, y con
            // el campo dandose por bueno el boton se encendia: el cliente seguia sin tocarla
            // y se estrellaba al guardar, veinte pantallas despues y sin poder volver.
            !fechaDeNacimientoUsable(answers.birthdate),
            // Y UN TELÉFONO O UN CORREO SIN FORMA, IGUAL (ALT-10, 14-10): con «abc» de
            // teléfono el botón se encendía, y es el WhatsApp por el que se le va a escribir.
            !esUnTelefono(answers.phone), !esUnCorreo(answers.email),
        ].filter(Boolean).length;
        const escrito = (k) => `${answers[k] ?? ''}`.trim() !== '';
        const listo = porRellenar === 0;
        body = (
            <div>
                <Title />
                {/* PLANTILLA B, FORMULARIO (rediseño del 18-10): nombre, sexo, fecha y
                    teléfono en dos columnas (una en estrecho) y el email. */}
                <div className="flex flex-col gap-[18px] mb-7">
                    <MiniInput {...mini} k="name" label="Nombre completo" obligatorio />
                    <div>
                        <p className={ETIQUETA}>Sexo<Obligatorio /></p>
                        <div className="grid grid-cols-2 gap-2">
                            {[['hombre', 'Hombre'], ['mujer', 'Mujer']].map(([v, etiqueta]) => (
                                <button key={v} type="button" onClick={() => {
                                    set('sex', v);
                                    if (!objetivoValePara(answers.goal, v)) set('goal', null);
                                }}
                                    className={`h-[52px] rounded-[12px] border-[1.5px] text-base font-bold text-[#f2f0ed] inline-flex items-center justify-center gap-2 transition-colors ${
                                        answers.sex === v
                                            ? 'border-[#ff6a1f] bg-[#2a1b10]'
                                            : 'border-[#2a2a2a] bg-[#161616] hover:border-[#ff6a1f]'}`}>
                                    {etiqueta}
                                    {answers.sex === v && <Check className="w-3.5 h-3.5 text-[#ff6a1f]" strokeWidth={3} />}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-[18px]">
                        <MiniInput {...mini} k="birthdate" label="Fecha de nacimiento" type="date"
                            placeholder="La verdadera, no me engañes." obligatorio
                            aviso={porQueNoValeLaFecha(answers.birthdate)} />
                        <MiniInput {...mini} k="phone" label="Teléfono (WhatsApp)" type="tel"
                            placeholder="Un whatsapp de contacto." obligatorio
                            aviso={escrito('phone') && !esUnTelefono(answers.phone) ? TELEFONO_QUE_NO_VALE : null} />
                    </div>
                    <MiniInput {...mini} k="email" label="Email" type="email"
                        placeholder="Uno que revises a diario." obligatorio
                        aviso={escrito('email') && !esUnCorreo(answers.email) ? CORREO_QUE_NO_VALE : null} />
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <BackBtn />
                    <Button onClick={goNext} disabled={!listo}
                        className={CTA}>
                        Seguir <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
                <FaltanCampos n={porRellenar} testId="contacto-faltan" />
            </div>
        );
    } else if (step.type === 'ocupacion') {
        // PANTALLA 12, PRIMERA MITAD: la profesión sola (20-10, prompt del rediseño, punto 0).
        // El sedentarismo va en la siguiente (`actividad`).
        const sinProfesion = !(answers.profesion || '').trim();
        body = (
            <div>
                <Title />
                <div className="mb-7">
                    <Input value={answers.profesion ?? ''} onChange={e => set('profesion', e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && !sinProfesion) goNext(); }}
                        autoFocus placeholder="Escribe tu respuesta..." data-testid="ocupacion-profesion"
                        className={CAMPO} />
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <BackBtn />
                    <Button onClick={goNext} disabled={sinProfesion} data-testid="ocupacion-ok"
                        className={CTA}>
                        Continuar <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
                <FaltanCampos n={sinProfesion ? 1 : 0} testId="ocupacion-faltan" />
            </div>
        );
    } else if (step.type === 'actividad') {
        // PANTALLA 12, SEGUNDA MITAD: cuánto se mueve, con las opciones y el texto de siempre.
        // Avanza sola al elegir. El candado de la regla 1 sigue: sin la profesión de la
        // pantalla anterior (por ejemplo, si llegó aquí desde el repaso) no se pasa.
        const actividad = q('actividad_diaria');
        const sinProfesion = !(answers.profesion || '').trim();
        body = (
            <div>
                <Title />
                <div className="flex flex-col gap-3">
                    {segunRespuestas(actividad.options).map(o => (
                        <TarjetaOpcion key={o.value} selected={answers.actividad_diaria === o.value}
                            onClick={() => {
                                set('actividad_diaria', o.value);
                                trasResponder('actividad_diaria', o.value);
                                if ((answersRef.current.profesion || '').trim()) pasarSola();
                            }}>
                            <EtiquetaDeOpcion label={o.label} />
                        </TarjetaOpcion>
                    ))}
                </div>
                {!!answers.actividad_diaria && sinProfesion && (
                    <p className="text-sm text-amber-500 mt-4" data-testid="falta-profesion">
                        Por favor, rellena «A qué te dedicas» para continuar.
                    </p>
                )}
                <div className="flex items-center gap-3 flex-wrap mt-7">
                    <BackBtn />
                </div>
            </div>
        );
    } else if (step.type === 'peso_hito') {
        // PANTALLAS 9, 10 y 11: cada hito de su peso con SU AÑO. Antes eran cuatro casillas
        // sueltas en una sola pantalla, sin años y solo para quien llevaba entrenador: «si
        // viene como "unos 95 hace tres años" no se puede calcular nada con él».
        const kPeso = step.key, kAno = `${step.key}_ano`, kNota = `${step.key}_nota`;
        // El año, EN BLANCO (punto 2 del doc del 23-08): el placeholder «2019» se leía como
        // valor puesto. Y validado: cuatro cifras entre 1940 y el año actual.
        const anoCrudo = `${answers[kAno] ?? ''}`.trim();
        // Y QUE NO SEA UN AÑO QUE NO HA LLEGADO NI ANTERIOR A SU NACIMIENTO (ALT-12, 14-10;
        // la regla y su frase, en `lib/anoDelHito`). Un año que no vale apaga el botón TAMBIÉN
        // en la pregunta opcional: ahí el aviso salía y «OK» dejaba pasar «75 kg (2030)».
        const anoMal = porQueNoValeElAno(anoCrudo, answers.birthdate);
        const hitoListo = !avisoDelPaso && !anoMal && (!step.required
            || (`${answers[kPeso] ?? ''}`.trim() !== '' && anoCrudo !== ''));
        // Los dos campos de arriba son obligatorios cuando el paso lo es (ficha 02). La nota
        // y la foto no cuentan: ahí pone «(opcional)» y no apagan el botón.
        const porRellenar = !step.required ? 0
            : [`${answers[kPeso] ?? ''}`.trim() === '', anoCrudo === ''].filter(Boolean).length;
        // Elegir (o cambiar) la foto de su mejor forma. Se queda en el formulario como data
        // URL y viaja una sola vez, en el envío final (no va en el borrador: ver guardarProgreso).
        const elegirFotoMejorForma = () => {
            const input = document.createElement('input');
            input.type = 'file';
            input.accept = 'image/*';
            input.onchange = async (ev) => {
                const f = ev.target.files?.[0];
                if (!f) return;
                if (f.size > 8 * 1024 * 1024) { toast.error('La foto pesa demasiado (máximo 8 MB)'); return; }
                // Lo que no es una foto no se da por «foto lista» (ALT-11, 14-10).
                if (!(await pareceUnaImagen(f))) { toast.error(NO_ES_UNA_FOTO); return; }
                const reader = new FileReader();
                reader.onload = (e) => set('foto_mejor_momento', e.target.result);
                reader.readAsDataURL(f);
            };
            input.click();
        };
        const conFoto = !!answers.foto_mejor_momento;
        // «ESTOY EN MI PEOR MOMENTO DE FORMA» (24-10): su máximo es el peso de hoy y el año,
        // este. Se apunta como respuesta (con la marca `peor_forma_ahora`, para que la ficha
        // lo distinga de un máximo escrito a mano) y pasa a la siguiente.
        const pesoDeHoy = answers.weight ?? profile?.weight;
        const estoyEnMiPeorMomento = () => {
            if (pesoDeHoy != null && `${pesoDeHoy}`.trim() !== '') set(kPeso, String(pesoDeHoy));
            set(kAno, String(ahoraDeLaApp().getFullYear()));
            set('peor_forma_ahora', true);
            goNext();
        };
        // La mejor forma en blanco ES una respuesta (24-10, «si nunca estuviste en forma,
        // simplemente no contestes nada»): se apunta como la apuntaba el «Si no, pásala».
        const continuarHito = () => {
            const enBlanco = `${answers[kPeso] ?? ''}`.trim() === '';
            if (step.key === 'peso_mejor_momento') set('mejor_forma_pasada', enBlanco ? true : null);
            // Escribir el máximo a mano quita la marca del botón.
            if (step.peorAhora && answers.peor_forma_ahora
                && (`${answers[kAno] ?? ''}` !== String(ahoraDeLaApp().getFullYear())
                    || `${answers[kPeso] ?? ''}` !== String(pesoDeHoy ?? ''))) {
                set('peor_forma_ahora', null);
            }
            goNext();
        };
        // REDISEÑO «TU HISTORIA» DEL MÁXIMO PESO (04-11): dos opciones con una «O» en medio
        // -- «Ahora estoy en mi peor momento de forma» o «Fue en otro momento» -- y el peso, el año
        // y la nota solo al elegir la segunda. Cambia el aspecto, no la lógica: la primera
        // contesta y pasa como el botón de antes (`estoyEnMiPeorMomento`), la segunda pide
        // peso y año como siempre (`hitoListo`, con el año comprobado por `porQueNoValeElAno`)
        // y sigue con `continuarHito`. El fondo de la pantalla es el de siempre.
        const enPeorMomento = !!answers.peor_forma_ahora;
        const escritoAlgo = `${answers[kPeso] ?? ''}`.trim() !== '' || anoCrudo !== '';
        const enOtroMomento = !enPeorMomento && (hitoEnOtroMomento || escritoAlgo);
        const elegirOtroMomento = () => {
            if (enPeorMomento) {
                // Venía de «peor momento» (el peso de hoy y este año): se empieza en blanco.
                set('peor_forma_ahora', null);
                set(kPeso, '');
                set(kAno, '');
            }
            setHitoEnOtroMomento(true);
        };
        const Radio = ({ on }) => (
            <span className={`flex-none w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center ${on ? 'border-[#ff6a1f]' : 'border-[#4a433d]'}`}>
                <span className={`w-2.5 h-2.5 rounded-full ${on ? 'bg-[#ff6a1f]' : 'bg-transparent'}`} />
            </span>
        );
        const tarjeta = (on) => `rounded-[14px] border-[1.5px] transition-colors ${
            on ? 'border-[#ff6a1f] bg-[#2a1b10]' : 'border-[#2a2a2a] bg-[#161616] hover:border-[#6b4a35]'}`;
        const faltan = enOtroMomento ? [`${answers[kPeso] ?? ''}`.trim() === '' && 'peso', anoCrudo === '' && 'año'].filter(Boolean) : [];
        const puedeSeguir = enPeorMomento || (enOtroMomento && hitoListo);
        const pista = !enPeorMomento && !enOtroMomento ? 'Elige una de las dos opciones para continuar'
            : (avisoDelPaso || anoMal) ? null
            : faltan.length ? `Te falta: ${faltan.join(' y ')}` : null;
        if (step.peorAhora) {
            body = (
                <div>
                    <Title />
                    <div className="flex flex-col gap-3 mb-7">
                        <button type="button" onClick={estoyEnMiPeorMomento} data-testid="peso-hito-peor-ahora"
                            className={`${tarjeta(enPeorMomento)} w-full text-left flex items-center gap-4 px-[22px] py-5`}>
                            <Radio on={enPeorMomento} />
                            <span className="text-lg font-bold text-[#f2f0ed]">Ahora estoy en mi peor momento de forma</span>
                        </button>

                        <div className="flex items-center gap-3.5 text-xs font-bold tracking-[0.16em] text-[#6f6861]" aria-hidden="true">
                            <span className="flex-1 h-px bg-[#2a2622]" />O<span className="flex-1 h-px bg-[#2a2622]" />
                        </div>

                        <div className={`${tarjeta(enOtroMomento)} overflow-hidden`}>
                            <button type="button" onClick={elegirOtroMomento} data-testid="peso-hito-otro-momento"
                                className="w-full text-left flex items-center gap-4 px-[22px] py-5">
                                <Radio on={enOtroMomento} />
                                <span className="text-lg font-bold text-[#f2f0ed]">Fue en otro momento</span>
                            </button>
                            {enOtroMomento && (
                                <div className="flex flex-col gap-5 pl-[22px] sm:pl-[60px] pr-[22px] pb-[22px] pt-1">
                                    <div className="grid grid-cols-2 gap-3.5">
                                        <MiniInput {...mini} k={kPeso} label="Peso" type="number" unit="kg"
                                            placeholder="95" obligatorio={!!step.required} />
                                        <MiniInput {...mini} k={kAno} label="Año" type="number"
                                            placeholder="2019" obligatorio={!!step.required} />
                                    </div>
                                    {(avisoDelPaso || anoMal) && (
                                        <p className="text-sm text-amber-500 -mt-2" data-testid="aviso-del-paso">
                                            {avisoDelPaso || anoMal}
                                        </p>
                                    )}
                                    <div>
                                        <label className={ETIQUETA}>
                                            {step.nota} <span className="text-[#5f5a55]">(opcional)</span>
                                        </label>
                                        <textarea value={answers[kNota] ?? ''} onChange={e => set(kNota, e.target.value)}
                                            rows={3} className={AREA} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                        <BackBtn />
                        <Button onClick={continuarHito} disabled={!puedeSeguir} data-testid="peso-hito-ok"
                            className={CTA}>
                            Continuar <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                    {pista && <p className="text-sm text-[#8a8580] mt-3" data-testid="peso-hito-pista">{pista}</p>}
                </div>
            );
        } else body = (
            <div>
                <Title />
                {step.peorAhora && (
                    <button type="button" onClick={estoyEnMiPeorMomento} data-testid="peso-hito-peor-ahora"
                        className={`w-full mb-[18px] h-[52px] rounded-[12px] border-[1.5px] text-base font-bold text-[#f2f0ed] transition-colors ${
                            answers.peor_forma_ahora ? 'border-[#ff6a1f] bg-[#2a1b10]' : 'border-[#2a2a2a] bg-[#161616] hover:border-[#ff6a1f]'}`}>
                        Ahora estoy en mi peor momento de forma
                    </button>
                )}
                {/* PLANTILLA E, FOTO + DATOS (rediseño del 18-10): la foto arriba, peso y año
                    en dos columnas y lo que quiera contar debajo. En el máximo peso no hay
                    foto, y el peso y el año siguen siendo obligatorios. */}
                {step.conFoto && (
                    <div className="mb-[18px]">
                        {/* EL BOTÓN EN ESPAÑOL (doc 23-08, punto 3): el input de fichero
                            crudo pintaba el «Choose File · No file chosen» del navegador.
                            Y LA FOTO SE VE (Francisco, 13-10): elegida, sale de fondo de la
                            zona, con sus dos salidas, cambiarla y quitarla. */}
                        {/* ELEGIDA, LA FOTO OCUPA LA ZONA Y TOCARLA LA CAMBIA (20-10, prompt del
                            rediseño, punto 2). Fuera «Foto lista», «Tócala para verla entera»,
                            «Cambiarla» y «Quitarla». Sin elegir, la zona para subirla. */}
                        {conFoto ? (
                            <button type="button" onClick={elegirFotoMejorForma}
                                aria-label="Cambiar la foto de tu mejor forma" data-testid="foto-mejor-forma-elegida"
                                className="block w-full h-[320px] rounded-[18px] border-[1.5px] border-[#3a3a3a] overflow-hidden bg-[#141414] transition-colors hover:border-[#ff6a1f]">
                                <img src={answers.foto_mejor_momento} alt="La foto de tu mejor forma"
                                    className="block w-full h-full object-contain" />
                            </button>
                        ) : (
                            <button type="button" onClick={elegirFotoMejorForma} data-testid="foto-mejor-forma"
                                className="relative w-full h-[200px] rounded-[18px] border-[1.5px] border-dashed border-[#3a3a3a] overflow-hidden bg-[#141414] flex flex-col items-center justify-center gap-3 transition-colors hover:border-[#ff6a1f]">
                                <span className="relative w-14 h-14 rounded-full bg-[#222] flex items-center justify-center">
                                    <ImagePlus className="w-6 h-6 text-[#ff6a1f]" />
                                </span>
                                <span className="relative flex flex-col items-center gap-0.5 px-3.5 py-2 rounded-[10px] bg-[#0b0b0b]/70">
                                    <span className="text-[15px] font-bold text-[#f2f0ed]">Sube la foto de tu mejor forma</span>
                                    <span className="text-xs text-[#8a8580]">Opcional</span>
                                </span>
                            </button>
                        )}
                    </div>
                )}
                <div className="grid grid-cols-2 gap-3 mb-[18px]">
                    <MiniInput {...mini} k={kPeso} label="Peso" type="number" unit="kg"
                        obligatorio={!!step.required} />
                    <MiniInput {...mini} k={kAno} label="Año" type="number"
                        obligatorio={!!step.required} />
                </div>
                {(avisoDelPaso || anoMal) && (
                    <p className="text-sm text-amber-500 -mt-2 mb-4" data-testid="aviso-del-paso">
                        {avisoDelPaso || anoMal}
                    </p>
                )}
                <div className="mb-7">
                    <label className={ETIQUETA}>
                        {step.nota} <span className="text-[#5f5a55]">(opcional)</span>
                    </label>
                    <textarea value={answers[kNota] ?? ''} onChange={e => set(kNota, e.target.value)}
                        rows={3} className={AREA} />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    <BackBtn />
                    <Button onClick={continuarHito} disabled={!hitoListo} data-testid="peso-hito-ok"
                        className={CTA}>
                        Continuar <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    {/* «Si no, pásala» se quitó el 24-10: dejarlo en blanco y continuar hace
                        lo mismo (ver `continuarHito`). */}
                </div>
                <FaltanCampos n={porRellenar} testId="peso-hito-faltan" />
            </div>
        );
    } else if (step.type === 'tres_cosas') {
        // LA PANTALLA DEL DOC 19-08, con su texto literal. «Lo hago luego» va directo a
        // la oferta de los 87 € y le deja el aviso pendiente de las preferencias.
        body = (
            <div>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-3 leading-tight">
                    Tres cosas y terminamos
                </h2>
                <div className="space-y-3 mb-6">
                    {[['1 · Subir tus fotos', 'Frente, espaldas y perfil (elige un perfil y no cambies)'],
                      ['2 · Tomar las medidas', 'Aquí vas a necesitar que te ayude alguien (y si puede ser siempre el mismo, mejor)'],
                      ['3 · Completar las preferencias']]
                        .map(([t, d]) => (
                            <div key={t} className="surface p-4">
                                <p className="font-semibold text-foreground">{t}</p>
                                {d && <p className="text-sm text-foreground/50">{d}</p>}
                            </div>
                        ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                    <BackBtn />
                    <Button onClick={goNext} data-testid="empezar-por-las-fotos"
                        className={CTA}>
                        Empezar por las fotos <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                    <Button variant="outline" onClick={saltarAOferta} data-testid="lo-hago-luego"
                        className="px-8 py-6 text-lg">
                        Lo hago luego
                    </Button>
                </div>
            </div>
        );
    } else if (step.type === 'fotos_alta') {
        // PASO 1 · Tus fotos, con el texto literal. Se guardan al elegirlas (TresFotos
        // sube cada una al momento), así que salir por aquí no pierde nada.
        body = (
            <div>
                {/* Con ceja y sin «Te recomiendo repetirlas cada 4 semanas...» ni el aviso del
                    reporte mensual de `TresFotos` (20-10, prompt del rediseño, punto 32). */}
                <Title />
                {/* La del largo trae su subtítulo (25-10); la del que no lleva entrenador, la
                    frase de siempre. */}
                {!step.desc && (
                    <p className="text-foreground/70 mb-4 text-sm -mt-3">
                        Hazlas con buena luz y siempre en el mismo sitio.
                    </p>
                )}
                <div className="mb-6">
                    <TresFotos api={api} token={token} esMensual={false} alCambiarHechas={setFotosAltaHoy} />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                    <BackBtn />
                    {/* «CONTINUAR» CON UNA FOTO COMO MÍNIMO (04-11); sin ninguna, «Lo hago luego». */}
                    <Button onClick={goNext} data-testid="fotos-continuar" disabled={fotosAltaHoy < 1}
                        className={CTA}>
                        Continuar <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                    {/* Aplaza SOLO este paso y sigue con el siguiente (10-10): saltaba a la
                        oferta de los 87 €, por encima de las medidas, las preferencias y
                        «Ya está todo». El que salta las tres es el de «Tres cosas y
                        terminamos». */}
                    <Button variant="outline" onClick={goNext} data-testid="fotos-lo-hago-luego"
                        className="px-8 py-6 text-lg">
                        Lo hago luego
                    </Button>
                </div>
            </div>
        );
    } else if (step.type === 'medidas_alta') {
        // PASO 2 · Tus medidas. Se guardan al continuar («se guarda al pasar de pantalla,
        // no al final»): quien salga después no las pierde.
        const guardarMedidasAlta = async () => {
            const conValor = Object.fromEntries(
                Object.entries(medidasAlta).filter(([, v]) => v !== '' && v != null));
            const chequeo = revisarMedidas(conValor, { todas: false });
            if (!chequeo.ok) { toast.error(chequeo.error); return; }
            if (Object.keys(conValor).length) {
                try {
                    // Son las medidas del DÍA 1: van al punto de partida (medidas_inicio y
                    // la marca de hecho), no a la serie de Seguimiento. Así la ficha y la
                    // comparativa del mes que viene salen de aquí, y al que vuelva al alta
                    // no se le piden otra vez.
                    await api.post('/clients/punto-de-partida', { medidas: conValor });
                } catch (e) {
                    console.error('[alta] las medidas no se guardaron', e);
                    toast.error('No se pudieron guardar las medidas. Puedes añadirlas después en Seguimiento.');
                }
            }
            goNext();
        };
        body = (
            <div>
                {/* Con su ceja, como «Tus fotos» (20-10). */}
                <Title />
                <p className="text-foreground/70 mb-4 text-sm -mt-3">
                    Si te puede medir alguien, y siempre el mismo, mejor. Cada 4 semanas. En cm.
                </p>
                <div className="grid grid-cols-2 gap-2 mb-6 items-end">
                    {MEDIDAS.map(({ key, label }) => (
                        <label key={key} className="flex flex-col justify-end text-xs text-foreground/50">
                            {label}
                            <Input type="number" inputMode="decimal" step="0.5"
                                value={medidasAlta[key] ?? ''} data-testid={`alta-medida-${key}`}
                                onChange={e => setMedidasAlta(m => ({ ...m, [key]: e.target.value }))}
                                className="mt-0.5 bg-card border-[#222222]" />
                        </label>
                    ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                    <BackBtn />
                    <Button onClick={guardarMedidasAlta} data-testid="medidas-continuar"
                        className={CTA}>
                        Continuar <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                    {/* Aplaza SOLO este paso y sigue con el siguiente (10-10): saltaba a la
                        oferta de los 87 €, por encima de las medidas, las preferencias y
                        «Ya está todo». El que salta las tres es el de «Tres cosas y
                        terminamos». */}
                    <Button variant="outline" onClick={goNext} data-testid="medidas-lo-hago-luego"
                        className="px-8 py-6 text-lg">
                        Lo hago luego
                    </Button>
                </div>
            </div>
        );
    } else if (step.type === 'ya_esta_todo') {
        // PASO 4 · después de las preferencias, con el literal del doc del 23-08 (punto
        // 20): «Ya está todo · Este es tu punto de partida», la lista con lo que de
        // verdad ha dejado hecho, la promesa del mes que viene y «Seguir» (detrás va la
        // oferta).
        const nMedidas = Object.values(medidasAlta).filter(v => v !== '' && v != null).length;
        const estadoMedidas = nMedidas > 0 ? `${nMedidas} de ${MEDIDAS.length}`
            : yaTieneMedidas ? 'Guardadas' : 'Pendientes';
        body = (
            <div>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-2 leading-tight">
                    Ya está todo
                </h2>
                <div className="space-y-3 mb-6" data-testid="punto-de-partida-lista">
                    {[['Las tres fotos', yaTieneFotos ? 'Guardadas' : 'Pendientes'],
                      ['Tus medidas', estadoMedidas],
                      ['Tus preferencias', 'Guardadas']]
                        .map(([t, d]) => (
                            <div key={t} className="flex items-start gap-3">
                                <Check className="w-5 h-5 text-brand mt-0.5 shrink-0" />
                                <div>
                                    <p className="font-semibold text-foreground">{t}</p>
                                    <p className="text-sm text-foreground/50">{d}</p>
                                </div>
                            </div>
                        ))}
                </div>
                <div className="flex items-center gap-3 flex-wrap">
                    <BackBtn />
                    <Button onClick={goNext} data-testid="ir-a-mi-panel"
                        className={CTA}>
                        Seguir <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            </div>
        );
    // AQUÍ VIVÍA «TUS FOTOS Y TUS MEDIDAS», la pantalla que no pedía nada y mandaba a
    // Reportes a hacerlo allí. Se retira el 8-09: el completo las pide ahora en sus dos
    // pantallas, como el resto del alta. No se deja escrita sin usar, que es justo lo que
    // le pasaba a la pantalla del momento del entreno y costó encontrarlo.
    } else if (step.type === 'oferta_ajuste') {
        // LA OFERTA DE LOS 87 €, CON SU TEXTO LITERAL. Todavía no cobra: el documento deja sin
        // decidir cómo se cobra, así que se apunta lo que contesta -- el sí y el no, que
        // saber cuánta gente lo rechaza vale igual -- y el equipo lo ve en su campana.
        body = (
            <div>
                <h2 className="font-heading font-bold text-3xl md:text-4xl text-foreground mb-4 leading-tight">
                    Una cosa más
                </h2>
                <p className="text-foreground/80 mb-3 leading-relaxed">
                    Puedes continuar con tu plan actual y empezar a usar la calculadora con unos
                    macros ajustados según tu perfil y teniendo en cuenta la evolución de otras
                    personas con un perfil parecido al tuyo que ya han pasado por aquí, o puedes
                    solicitar tus macros personalizados y recibir tu ajuste a medida.
                </p>
                <p className="text-sm text-foreground/50 italic mb-6">
                    (Esta segunda opción no está incluida en tu plan, tiene un coste adicional e
                    incluye también tu plan personalizado de suplementación.)
                </p>
                <div className="space-y-3">
                    <button data-testid="oferta-no" disabled={loading}
                        onClick={() => responderOferta(false)}
                        className="w-full text-left p-4 rounded-xl border-2 border-border hover:border-brand/50 transition-all disabled:opacity-50">
                        <span className="font-semibold text-foreground">
                            Me vale con la primera opción, la que está incluida en mi plan
                        </span>
                    </button>
                    <button data-testid="oferta-si" disabled={loading}
                        onClick={() => responderOferta(true)}
                        className="w-full text-left p-4 rounded-xl border-2 border-border hover:border-brand transition-all disabled:opacity-50">
                        <span className="font-semibold text-foreground">
                            Quiero el ajuste a medida, aunque implique pagar más
                        </span>
                        <span className="block text-sm text-brand font-bold mt-1">
                            87 € · macros personalizados + programa de suplementación
                        </span>
                    </button>
                </div>
                <div className="mt-6"><BackBtn /></div>
            </div>
        );
    } else if (step.type === 'dieta') {
        // P10 del doc: tres formas de meter la dieta y CONFIRMACIÓN OBLIGATORIA. El cliente no
        // calcula nada: cuenta lo que come y le devolvemos lo que hemos entendido. Sin su
        // confirmación el dato no entra en el cálculo, porque manda sobre todo lo demás.
        body = (
            <div>
                <Title />

                {!lecturaDieta ? (
                    <div className="space-y-4 mb-6">
                        {/* DOS PUERTAS, NO TRES (doc del 23-08, punto 10): «Un día mío» se
                            quita. Era elegir un día ya creado en la calculadora, y quien
                            está en el alta no tiene ninguno. */}
                        <div className="grid grid-cols-2 gap-2">
                            {[['texto', 'Escribirla'], ['foto', 'Una foto']].map(([modo, etiqueta]) => (
                                <button key={modo} onClick={() => set('dieta_modo', modo)}
                                    className={`px-3 py-2.5 rounded-xl border-2 text-sm font-semibold transition-all ${
                                        (answers.dieta_modo || 'texto') === modo
                                            ? 'border-brand bg-brand/10 text-foreground'
                                            : 'border-[#222222] text-foreground/60 hover:border-white/30'}`}>
                                    {etiqueta}
                                </button>
                            ))}
                        </div>

                        {(answers.dieta_modo || 'texto') === 'texto' && (
                            <textarea value={answers.dieta_texto ?? ''} onChange={e => set('dieta_texto', e.target.value)}
                                rows={5} placeholder="Un día tipo, con cantidades. Por ejemplo: desayuno 80 g de avena y 30 g de proteína; comida 200 g de pollo con 100 g de arroz; cena merluza con ensalada."
                                className="w-full rounded-xl bg-card border-2 border-[#222222] p-3 text-foreground text-sm resize-none focus:outline-none focus:border-brand" />
                        )}

                        {answers.dieta_modo === 'foto' && (
                            <div>
                                <button onClick={elegirFotoDieta}
                                    className="w-full rounded-xl border-2 border-dashed border-[#333] py-8 text-center hover:border-brand transition-colors">
                                    <ImagePlus className="w-7 h-7 text-foreground/40 mx-auto mb-2" />
                                    <span className="text-foreground/60 text-sm">
                                        {answers.dieta_imagen ? 'Foto elegida. Toca para cambiarla' : 'Sube la foto de tu dieta'}
                                    </span>
                                </button>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-3">
                            <BackBtn />
                            <Button onClick={leerMiDieta} disabled={leyendoDieta}
                                className={CTA}>
                                {leyendoDieta ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Leyendo...</> : <>Calcular <ArrowRight className="w-4 h-4 ml-2" /></>}
                            </Button>
                            <Button variant="ghost" onClick={goNext} className="text-foreground/50">
                                No lo sé, sáltalo
                            </Button>
                        </div>
                    </div>
                ) : (
                    /* Lo que hemos entendido, para que lo confirme */
                    <div className="space-y-4 mb-6">
                        <div className="rounded-xl border-2 border-brand/40 bg-brand/5 p-5">
                            {/* PROTEÍNA · HIDRATOS · GRASA, como en toda la app (punto 18 del
                                17-08). Aquí iba hidratos primero, y es la única pantalla que lo
                                hacía: la tabla de macros de dos pasos después, las tarjetas de
                                comida, la cabecera del día y el asistente van todos en el orden
                                del método. */}
                            <p className="text-foreground text-base mb-3">
                                He entendido que estás comiendo{' '}
                                <strong className="text-brand">{lecturaDieta.macros.proteina} g de proteína</strong>,{' '}
                                <strong className="text-brand">{lecturaDieta.macros.hidratos} g de hidratos</strong> y{' '}
                                <strong className="text-brand">{lecturaDieta.macros.grasa} g de grasa</strong>. ¿Es correcto?
                            </p>
                            {/* QUÉ PIDIÓ Y QUÉ HEMOS COGIDO (punto 17 del 17-08). Decía solo
                                «250 g · Leche desnatada» cuando el cliente había escrito
                                «leche»: la variante la elegimos nosotros y él no tenía forma de
                                verlo ni de corregirlo. El backend ya devuelve `pedido`. */}
                            <ul className="text-xs text-foreground/50 space-y-0.5 max-h-40 overflow-y-auto">
                                {lecturaDieta.alimentos.map((a, i) => (
                                    <li key={i}>
                                        {a.cantidad_g} g{a.cantidad_asumida && <span className="text-foreground/35"> (a ojo)</span>} · {a.nombre}
                                        {a.pedido && a.nombre
                                            && !a.nombre.toLowerCase().startsWith(String(a.pedido).toLowerCase())
                                            && <span className="text-foreground/35"> · tú dijiste «{a.pedido}»</span>}
                                    </li>
                                ))}
                            </ul>
                            {lecturaDieta.no_reconocidos?.length > 0 && (
                                <p className="text-xs text-amber-500 mt-2">
                                    No hemos reconocido: {lecturaDieta.no_reconocidos.join(', ')}
                                </p>
                            )}
                            {/* Lo que el lector pide aclarar (13-10): una ensalada cuenta por lo que
                                lleva. La frase la da el servidor (`pistas`); aqui solo se pinta. */}
                            {lecturaDieta.pistas?.map((pista, i) => (
                                <p key={i} className="text-xs text-amber-500 mt-1">{pista}</p>
                            ))}
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Button onClick={confirmarDieta}
                                className={CTA}>
                                Sí, es correcto <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                            <Button variant="outline" onClick={() => setLecturaDieta(null)}
                                className="border-[#333] text-foreground">
                                No, corregir
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        );
    } else if (step.type === 'partida') {
        // Las medidas del día 1. Las fotos ya NO se piden aquí: van con el reporte (punto 19
        // del doc del 07-08), que es donde el cliente entiende para qué son.
        // «APUNTA TUS MEDIDAS» (20-10, prompt del rediseño, punto 11): con la ceja «Tu
        // cuerpo», sin la frase de las fotos y con «Continuar» (guarda y sigue) y «Atrás» en
        // vez de «Ver mi ficha» y «Ahora no». La ficha que venía detrás ya no existe.
        body = (
            <div>
                <Title />

                <div className="space-y-4 mb-6">
                    {/* Las MISMAS diez que en el reporte, para que la del día 1 se pueda
                        comparar con la del mes que viene. Antes aquí se pedían cuatro
                        (cintura, abdomen, cadera y la altura), en el check-in cinco y en el
                        reporte otras cinco: tres listas distintas y ninguna comparable. */}
                    <div>
                        <label className="block text-xs font-bold text-foreground/50 uppercase tracking-wider mb-2">
                            Tus medidas de hoy
                        </label>
                        <div className="rounded-xl overflow-hidden bg-black mb-3" style={{ aspectRatio: '16 / 9' }}>
                            <iframe src={VIDEO_MEDIDAS} title="Cómo medir los perímetros"
                                allow="fullscreen; picture-in-picture" className="w-full h-full border-0" />
                        </div>
                        {/* items-end: con una etiqueta en dos líneas («Brazo izquierdo
                            relajado») los campos quedaban descuadrados. */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 items-end">
                            {MEDIDAS.map(({ key, label }) => (
                                <MiniInput key={key} {...mini} k={`medida_${key}`} label={label}
                                    type="number" unit="cm" placeholder="--" />
                            ))}
                        </div>
                    </div>

                    {/* La altura es un dato FIJO, no una medida de seguimiento: va aparte y
                        solo si todavía no la ha dado. */}
                    {!answers.height && (
                        <div className="max-w-[10rem]">
                            <MiniInput {...mini} k="height" label="Tu altura" type="number" unit="cm" placeholder="178" />
                        </div>
                    )}
                </div>

                {/* «CONTINUAR» CON TODAS LAS MEDIDAS (04-11): guarda y sigue. Sin ellas, «Lo hago
                    luego» sigue sin guardar nada; las puede apuntar después en Seguimiento. */}
                <div className="flex items-center gap-3 flex-wrap">
                    <BackBtn />
                    <Button onClick={guardarPuntoDePartida} data-testid="partida-continuar"
                        disabled={!MEDIDAS.every(({ key }) => num(answers[`medida_${key}`]))}
                        className={CTA}>
                        Continuar <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                    <Button variant="outline" onClick={goNext} data-testid="partida-lo-hago-luego"
                        className="px-8 py-6 text-lg">
                        Lo hago luego
                    </Button>
                </div>
            </div>
        );
    } else if (step.type === 'exclusiones') {
        body = (
            <div>
                <Title />
                <ExclusionesDelAlta answers={answers} set={set} api={api} />
                <div className="flex items-center gap-3 flex-wrap mt-7">
                    <BackBtn />
                    <Button onClick={goNext} data-testid="exclusiones-ok"
                        className={CTA}>
                        Continuar <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </div>
        );
    } else if (step.type === 'preferidos') {
        // Sin los 20 g de proteína y de hidratos no se sigue (24-10): el motor no podría
        // cuadrarle una comida con lo que le gusta. Mientras se comprueba, o si la
        // comprobación falla, el botón no se apaga.
        body = (
            <div>
                <Title />
                <PreferidosDelAlta answers={answers} set={set} api={api} onCuadra={setPreferidosCuadran} />
                <div className="flex items-center gap-3 flex-wrap mt-7">
                    <BackBtn />
                    <Button onClick={goNext} disabled={preferidosCuadran === false} data-testid="preferidos-ok"
                        className={CTA}>
                        Continuar <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </div>
        );
    } else if (step.type === 'multiselect') {
        // NO REVIENTA CON FICHAS ANTIGUAS (doc 23-08, punto 15; la causa real del «botón
        // de confirmar que a veces no responde»): `alergias` puede llegar como STRING de
        // los cuestionarios viejos, y `selected.filter` sobre un string tiraba un
        // TypeError que dejaba la pantalla muerta. Se normaliza: del texto se rescatan
        // las opciones que nombre, y a partir de ahí ya es una lista normal.
        const crudo = answers[step.key];
        const selected = Array.isArray(crudo) ? crudo
            : (typeof crudo === 'string' && crudo)
                ? segunRespuestas(step.options).map(o => o.value).filter(v => crudo.toLowerCase().includes(String(v).toLowerCase()))
                : [];
        // «No, ninguna» ES EXCLUYENTE (mismo punto): marcarla desmarca el resto, y marcar
        // cualquier otra la desmarca a ella. Antes se podía tener «ninguna» + «lactosa» a
        // la vez, que no significa nada.
        // Y EN LA FARMACOLOGÍA PASA LO MISMO: «No, nada» con un ciclo marcado a la vez no
        // significa nada. Cada multiselección dice cuál es la suya.
        const excluyente = step.key === 'alergias' ? 'ninguna'
            : step.key === 'farmacologia_uso' ? 'no' : null;
        // UN TOPE (24-10): los días de entreno son cuatro como mucho. Llegado al tope, la
        // quinta no entra y se dice por qué.
        const tope = step.max || null;
        const toggle = (v) => {
            let next;
            if (selected.includes(v)) next = selected.filter(x => x !== v);
            else if (excluyente && v === excluyente) next = [v];
            else {
                const sinExcluyente = selected.filter(x => x !== excluyente);
                if (tope && sinExcluyente.length >= tope) {
                    toast.info(`Como mucho ${tope}. Quita uno para cambiarlo.`);
                    return;
                }
                next = [...sinExcluyente, v];
            }
            set(step.key, next);
            // Si quita el «Otro», lo que había escrito deja de tener a qué referirse.
            if (step.otro && v === step.otro.valor && !next.includes(v)) set(step.otro.clave, '');
        };
        // «OTRO» CON SU CUADRO DE TEXTO, OBLIGATORIO (24-10, «¿Cómo me has conocido?»).
        const conOtro = !!step.otro && selected.includes(step.otro.valor);
        const faltaElOtro = conOtro && !`${answers[step.otro.clave] ?? ''}`.trim();
        body = (
            <div>
                <Title />
                <div className="flex flex-col gap-3">
                    {segunRespuestas(step.options).map(o => {
                        const on = selected.includes(o.value);
                        return (
                            <button key={o.value} type="button" onClick={() => toggle(o.value)}
                                className={`w-full text-left rounded-[18px] px-5 py-4 border-[1.5px] transition-colors flex items-center justify-between text-base font-semibold text-[#f2f0ed] ${
                                    on ? 'border-[#ff6a1f] bg-[#2a1b10]' : 'border-[#2a2a2a] bg-[#161616] hover:border-[#ff6a1f]'}`}>
                                {sinPunto(o.label)}
                                {on && <Check className="w-5 h-5 text-[#ff6a1f]" />}
                            </button>
                        );
                    })}
                </div>
                {conOtro && (
                    <div className="mt-4" data-testid="multiselect-otro">
                        <label className={ETIQUETA}>{step.otro.etiqueta}<Obligatorio /></label>
                        <Input value={answers[step.otro.clave] ?? ''} autoFocus
                            onChange={e => set(step.otro.clave, e.target.value)}
                            placeholder="Escribe tu respuesta..." className={CAMPO} />
                    </div>
                )}
                {/* El botón apagado tiene que decir por qué: sin esto se percibía como
                    «no responde» (doc 23-08, punto 15). */}
                {!selected.length && !step.exactos && (
                    <p className="text-sm text-foreground/50 mt-4" data-testid="multiselect-pista">
                        Marca al menos una opción para seguir.
                    </p>
                )}
                {!!step.exactos && selected.length !== step.exactos && (
                    <p className="text-sm text-foreground/50 mt-4" data-testid="multiselect-pista">
                        Marca exactamente {step.exactos} para seguir{selected.length ? ` (llevas ${selected.length})` : ''}.
                    </p>
                )}
                <div className="flex items-center gap-3 flex-wrap mt-7">
                    <BackBtn />
                    {/* LOS DÍAS DE ENTRENO, EXACTOS (04-11): con `exactos`, Continuar solo se
                        enciende con justo ese número marcado. */}
                    <Button onClick={goNext}
                        disabled={!selected.length || faltaElOtro || (!!step.exactos && selected.length !== step.exactos)}
                        className={CTA}>
                        Continuar <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
                <FaltanCampos n={faltaElOtro ? 1 : 0} testId="multiselect-faltan" />
            </div>
        );
    } else if (step.type === 'choice') {
        const opts = step.confirm ? confirmOptions() : segunRespuestas(step.options);
        // PLANTILLA C (rediseño del 18-10). Con dos opciones cortas, rejilla de dos; con
        // más, o con textos largos, lista. El objetivo lleva icono y su etiqueta grande
        // (VOLUMEN / DEFINICIÓN), que sale del paréntesis de la propia opción: el valor que
        // se guarda es el de siempre.
        const esObjetivo = step.key === 'goal';
        const enRejilla = opts.length === 2 && (esObjetivo || opts.every(o => String(o.label).length <= 40));
        body = (
            <div>
                <Title />
                <div className={enRejilla
                    ? 'grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-3'
                    : 'flex flex-col gap-3'}>
                    {opts.map(o => {
                        const selected = !step.confirm && answers[step.key] === o.value;
                        const etiqueta = sinPunto(o.label);
                        const marca = esObjetivo ? (etiqueta.match(/\(([^)]+)\)\s*$/) || [])[1] : null;
                        return (
                            <TarjetaOpcion key={String(o.value)} selected={selected} onClick={() => pickChoice(o.value)}>
                                {marca ? (
                                    <span className="flex flex-col gap-3.5">
                                        <span className="w-[52px] h-[52px] rounded-[14px] bg-[#222] flex items-center justify-center">
                                            <IconoObjetivo value={o.value} />
                                        </span>
                                        <span className="flex flex-col gap-1">
                                            <span className="font-heading text-[30px] font-extrabold tracking-[0.02em] leading-none">{marca}</span>
                                            <span className="text-[15px] text-[#b5b0aa]">{etiqueta.replace(/\s*\([^)]+\)\s*$/, '')}</span>
                                        </span>
                                    </span>
                                ) : enRejilla ? (
                                    <span className="font-heading text-[30px] font-extrabold leading-none">{etiqueta}</span>
                                ) : (
                                    <EtiquetaDeOpcion label={etiqueta} />
                                )}
                            </TarjetaOpcion>
                        );
                    })}
                </div>
                <div className="flex items-center gap-3 flex-wrap mt-7"><BackBtn /><PistaAutomatica /></div>
            </div>
        );
    } else if (step.type === 'biotype_intro') {
        body = (
            <div>
                <Title />
                <div className="space-y-3 max-h-[52vh] overflow-y-auto pr-1">
                    {BIOTIPOS.map((b, n) => (
                        <div key={b.value} className="flex gap-4 p-3 rounded-xl border-2 border-[#222222]">
                            <img src={b.img} alt={b.label} loading="lazy"
                                className="w-20 h-24 object-cover rounded-lg flex-shrink-0 bg-card" />
                            <div>
                                <p className="font-bold text-foreground">{n + 1}. {b.label}</p>
                                <p className="text-foreground/50 text-sm mt-1">{b.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="flex items-center gap-3 flex-wrap mt-7">
                    <BackBtn />
                    <Button onClick={goNext} className={CTA}>
                        Elegir mi biotipo <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
            </div>
        );
    } else if (step.type === 'biotype') {
        body = (
            <div>
                <Title />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[58vh] overflow-y-auto pr-1">
                    {BIOTIPOS.map(b => {
                        const selected = answers.biotype === b.value;
                        return (
                            <button key={b.value} onClick={() => { set('biotype', b.value); pasarSola(); }}
                                className={`rounded-[18px] border-[1.5px] overflow-hidden transition-colors text-left ${selected ? 'border-[#ff6a1f] bg-[#2a1b10]' : 'border-[#2a2a2a] bg-[#161616] hover:border-[#ff6a1f]'}`}>
                                <img src={b.img} alt={b.label} loading="lazy"
                                    className="w-full aspect-[3/4] object-cover bg-card" />
                                <p className={`px-3 py-2.5 text-[13px] font-bold ${selected ? 'text-[#ff8a4d]' : 'text-[#f2f0ed]'}`}>{b.label}</p>
                            </button>
                        );
                    })}
                </div>
                <div className="flex items-center gap-3 flex-wrap mt-7"><BackBtn /><PistaAutomatica /></div>
            </div>
        );
    } else if (step.type === 'bf') {
        // SIN «LA TUYA» NI «EL MODELO DEL 12 %» DEBAJO (24-10): su foto ya está en el centro
        // del carrusel, y ahí mismo dice «CAMBIAR FOTO» para sustituirla.
        body = (
            <div>
                <Title />
                <BodyFatSlider value={answers.body_fat} onChange={(v) => set('body_fat', v)}
                    sexo={answers.sex}
                    photo={answers.foto_grasa || null}
                    onPhoto={(f) => set('foto_grasa', f)}
                    pista="Para que te salga lo más fiable posible, ajusta el tamaño de tu foto al de la imagen." />
                {/* SIN COLETILLA PARA NINGÚN PLAN (04-11, prueba 5.9): fuera también la del que
                    no lleva entrenador, «Si tu plan no lo incluye, te toca repetir este
                    ejercicio cada 12 semanas máximo». La del que lleva entrenador ya se quitó el
                    18-10. */}
                <div className="flex items-center gap-3 mt-6">
                    <BackBtn />
                    <Button onClick={goNext}
                        className={CTA}>
                        Continuar <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                </div>
            </div>
        );
    } else if (step.textarea) {
        body = (
            <div>
                <Title />
                <textarea value={answers[step.key] ?? ''} onChange={e => set(step.key, e.target.value)}
                    rows={4} placeholder="Escribe tu respuesta..."
                    className={`${AREA} ${step.nota ? 'mb-3' : 'mb-7'}`} />
                {/* Lo que se dice debajo de la caja, antes de los botones (25-10). */}
                {step.nota && (
                    <p className="text-sm text-[#a8a29c] mb-7" data-testid="nota-del-paso">{step.nota}</p>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                    <BackBtn />
                    <Button onClick={goNext} disabled={!inputValid()}
                        className={CTA}>
                        Continuar <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
                <FaltanCampos n={campoVacio() ? 1 : 0} testId="paso-faltan" />
            </div>
        );
    } else {
        // text | email | tel | date | number
        const inputType = step.type === 'number' ? 'number' : step.type === 'tel' ? 'tel' : step.type === 'date' ? 'date' : step.type === 'email' ? 'email' : 'text';
        // PLANTILLA D, STEPPER (rediseño del 18-10): el peso y la altura. El resto de campos
        // sueltos se quedan como campo de texto, con el aspecto nuevo.
        const stepper = step.type === 'number' && STEPPERS[step.key];
        const [minStepper, maxStepper] = stepper ? RANGOS_PERFIL[step.key] : [0, 0];
        body = (
            <div>
                <Title />
                {stepper ? (
                    <div className="mb-7">
                        <Stepper value={answers[step.key]} onChange={v => set(step.key, v)}
                            onEnter={() => { if (inputValid()) goNext(); }}
                            unit={step.unit} paso={stepper.paso} inicio={stepper.inicio}
                            min={minStepper} max={maxStepper} bloqueado={!!step.locked} />
                    </div>
                ) : (
                    <div className="relative mb-7">
                        <Input
                            type={inputType}
                            autoFocus={!step.locked}
                            disabled={step.locked}
                            readOnly={step.locked}
                            value={answers[step.key] ?? ''}
                            onChange={e => set(step.key, e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter' && inputValid()) goNext(); }}
                            placeholder="Escribe tu respuesta..."
                            className={`${CAMPO} ${step.unit ? 'pr-12' : ''} ${step.locked ? 'opacity-60 cursor-not-allowed' : ''}`}
                        />
                        {step.unit && (
                            <span className="absolute right-4 top-1/2 -translate-y-1/2 font-semibold text-[#8a8580] pointer-events-none">{step.unit}</span>
                        )}
                    </div>
                )}
                {/* El aviso, aquí y no veinte pantallas después. */}
                {avisoDelPaso && (
                    <p className="text-sm text-amber-500 -mt-4 mb-6" data-testid="aviso-del-paso">
                        {avisoDelPaso}
                    </p>
                )}
                <div className="flex items-center gap-3 flex-wrap">
                    <BackBtn />
                    <Button onClick={goNext} disabled={!inputValid()}
                        className={CTA}>
                        Continuar <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                </div>
                <FaltanCampos n={campoVacio() ? 1 : 0} testId="paso-faltan" />
            </div>
        );
    }

    // Dos tramos (doc 29-07): el primero ajusta los macros y al acabarlo ya se los
    // entregamos; el segundo completa el perfil y es opcional. Los pasos de STEPS_AJUSTE son
    // el primer tramo; lo que viene detras (preferencias y perfil largo) es el segundo. La
    // barra de progreso se quitó el 18-10; el tramo sigue diciendo la etiqueta de la cabecera.
    const limiteTramo1 = modoAjuste ? laBaseQueFalta.length + STEPS_AJUSTE.length : flow.length;
    const enTramo1 = idx < limiteTramo1;
    // Las pantallas que ve de verdad (`visible`): de ahí sale la foto del fondo.
    const posVisible = flow.slice(0, idx + 1).filter(visible).length;
    // LA CABECERA DICE LO QUE SE ESTÁ HACIENDO (12-09). El que viene a completar sus huecos
    // entra en modo ajuste, así que leía «Ajustando tus macros» mientras le preguntaban su
    // altura: no está ajustando nada, está dando tres datos que faltaban. Y a ese le vale su
    // portada, «Nos falta información», que es lo mismo que dice la tarjeta del Inicio que
    // le trajo hasta aquí.
    const etiquetaTramo = !modoAjuste
        ? null
        : pidioCompletar ? 'Completando tus datos'
            : enTramo1 ? 'Ajustando tus macros' : 'Completando tu perfil (opcional)';

    // «CERRAR SESIÓN» GUARDA ANTES DÓNDE ESTÁ (24-10). El borrador se escribía al avanzar,
    // así que lo marcado o escrito en la pantalla de ahora -- y la pantalla misma si había
    // vuelto atrás -- se perdía al salir. Se guarda esta pantalla con todo lo que lleva, y al
    // volver a entrar sigue aquí (`pasoDelBorrador`). Las fotos no viajan en el borrador.
    const guardarAntesDeSalir = () => {
        cancelarAvancePendiente();
        return guardarProgreso(answersRef.current, idx);
    };

    return (
        <Shell
            giro={posVisible}
            tramo={etiquetaTramo}
            alCerrarSesion={guardarAntesDeSalir}
            cabecera={modoAjuste && enTramo1 && step.type !== 'result'
                ? <MacrosEnVivo macros={vistaPrevia?.macros} previos={previosRef.current} calculando={calculandoVivo} />
                : null}
        >
            {body}
        </Shell>
    );
};

export default QuestionnairePage;
